import { useState, useEffect, useRef } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// ─── Language ─────────────────────────────────────────────────────────────────
const LANG = {
  en: {
    home:"Home", business:"Business", ai:"AI", contact:"Contact",
    signIn:"Sign In", register:"Register", signOut:"Sign Out", myAccount:"My Account",
    heroTitle:"Full-Stack Developer", heroSub:"& AI Builder",
    heroDesc:"Building AI systems, automation tools, and scalable apps. Creator of TOOLREX.ONLINE.",
    exploreTools:"Explore Tools", getContact:"Contact",
    aboutTitle:"AI Engineer & Software Architect",
    aboutDesc:"Focused on Full-Stack Engineering, AI Systems, and Business Automation.",
    readyBuild:"Ready to Build?",
    readyDesc:"Browse my tools, request a custom build, or reach out on Telegram.",
    bizTitle:"Business Hub",
    bizDesc:"AI tools, automation services, custom software development, and more.",
    searchPlaceholder:"Search tools...",
    faqTitle:"FAQ", faqDesc:"Common questions answered.",
    licenseCheck:"Check License",
  },
  kh: {
    home:"ទំព័រដើម", business:"ជំនួញ", ai:"AI", contact:"ទំនាក់ទំនង",
    signIn:"ចូល", register:"ចុះឈ្មោះ", signOut:"ចាកចេញ", myAccount:"គណនីរបស់ខ្ញុំ",
    heroTitle:"អ្នកអភិវឌ្ឍ Full-Stack", heroSub:"& AI Builder",
    heroDesc:"សាងសង់ប្រព័ន្ធ AI ឧបករណ៍ស្វ័យប្រវត្តិ និងកម្មវិធីដែលអាចពង្រីកបាន។",
    exploreTools:"ស្វែងរកឧបករណ៍", getContact:"ទំនាក់ទំនង",
    aboutTitle:"វិស្វករ AI & អ្នករចនាប្រព័ន្ធ",
    aboutDesc:"ផ្តោតលើ Full-Stack Engineering, ប្រព័ន្ធ AI, និងស្វ័យប្រវត្តិកម្ម។",
    readyBuild:"ត្រៀមខ្លួនសាងសង់ហើយ?",
    readyDesc:"រុករកឧបករណ៍ ស្នើសុំការសាងសង់ ឬទំនាក់ទំនងតាម Telegram។",
    bizTitle:"មជ្ឈមណ្ឌលជំនួញ",
    bizDesc:"ឧបករណ៍ AI សេវាស្វ័យប្រវត្តិ ការអភិវឌ្ឍកម្មវិធី និងច្រើនទៀត។",
    searchPlaceholder:"ស្វែងរកឧបករណ៍...",
    faqTitle:"សំណួរញឹកញាប់", faqDesc:"ចម្លើយចំពោះសំណួរទូទៅ។",
    licenseCheck:"ពិនិត្យអាជ្ញាប័ណ្ណ",
  },
};

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const LOGO_URL = "/logo192.png";

// ─── EMREX TOOL ───────────────────────────────────────────────────────────────
const EMREX_TOOL = {
  id: 7,
  isNew: true,
  isFeatured: true,
  icon: "📧",
  name: "EMrex",
  tagline: "The Ultimate Email Account Manager",
  description:
    "Manage unlimited email accounts in one powerful dashboard. Login, edit, connect social platforms, export CSV data, and monitor everything from a clean, beginner-friendly interface. Built for marketers, businesses, and power users.",
  category: "Email Management",
  license: "1 Year License",
  pricing: [
    {
      id: "yearly",
      label: "1 Year",
      price: 1,
      original: 9,
      badge: "BEST DEAL",
      discount: "89% OFF",
      perks: [
        "1 license key",
        "1 device",
        "1 year access",
        "Free group support",
        "Free AI & IT discussion group",
      ],
    },
  ],
  features: [
    {
      icon: "📬",
      title: "Unlimited Accounts",
      desc: "Control unlimited email accounts from one place",
    },
    {
      icon: "🔐",
      title: "Secure Login",
      desc: "Login and store credentials safely in the tool",
    },
    {
      icon: "✏️",
      title: "Easy Edit",
      desc: "Edit email info quickly and efficiently",
    },
    {
      icon: "🔗",
      title: "Platform Connect",
      desc: "Link Facebook, Telegram, TikTok, Instagram",
    },
    {
      icon: "📤",
      title: "CSV Export",
      desc: "Export all account data to CSV instantly",
    },
    {
      icon: "📊",
      title: "Full Dashboard",
      desc: "View all accounts and platforms at a glance",
    },
    {
      icon: "🎯",
      title: "Beginner Friendly",
      desc: "Clean UI designed for easy use",
    },
    {
      icon: "♾️",
      title: "Lifetime Key",
      desc: "Pay once, use forever with free updates",
    },
  ],
  testimonials: [
    {
      name: "Sam K.",
      role: "Digital Marketer",
      text: "Managing 50+ accounts used to be a nightmare. EMrex made it effortless.",
      stars: 5,
    },
    {
      name: "Rina T.",
      role: "Social Media Manager",
      text: "The CSV export alone saved me hours every week. Total game changer.",
      stars: 5,
    },
    {
      name: "Dev M.",
      role: "Agency Owner",
      text: "Clean, fast, and actually works. Best $1 I've spent this year.",
      stars: 5,
    },
  ],
};

const FEATURED_TOOL = {
  id: 9,
  isNew: true,
  isFeatured: true,
  icon: "▶️",
  name: "YT REX",
  tagline: "Full-Power YouTube Channel Automation",
  description:
    "Automate your entire YouTube workflow — manage unlimited accounts, auto-subscribe, like, comment, upload videos, update branding, and run account health checks. Powered by real Brave browser sessions with full proxy support.",
  category: "YouTube Tools",
  license: "1 Year License",
  pricing: [
    {
      id: "yearly",
      label: "1 Year",
      price: 1,
      original: 9,
      badge: "BEST DEAL",
      discount: "89% OFF",
      perks: [
        "1 license key",
        "1 device",
        "1 year access",
        "Free group support",
        "Free AI & IT discussion group",
      ],
    },
  ],
  features: [
    {
      icon: "👥",
      title: "Multi-Account",
      desc: "Manage unlimited YouTube accounts from one dashboard",
    },
    {
      icon: "🤖",
      title: "Auto Engage",
      desc: "Auto-subscribe, like, and comment on any channel",
    },
    {
      icon: "📤",
      title: "Video Upload",
      desc: "Bulk upload videos with titles, descriptions & tags",
    },
    {
      icon: "🎨",
      title: "Branding Update",
      desc: "Auto-update channel banner and profile picture",
    },
    {
      icon: "🔍",
      title: "Account Checker",
      desc: "Instantly verify account health and login status",
    },
    {
      icon: "🌐",
      title: "Proxy Support",
      desc: "Run each account through its own proxy safely",
    },
    {
      icon: "⚡",
      title: "Multi-Tab Speed",
      desc: "Run up to 6 browser sessions simultaneously",
    },
    {
      icon: "🛡️",
      title: "Anti-Detect",
      desc: "Real Brave browser — undetected by YouTube",
    },
  ],
  testimonials: [
    {
      name: "Leon K.",
      role: "YouTube Growth Agency",
      text: "Manages 30+ channels automatically. What used to take a full day now runs overnight.",
      stars: 5,
    },
    {
      name: "Nadia S.",
      role: "Content Strategist",
      text: "The account checker alone saved me from losing three channels. Absolute must-have.",
      stars: 5,
    },
    {
      name: "Marcus D.",
      role: "Digital Marketer",
      text: "Proxy support + multi-tab mode is insane. No other tool comes close.",
      stars: 5,
    },
  ],
};

const ALL_TOOLS = [
  { ...EMREX_TOOL, price: 1, originalPrice: 9 },
  {
    id: 8,
    isNew: true,
    icon: "🎬",
    name: "DL REX",
    tagline: "Professional Bulk Video Downloader",
    description:
      "Download from TikTok, YouTube, Instagram & 1000+ sites in bulk. Add watermarks, color grade, monitor accounts 24/7.",
    price: 1,
    originalPrice: 9,
    category: "Video Tools",
    license: "1 Year License",
    pricing: [
      {
        id: "yearly",
        label: "1 Year",
        price: 1,
        original: 9,
        badge: "BEST DEAL",
        discount: "89% OFF",
        perks: [
          "1 license key",
          "1 device",
          "1 year access",
          "Free group support",
          "Free AI & IT discussion group",
        ],
      },
    ],
    features: [
      { icon: "📥", title: "Bulk Download", desc: "Paste 100+ URLs at once" },
      {
        icon: "👤",
        title: "Account Download",
        desc: "Download ALL videos from any account",
      },
      {
        icon: "🎨",
        title: "Watermark Editor",
        desc: "Add logo/text with live preview",
      },
      {
        icon: "🎬",
        title: "Video Color Edit",
        desc: "Brightness, contrast, saturation in one pass",
      },
      {
        icon: "👁️",
        title: "Account Watcher",
        desc: "Auto-downloads new videos 24/7",
      },
      {
        icon: "🌐",
        title: "1000+ Sites",
        desc: "YouTube, TikTok, Instagram, Facebook and more",
      },
      {
        icon: "🔒",
        title: "Cookie Support",
        desc: "Access private content via cookies",
      },
      {
        icon: "⚡",
        title: "Up to 8x Speed",
        desc: "1-8 simultaneous downloads",
      },
    ],
    testimonials: [
      {
        name: "Marco V.",
        role: "Content Creator",
        text: "Downloaded 3 years of YouTube content in one afternoon.",
        stars: 5,
      },
      {
        name: "Sarah L.",
        role: "Agency Owner",
        text: "Watermark feature alone is worth the price.",
        stars: 5,
      },
    ],
  },
  { ...FEATURED_TOOL },
  {
    id: 10,
    isNew: false,
    icon: "✅",
    name: "TODO REX",
    tagline: "Smart Task Manager for Power Users",
    description:
      "Stay on top of every project with a fast, offline task manager. Organize tasks by category, set target dates, track progress, and switch between dark and light themes. Simple, snappy, and yours forever.",
    category: "Productivity",
    license: "1 Year License",
    pricing: [
      {
        id: "yearly",
        label: "1 Year",
        price: 1,
        original: 9,
        badge: "BEST DEAL",
        discount: "89% OFF",
        perks: [
          "1 license key",
          "1 device",
          "1 year access",
          "Free group support",
          "Free AI & IT discussion group",
        ],
      },
    ],
    features: [
      {
        icon: "📂",
        title: "Categories",
        desc: "Organize tasks into custom categories",
      },
      {
        icon: "📅",
        title: "Target Dates",
        desc: "Set deadlines and never miss them",
      },
      {
        icon: "✔️",
        title: "Done Tracking",
        desc: "Mark tasks complete with one click",
      },
      {
        icon: "🗃️",
        title: "Offline Storage",
        desc: "All data stored locally — no cloud required",
      },
      {
        icon: "🌙",
        title: "Dark / Light Mode",
        desc: "Switch themes to match your workspace",
      },
      {
        icon: "⚡",
        title: "Instant & Snappy",
        desc: "Lightweight app with zero lag",
      },
    ],
    testimonials: [
      {
        name: "Tina R.",
        role: "Freelancer",
        text: "Finally a task manager that's simple and doesn't require the internet. Love it.",
        stars: 5,
      },
      {
        name: "Kevin L.",
        role: "Project Manager",
        text: "Category system keeps all my client projects separate. Clean and fast.",
        stars: 5,
      },
    ],
  },
];

const CATEGORIES = ["All", ...new Set(ALL_TOOLS.map((t) => t.category))];

const C = {
  bg: "#05080f",
  surface: "#0a0f1a",
  surface2: "#0e1525",
  border: "#162035",
  borderHover: "#1e3050",
  accent: "#38bdf8",
  accentB: "#0ea5e9",
  accentDim: "#38bdf811",
  accentGlow: "#38bdf833",
  green: "#34d399",
  greenDim: "#34d39911",
  orange: "#fb923c",
  orangeDim: "#fb923c11",
  text: "#e2eaf6",
  muted: "#6b7f99",
  gold: "#fbbf24",
  danger: "#f87171",
  indigo: "#6366f1",
  indigoDim: "#6366f115",
  indigoGlow: "#6366f133",
  purple: "#a855f7",
  purpleDim: "#a855f715",
  textBright: "#f0f4ff",
  mutedBright: "#8892c0",
};

const SERVICE_PACKAGES = [
  {
    name:"Starter", icon:"🚀", price:49, color:"#38bdf8",
    desc:"Perfect for small projects and MVPs",
    features:["Basic web application","Responsive UI design","REST API integration","1 month support","GitHub delivery"],
  },
  {
    name:"Professional", icon:"⚡", price:149, color:"#6366f1", badge:"MOST POPULAR",
    desc:"Full-stack SaaS or business platform",
    features:["Full-stack application","Auth + database","AI/Bot integration","Admin dashboard","3 months support","Docker deployment"],
  },
  {
    name:"Enterprise", icon:"🏆", price:499, color:"#a855f7",
    desc:"Large-scale AI systems and platforms",
    features:["Custom AI platform","Scalable architecture","Automation pipeline","Team collaboration","6 months support","Production deployment","Performance optimization"],
  },
];


const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Exo+2:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=JetBrains+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }
html { scroll-behavior: smooth; -webkit-text-size-adjust: 100%; }
body { font-family: 'Exo 2', sans-serif; background: #05080f; color: #e2eaf6; min-height: 100vh; min-height: -webkit-fill-available; overflow-x: hidden; cursor: none; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
@media(pointer:coarse){ body{cursor:auto!important;} .cursor,.cursor-ring{display:none!important;} }
.cursor { position: fixed; width: 10px; height: 10px; border-radius: 50%; background: #38bdf8; pointer-events: none; z-index: 99999; transform: translate(-50%, -50%); transition: transform 0.1s; mix-blend-mode: screen; }
.cursor-ring { position: fixed; width: 36px; height: 36px; border-radius: 50%; border: 1px solid #38bdf866; pointer-events: none; z-index: 99998; transform: translate(-50%, -50%); transition: transform 0.15s ease-out; }
::-webkit-scrollbar { width: 3px; }
::-webkit-scrollbar-track { background: #05080f; }
::-webkit-scrollbar-thumb { background: #162035; border-radius: 2px; }
@keyframes fadeUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
@keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
@keyframes glow { 0%,100%{box-shadow:0 0 20px #38bdf833;} 50%{box-shadow:0 0 50px #38bdf833;} }
@keyframes spin { to { transform:rotate(360deg); } }
@keyframes scanline { 0%{transform:translateY(-100%);} 100%{transform:translateY(100vh);} }
@keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
@keyframes float { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-10px);} }
@keyframes blink { 0%,100%{border-color:transparent;} 50%{border-color:#38bdf8;} }
@keyframes splashIn { from{opacity:0;transform:translateY(40px) scale(0.96);} to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes orbDrift { 0%{transform:translate(0,0) scale(1);} 33%{transform:translate(30px,-24px) scale(1.08);} 66%{transform:translate(-22px,18px) scale(0.94);} 100%{transform:translate(0,0) scale(1);} }
@keyframes shimmer { 0%{background-position:200% center;} 100%{background-position:-200% center;} }
@keyframes dotPop { 0%{transform:scale(0);opacity:0;} 60%{transform:scale(1.4);} 100%{transform:scale(1);opacity:1;} }
@keyframes slideInRight { from{opacity:0;transform:translateX(40px);} to{opacity:1;transform:translateX(0);} }
@keyframes slideInLeft  { from{opacity:0;transform:translateX(-40px);} to{opacity:1;transform:translateX(0);} }
@keyframes slideInUp    { from{opacity:0;transform:translateY(32px);} to{opacity:1;transform:translateY(0);} }
@keyframes borderPulse  { 0%,100%{opacity:0.4;} 50%{opacity:1;} }
@keyframes shake { 0%,100%{transform:translateX(0);} 20%,60%{transform:translateX(-6px);} 40%,80%{transform:translateX(6px);} }
.splash-input { transition: all 0.25s !important; }
.splash-input:focus { border-color: var(--splash-accent,rgba(124,58,237,0.6)) !important; box-shadow: 0 0 0 3px var(--splash-accent-dim,rgba(124,58,237,0.1)) !important; outline:none; }
.splash-tab-btn { transition: all 0.3s cubic-bezier(0.16,1,0.3,1); }
.splash-tab-btn:hover { opacity: 1 !important; }
.splash-cta:hover { transform: translateY(-3px) !important; filter: brightness(1.1); }
.splash-cta { transition: all 0.3s cubic-bezier(0.16,1,0.3,1) !important; }
@keyframes particleFloat { 0% { transform: translateY(0) scale(1); opacity: 0.6; } 100% { transform: translateY(-120px) scale(0); opacity: 0; } }
@keyframes urgentPulse { 0%,100%{box-shadow:0 0 0 0 rgba(251,146,60,0.4)} 50%{box-shadow:0 0 0 8px rgba(251,146,60,0)} }
.fade-up { animation: fadeUp 0.7s ease forwards; }
.fade-in { animation: fadeIn 0.5s ease forwards; }
.nav { position: fixed; top:0; left:0; right:0; z-index:200; border-bottom: 1px solid #162035; background: #05080fdd; backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); padding-top: env(safe-area-inset-top); }
.nav-inner { max-width:1200px; margin:0 auto; padding:0 24px; height:68px; display:flex; align-items:center; justify-content:space-between; }
.logo-wrap { display:flex; align-items:center; gap:12px; cursor:pointer; font-family:'Orbitron',sans-serif; }
.logo-img { width:40px; height:40px; border-radius:50%; border: 2px solid #38bdf866; object-fit:cover; animation: glow 4s ease-in-out infinite; }
.logo-text { font-size:18px; font-weight:900; letter-spacing:0.05em; }
.logo-text span { color:#38bdf8; }
.logo-sub { font-size:9px; font-weight:400; color:#6b7f99; letter-spacing:0.2em; font-family:'JetBrains Mono',monospace; display:block; line-height:1; }
.nav-links { display:flex; gap:4px; align-items:center; }
.nav-link { padding:8px 14px; font-size:12px; font-weight:600; color:#6b7f99; cursor:pointer; border-radius:6px; transition:all 0.2s; border:none; background:none; font-family:'Exo 2',sans-serif; letter-spacing:0.05em; text-transform:uppercase; }
.nav-link:hover { color:#e2eaf6; background:#0a0f1a; }
.nav-link.active { color:#38bdf8; }
.btn-primary { background: linear-gradient(135deg, #38bdf8, #0ea5e9); color: #05080f; border: none; padding:12px 28px; font-family:'Exo 2',sans-serif; font-size:13px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; border-radius:6px; transition:all 0.25s ease; }
.btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 32px #38bdf833; }
.btn-outline { background:transparent; color:#38bdf8; border:1px solid #38bdf866; padding:12px 28px; font-family:'Exo 2',sans-serif; font-size:13px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; border-radius:6px; transition:all 0.25s ease; }
.btn-outline:hover { border-color:#38bdf8; background:#38bdf811; transform:translateY(-2px); }
.btn-ghost { background:transparent; color:#6b7f99; border:1px solid #162035; padding:9px 20px; font-family:'Exo 2',sans-serif; font-size:12px; font-weight:600; cursor:pointer; border-radius:6px; transition:all 0.2s; }
.btn-ghost:hover { border-color:#38bdf844; color:#38bdf8; }
.btn-ghost.active-cat { border-color:#38bdf8; color:#38bdf8; background:#38bdf811; }
.btn-tg { background: linear-gradient(135deg, #229ED9, #1a7db5); color: white; border:none; padding:12px 28px; font-family:'Exo 2',sans-serif; font-size:13px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; border-radius:6px; transition:all 0.25s; display:flex; align-items:center; gap:8px; }
.btn-tg:hover { transform:translateY(-2px); box-shadow:0 8px 32px #229ED966; }
.btn-gh { background: linear-gradient(135deg, #333, #1a1a1a); color: white; border: 1px solid #444; padding:12px 28px; font-family:'Exo 2',sans-serif; font-size:13px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; border-radius:6px; transition:all 0.25s; display:flex; align-items:center; gap:8px; }
.btn-gh:hover { transform:translateY(-2px); }
.card { background:#0a0f1a; border:1px solid #162035; border-radius:14px; padding:24px; transition:all 0.3s ease; position:relative; overflow:hidden; }
.card::before { content:''; position:absolute; top:0; left:0; right:0; height:1px; background:linear-gradient(90deg, transparent, #38bdf855, transparent); opacity:0; transition:opacity 0.3s; }
.card:hover { border-color:#38bdf833; transform:translateY(-5px); box-shadow:0 20px 60px #00000099; }
.card:hover::before { opacity:1; }
.new-badge { background: linear-gradient(135deg, #34d399, #10b981); color: #001a0d; font-size:9px; font-weight:800; letter-spacing:0.15em; padding:3px 8px; border-radius:4px; text-transform:uppercase; display:inline-block; font-family:'JetBrains Mono',monospace; }
.hot-badge { background: linear-gradient(135deg, #fb923c, #ea580c); color: white; font-size:9px; font-weight:800; letter-spacing:0.15em; padding:3px 8px; border-radius:4px; text-transform:uppercase; display:inline-block; font-family:'JetBrains Mono',monospace; }
.tag { background:#38bdf811; color:#38bdf8; border:1px solid #38bdf822; font-size:10px; font-weight:600; letter-spacing:0.1em; padding:3px 10px; border-radius:20px; display:inline-block; font-family:'JetBrains Mono',monospace; }
.tag-green { background:#34d39911; color:#34d399; border:1px solid #34d39922; font-size:10px; font-weight:600; letter-spacing:0.1em; padding:3px 10px; border-radius:20px; display:inline-block; font-family:'JetBrains Mono',monospace; }
.tag-orange { background:#fb923c11; color:#fb923c; border:1px solid #fb923c22; font-size:10px; font-weight:600; letter-spacing:0.1em; padding:3px 10px; border-radius:20px; display:inline-block; font-family:'JetBrains Mono',monospace; }
.label { font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:500; letter-spacing:0.2em; text-transform:uppercase; color:#38bdf8; display:block; margin-bottom:10px; }
h1,h2,h3 { font-family:'Orbitron',sans-serif; line-height:1.1; }
input, textarea { width:100%; background:#0a0f1a; border:1px solid #162035; border-radius:8px; padding:14px 16px; color:#e2eaf6; font-family:'Exo 2',sans-serif; font-size:16px; outline:none; transition:border-color 0.2s; resize:vertical; -webkit-appearance:none; appearance:none; }
button { -webkit-appearance:none; appearance:none; touch-action:manipulation; }
input:focus, textarea:focus { border-color:#38bdf8; box-shadow:0 0 0 3px #38bdf811; }
input::placeholder, textarea::placeholder { color:#6b7f99; }
.section { padding:90px 24px; max-width:1200px; margin:0 auto; }
.grid { display:grid; gap:20px; }
.grid-2 { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
.grid-3 { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
.particle { position:absolute; width:3px; height:3px; border-radius:50%; background:#38bdf8; pointer-events:none; animation: particleFloat linear infinite; }
.typing-text { display:inline-block; border-right:2px solid #38bdf8; animation:blink 0.8s step-end infinite; white-space:nowrap; overflow:hidden; }
.scanline-wrap { position:absolute; inset:0; overflow:hidden; pointer-events:none; }
.scanline { position:absolute; width:100%; height:2px; background:linear-gradient(90deg,transparent,#38bdf822,transparent); animation:scanline 4s linear infinite; }
.terminal { background:#0e1525; border:1px solid #162035; border-radius:12px; overflow:hidden; }
.terminal-bar { display:flex; align-items:center; gap:6px; padding:12px 16px; background:#0a0f1a; border-bottom:1px solid #162035; }
.t-dot { width:10px; height:10px; border-radius:50%; }
.terminal-body { padding:20px; font-family:'JetBrains Mono',monospace; font-size:13px; line-height:1.8; }
.t-green { color:#34d399; }
.t-blue { color:#38bdf8; }
.t-gold { color:#fbbf24; }
.t-white { color:#e2eaf6; }
.about-grid { display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:center; }
.detail-grid { display:grid; grid-template-columns:1fr 400px; gap:56px; align-items:start; }
footer { border-top:1px solid #162035; background:#0a0f1a; margin-top:80px; padding-bottom: env(safe-area-inset-bottom, 0px); }
.hero-title { background:linear-gradient(135deg,#e2eaf6,#38bdf8); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
.price-badge { background:#f8717122; color:#f87171; border:1px solid #f8717133; font-size:11px; padding:4px 10px; border-radius:6px; font-family:'JetBrains Mono',monospace; text-decoration:line-through; margin-top:12px; display:inline-block; }
.price-card { background:#0a0f1a; border:2px solid #162035; border-radius:16px; padding:24px; cursor:pointer; transition:all 0.3s; position:relative; overflow:hidden; }
.price-card:hover { border-color:#38bdf866; transform:translateY(-4px); box-shadow:0 16px 48px rgba(0,0,0,0.5); }
.price-card.selected { border-color:#38bdf8; box-shadow:0 0 30px #38bdf833; }
.price-card.best { border-color:#fb923c66; }
.price-card.best.selected { border-color:#fb923c; box-shadow:0 0 30px #fb923c22; }
.price-card.best:hover { border-color:#fb923c66; }
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.85); z-index:1000; display:flex; align-items:center; justify-content:center; padding:24px; backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px); animation:fadeIn 0.2s ease; }
.modal { background:#0a0f1a; border:1px solid #162035; border-radius:20px; width:100%; max-width:480px; overflow:hidden; animation:fadeUp 0.3s ease; max-height:90vh; overflow-y:auto; }
.timer-bar { background:linear-gradient(135deg,#fb923c22,#f8717111); border:1px solid #fb923c33; border-radius:10px; padding:14px 20px; display:flex; align-items:center; gap:12px; animation:urgentPulse 2s ease-in-out infinite; }
.htb-grid { grid-template-columns: repeat(5, 1fr); }
@media(max-width:900px){ .htb-grid { grid-template-columns:repeat(2,1fr); } .step-arrow { display:none; } }
@media(max-width:500px){ .htb-grid { grid-template-columns:1fr; } }
.splash-grid { grid-template-columns:1fr 1fr; }
@media(max-width:620px){ .splash-grid { grid-template-columns:1fr; } }
/* ── Tablet 1024px ───────────────────────────────────────── */
@media(max-width:1024px){
  .detail-grid { grid-template-columns:1fr 340px!important; }
}
/* ── Tablet 768px ────────────────────────────────────────── */
@media(max-width:768px){
  .about-grid,.detail-grid,.dev-hero-grid { grid-template-columns:1fr!important; }
  .order-grid,.pkg-grid { grid-template-columns:1fr!important; }
  .biz-tabs { flex-wrap:wrap; gap:4px!important; }
  .section { padding:60px 18px!important; }
  .nav-inner { padding:0 16px; }
  .nav-links { display:flex; gap:2px; overflow-x:auto; scrollbar-width:none; -ms-overflow-style:none; }
  .nav-links::-webkit-scrollbar { display:none; }
  .nav-link { padding:6px 10px; font-size:11px; white-space:nowrap; }
  .logo-sub { display:none; }
  footer .grid { grid-template-columns:1fr 1fr!important; }
  .grid-3 { grid-template-columns:repeat(2,1fr)!important; }
  .card { padding:20px!important; }
  h1 { font-size:clamp(24px,5vw,40px)!important; }
  h2 { font-size:clamp(20px,4vw,32px)!important; }
  .biz-tabs button { flex:1; min-width:0; font-size:11px!important; padding:9px 8px!important; }
  .detail-grid { gap:28px!important; }
  .terminal-body { font-size:12px!important; }
}
/* ── Mobile 480px — iOS & Android ───────────────────────── */
@media(max-width:480px){
  .nav-inner { height:56px; padding:0 12px; }
  .nav-links { gap:0; }
  .nav-link { padding:5px 8px; font-size:10px; }
  .logo-img { width:30px; height:30px; }
  .logo-text { font-size:13px; }
  .section { padding:44px 14px!important; }
  .grid-2,.grid-3 { grid-template-columns:1fr!important; }
  .card { padding:16px!important; }
  .modal { max-width:calc(100vw - 24px); margin:0 12px; border-radius:16px; }
  .modal-overlay { padding:12px; align-items:flex-end; padding-bottom:env(safe-area-inset-bottom,16px); }
  footer { margin-top:32px; }
  footer .grid { grid-template-columns:1fr!important; gap:20px!important; }
  .price-card { padding:16px!important; }
  .htb-grid { grid-template-columns:1fr!important; }
  .timer-bar { flex-direction:column; gap:6px; text-align:center; }
  h1 { font-size:clamp(22px,7vw,32px)!important; }
  h2 { font-size:clamp(18px,5vw,26px)!important; }
  h3 { font-size:clamp(14px,4vw,20px)!important; }
  .auth-card-wrap { padding:0 12px!important; }
  .nav-auth { display:none; }
  .biz-tabs { overflow-x:auto; flex-wrap:nowrap!important; scrollbar-width:none; padding-bottom:2px; }
  .biz-tabs::-webkit-scrollbar { display:none; }
  .biz-tabs button { flex-shrink:0!important; }
  .neon-btn,.btn-primary,.btn-tg,.btn-outline { padding:11px 20px!important; font-size:13px!important; }
  .detail-grid,.about-grid { gap:20px!important; }
  .terminal { font-size:11px; }
  .terminal-body { padding:14px!important; font-size:11px!important; line-height:1.6!important; }
}
/* ── Small phones 360px ──────────────────────────────────── */
@media(max-width:360px){
  .nav-link { padding:4px 6px; font-size:9px; }
  .logo-text { font-size:12px; }
  h1 { font-size:clamp(20px,6vw,28px)!important; }
}

@keyframes gradShift   { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
@keyframes aiNavGlow   { 0%,100%{box-shadow:0 0 14px rgba(99,102,241,0.4)} 50%{box-shadow:0 0 28px rgba(168,85,247,0.6),0 0 8px rgba(99,102,241,0.3)} }
@keyframes orbFloat    { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(30px,-20px) scale(1.06)} 66%{transform:translate(-18px,14px) scale(0.96)} }
.glass-card { background:rgba(255,255,255,0.03); backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px); border:1px solid rgba(255,255,255,0.08); border-radius:16px; transition:all 0.35s cubic-bezier(0.16,1,0.3,1); }
.glass-card:hover { background:rgba(99,102,241,0.06); border-color:rgba(99,102,241,0.35); transform:translateY(-5px); box-shadow:0 24px 64px rgba(0,0,0,0.5),0 0 40px rgba(99,102,241,0.08); }
.neon-btn { position:relative; overflow:hidden; background:linear-gradient(135deg,#6366f1,#38bdf8); border:none; border-radius:10px; color:#fff; font-weight:800; cursor:pointer; transition:all 0.3s; font-family:'Exo 2',sans-serif; letter-spacing:0.06em; }
.neon-btn::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,#a855f7,#6366f1); opacity:0; transition:opacity 0.3s; }
.neon-btn:hover::before { opacity:1; }
.neon-btn:hover { transform:translateY(-2px); box-shadow:0 12px 40px rgba(99,102,241,0.45); }
.neon-btn > span { position:relative; z-index:1; display:flex; align-items:center; gap:8px; }
.neon-btn-outline { background:transparent; border:1px solid rgba(99,102,241,0.5); border-radius:10px; color:#a5b4fc; font-weight:700; cursor:pointer; transition:all 0.3s; font-family:'Exo 2',sans-serif; letter-spacing:0.06em; }
.neon-btn-outline:hover { background:rgba(99,102,241,0.1); border-color:#6366f1; color:#c7d2fe; transform:translateY(-2px); }
.skill-pill { display:inline-flex; align-items:center; gap:5px; padding:6px 14px; border-radius:100px; font-size:11px; font-weight:700; border:1px solid; transition:all 0.2s; cursor:default; font-family:'JetBrains Mono',monospace; letter-spacing:0.03em; }
.skill-pill:hover { transform:translateY(-2px) scale(1.05); filter:brightness(1.15); }
.stat-card { text-align:center; padding:28px 16px; border-radius:16px; background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.06); transition:all 0.3s; }
.stat-card:hover { background:rgba(99,102,241,0.08); border-color:rgba(99,102,241,0.3); transform:translateY(-4px); }
.biz-tab { padding:10px 22px; border-radius:8px; border:1px solid rgba(255,255,255,0.06); background:transparent; color:#7b8cba; font-size:11px; font-weight:700; cursor:pointer; letter-spacing:0.08em; font-family:'Exo 2',sans-serif; text-transform:uppercase; transition:all 0.2s; white-space:nowrap; }
.biz-tab:hover { color:#e2eaf6; background:rgba(255,255,255,0.05); }
.biz-tab.active { background:rgba(99,102,241,0.15); border-color:rgba(99,102,241,0.45); color:#a5b4fc; }
.pkg-card { border-radius:18px; padding:28px 24px; position:relative; overflow:hidden; transition:all 0.35s; }
.pkg-card:hover { transform:translateY(-6px); }
.hero-orb { position:absolute; border-radius:50%; filter:blur(80px); opacity:0.35; animation:orbFloat 8s ease-in-out infinite; pointer-events:none; }
.section-label { font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:600; letter-spacing:0.3em; text-transform:uppercase; display:flex; align-items:center; gap:8px; margin-bottom:16px; }
.section-label::before { content:''; display:inline-block; width:20px; height:2px; background:linear-gradient(90deg,#6366f1,#38bdf8); border-radius:1px; }
`;

function Cursor() {
  const c = useRef(null);
  const r = useRef(null);
  const isTouch = typeof window !== "undefined" && window.matchMedia("(pointer:coarse)").matches;
  useEffect(() => {
    if (isTouch) return;
    const move = (e) => {
      if (c.current) { c.current.style.left = e.clientX+"px"; c.current.style.top = e.clientY+"px"; }
      setTimeout(() => {
        if (r.current) { r.current.style.left = e.clientX+"px"; r.current.style.top = e.clientY+"px"; }
      }, 80);
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [isTouch]);
  if (isTouch) return null;
  return (<><div className="cursor" ref={c} /><div className="cursor-ring" ref={r} /></>);
}

// ─── CUSTOMER COUNTER (Feature 3) ────────────────────────────────────────────
const SOLD_COUNTS = {
  7: 143,
  8: 89,
  1: 312,
  2: 78,
  3: 94,
  4: 201,
  5: 267,
  6: 156,
};

// ─── VERSION CHANGELOG (Feature 6) ───────────────────────────────────────────
const CHANGELOGS = {
  7: [
    {
      v: "1.3.6",
      date: "2026-04-20",
      changes: [
        "Fixed Chrome login popup issue",
        "Improved CSV export speed",
        "Added Instagram follower tracking",
      ],
    },
    {
      v: "1.3.5",
      date: "2026-04-10",
      changes: [
        "Added TikTok follower count column",
        "Bug fixes for multi-account login",
      ],
    },
    {
      v: "1.3.0",
      date: "2026-03-28",
      changes: [
        "New Dashboard UI",
        "Added YouTube integration",
        "Performance improvements",
      ],
    },
  ],
  8: [
    {
      v: "2.0.0",
      date: "2026-04-22",
      changes: [
        "New watermark editor with live preview",
        "Added color grading feature",
        "Account Watcher 24/7 monitoring",
      ],
    },
    {
      v: "1.9.5",
      date: "2026-04-08",
      changes: [
        "Fixed proxy support for SOCKS5",
        "Added MP3/M4A audio extraction",
        "Speed improvements up to 2x",
      ],
    },
    {
      v: "1.9.0",
      date: "2026-03-15",
      changes: [
        "Added 1000+ site support via yt-dlp",
        "Bulk URL paste feature",
        "Download history with CSV export",
      ],
    },
  ],
};

// ─── NEWSLETTER SIGNUP (Feature 4) ───────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  function submit() {
    if (!email.includes("@")) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setDone(true);
    }, 1200);
  }
  return (
    <div
      style={{
        background: `linear-gradient(135deg,${C.surface2},${C.surface})`,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: "36px 28px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg,${C.orange},${C.accent})`,
        }}
      />
      <div style={{ fontSize: 32, marginBottom: 12 }}>📧</div>
      <h3 style={{ fontSize: 20, letterSpacing: "0.03em", marginBottom: 8 }}>
        Stay Updated
      </h3>
      <p
        style={{
          color: C.muted,
          fontSize: 13,
          marginBottom: 20,
          lineHeight: 1.7,
        }}
      >
        Get notified when new tools launch and when prices drop. No spam — ever.
      </p>
      {!done ? (
        <div
          style={{ display: "flex", gap: 8, maxWidth: 400, margin: "0 auto" }}
        >
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="your@email.com"
            style={{
              flex: 1,
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: "11px 14px",
              color: C.text,
              fontFamily: "Exo 2,sans-serif",
              fontSize: 13,
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = C.accent)}
            onBlur={(e) => (e.target.style.borderColor = C.border)}
          />
          <button
            onClick={submit}
            disabled={loading}
            style={{
              background: `linear-gradient(135deg,${C.accent},${C.accentB})`,
              color: C.bg,
              border: "none",
              borderRadius: 8,
              padding: "11px 20px",
              fontFamily: "Exo 2,sans-serif",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
            }}
          >
            {loading ? "..." : "Subscribe ✓"}
          </button>
        </div>
      ) : (
        <div
          style={{
            background: `${C.green}11`,
            border: `1px solid ${C.green}33`,
            borderRadius: 10,
            padding: "14px 20px",
            display: "inline-block",
            color: C.green,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          🎉 You're subscribed! We'll notify you of new tools and deals.
        </div>
      )}
    </div>
  );
}

// ─── BUNDLE DEAL (Feature 1) ──────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function BundleDeal({ onDetail, ALL_TOOLS }) {
  const [showModal, setShowModal] = useState(false);
  const emrex = ALL_TOOLS.find((t) => t.id === 7);
  const dlrex = ALL_TOOLS.find((t) => t.id === 8);
  if (!emrex || !dlrex) return null;
  const bundlePrice = 17;
  const originalTotal = 10 + 9.99;
  return (
    <>
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          }}
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 20,
              width: "100%",
              maxWidth: 460,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: 3,
                background: `linear-gradient(90deg,${C.orange},${C.accent})`,
              }}
            />
            <div style={{ padding: 24 }}>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📧 + 🎬</div>
                <div
                  style={{
                    fontFamily: "Orbitron,monospace",
                    fontSize: 16,
                    fontWeight: 800,
                    color: C.accent,
                    marginBottom: 4,
                  }}
                >
                  BUNDLE DEAL
                </div>
                <div style={{ fontSize: 13, color: C.muted }}>
                  EMrex + DL REX together
                </div>
              </div>
              <div
                style={{
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                }}
              >
                {[emrex, dlrex].map((t) => (
                  <div
                    key={t.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 0",
                      borderBottom: `1px solid ${C.border}`,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{t.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{ fontSize: 13, fontWeight: 700, color: C.text }}
                      >
                        {t.name}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>
                        {t.tagline}
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: "Orbitron,monospace",
                        fontSize: 14,
                        color: C.accent,
                        fontWeight: 700,
                      }}
                    >
                      ${t.id === 7 ? 10 : "9.99"}
                    </div>
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: 12,
                  }}
                >
                  <span style={{ fontSize: 13, color: C.muted }}>
                    Bundle price:
                  </span>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontFamily: "Orbitron,monospace",
                        fontSize: 24,
                        fontWeight: 900,
                        color: C.orange,
                      }}
                    >
                      ${bundlePrice}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        textDecoration: "line-through",
                      }}
                    >
                      ${originalTotal.toFixed(2)}
                    </div>
                    <div style={{ fontSize: 11, color: C.green }}>
                      Save ${(originalTotal - bundlePrice).toFixed(2)}!
                    </div>
                  </div>
                </div>
              </div>
              <a
                href="https://t.me/zyrexdev_official"
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none" }}
              >
                <button
                  style={{
                    width: "100%",
                    padding: 14,
                    background: `linear-gradient(135deg,${C.orange},#ea580c)`,
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontFamily: "Exo 2,sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                    letterSpacing: "0.05em",
                    marginBottom: 10,
                  }}
                >
                  🔥 Get Bundle — ${bundlePrice} on Telegram →
                </button>
              </a>
              <a
                href="https://link.payway.com.kh/ABAPAYMC436120K"
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none" }}
              >
                <button
                  style={{
                    width: "100%",
                    padding: 12,
                    background: "linear-gradient(135deg,#e63946,#c1121f)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontFamily: "Exo 2,sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    marginBottom: 10,
                  }}
                >
                  🏦 Pay with ABA Pay →
                </button>
              </a>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  width: "100%",
                  padding: 10,
                  background: "transparent",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  color: C.muted,
                  cursor: "pointer",
                  fontFamily: "Exo 2,sans-serif",
                  fontSize: 13,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          background: `linear-gradient(135deg,rgba(251,146,60,0.1),rgba(56,189,248,0.05))`,
          border: `2px dashed ${C.orange}55`,
          borderRadius: 16,
          padding: "28px 24px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: 12, right: 16 }}>
          <span
            style={{
              background: `linear-gradient(135deg,${C.orange},#ea580c)`,
              color: "#fff",
              fontSize: 10,
              fontWeight: 800,
              padding: "4px 10px",
              borderRadius: 20,
              fontFamily: "JetBrains Mono",
              letterSpacing: "0.1em",
            }}
          >
            BUNDLE DEAL
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 40 }}>📧 + 🎬</div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div
              style={{
                fontFamily: "Orbitron,monospace",
                fontSize: 16,
                fontWeight: 800,
                color: C.orange,
                marginBottom: 4,
              }}
            >
              EMrex + DL REX
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              Get both tools together and save $
              {(originalTotal - bundlePrice).toFixed(2)}. Email management +
              bulk video downloading in one deal.
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: "Orbitron,monospace",
                fontSize: 28,
                fontWeight: 900,
                color: C.orange,
              }}
            >
              ${bundlePrice}
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.muted,
                textDecoration: "line-through",
                marginBottom: 8,
              }}
            >
              ${originalTotal.toFixed(2)}
            </div>
            <button
              onClick={() => setShowModal(true)}
              style={{
                background: `linear-gradient(135deg,${C.orange},#ea580c)`,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 20px",
                fontFamily: "Exo 2,sans-serif",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                letterSpacing: "0.05em",
              }}
            >
              🔥 Get Bundle →
            </button>
          </div>
        </div>
      </div>
    </>
  );
}


// ─── CHANGELOG COMPONENT (Feature 6) ─────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function ToolChangelog({ toolId }) {
  const [open, setOpen] = useState(false);
  const logs = CHANGELOGS[toolId];
  if (!logs) return null;
  return (
    <div style={{ marginBottom: 32 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: "10px 16px",
          cursor: "pointer",
          color: C.text,
          fontFamily: "Exo 2,sans-serif",
          fontSize: 13,
          fontWeight: 600,
          width: "100%",
          textAlign: "left",
          transition: "border 0.2s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.borderColor = C.accent + "44")
        }
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
      >
        <span style={{ fontSize: 16 }}>📋</span>
        <span style={{ flex: 1 }}>Version History — Latest: v{logs[0].v}</span>
        <span
          style={{
            color: C.accent,
            fontSize: 16,
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div
          style={{
            border: `1px solid ${C.border}`,
            borderTop: "none",
            borderRadius: "0 0 8px 8px",
            overflow: "hidden",
          }}
        >
          {logs.map((log, i) => (
            <div
              key={i}
              style={{
                padding: "14px 16px",
                borderBottom:
                  i < logs.length - 1 ? `1px solid ${C.border}` : "none",
                background: i === 0 ? C.accentDim : "transparent",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontFamily: "Orbitron,monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    color: i === 0 ? C.accent : C.muted,
                  }}
                >
                  v{log.v}
                </span>
                {i === 0 && (
                  <span
                    style={{
                      background: C.accent,
                      color: C.bg,
                      fontSize: 9,
                      padding: "2px 7px",
                      borderRadius: 10,
                      fontFamily: "JetBrains Mono",
                      fontWeight: 700,
                    }}
                  >
                    LATEST
                  </span>
                )}
                <span
                  style={{
                    fontSize: 11,
                    color: C.muted,
                    fontFamily: "JetBrains Mono",
                    marginLeft: "auto",
                  }}
                >
                  {log.date}
                </span>
              </div>
              {log.changes.map((c, j) => (
                <div
                  key={j}
                  style={{
                    display: "flex",
                    gap: 8,
                    fontSize: 12,
                    color: C.muted,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ color: C.green, flexShrink: 0 }}>✓</span>
                  {c}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Auth helpers ─────────────────────────────────────────────────────────────

function checkPassword(pw) {
  if (pw.length < 8)          return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(pw))      return "Password must contain at least one uppercase letter (A-Z)";
  if (!/[a-z]/.test(pw))      return "Password must contain at least one lowercase letter (a-z)";
  if (!/[0-9]/.test(pw))      return "Password must contain at least one number (0-9)";
  return null;
}

async function apiPost(path, body, token) {
  try {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API}${path}`, { method:"POST", headers, body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.detail || "Request failed");
    return json;
  } catch (e) {
    if (e.message === "Failed to fetch" || e.name === "TypeError") {
      throw new Error("Cannot connect to server. Make sure the backend is running on port 8000.");
    }
    throw e;
  }
}

async function apiPatch(path, body, token) {
  try {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API}${path}`, { method:"PATCH", headers, body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.detail || "Request failed");
    return json;
  } catch (e) {
    if (e.message === "Failed to fetch" || e.name === "TypeError") {
      throw new Error("Cannot connect to server.");
    }
    throw e;
  }
}

async function apiGet(path, token) {
  try {
    const res = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.detail || "Request failed");
    return json;
  } catch (e) {
    if (e.message === "Failed to fetch" || e.name === "TypeError") {
      throw new Error("Cannot connect to server.");
    }
    throw e;
  }
}

// ─── AuthModal ────────────────────────────────────────────────────────────────

function AuthModal({ view: initialView, onClose, onLogin }) {
  const [view, setView] = useState(initialView || "login");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [code, setCode] = useState("");
  const [newPass, setNewPass] = useState("");

  function reset() {
    setStep(1); setError(""); setSuccess("");
    setName(""); setEmail(""); setPassword("");
    setConfirmPass(""); setCode(""); setNewPass("");
  }

  function switchView(v) { reset(); setView(v); }

  // ── Register ──────────────────────────────────────────────────────────────
  async function handleRegister() {
    setError(""); setLoading(true);
    try {
      if (password !== confirmPass) throw new Error("Passwords do not match");
      await apiPost("/auth/register", { email, name, password });
      setStep(2);
      setSuccess(`An 8-digit code was sent to ${email}`);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleVerify() {
    setError(""); setLoading(true);
    try {
      const data = await apiPost("/auth/verify", { email, code });
      localStorage.setItem("toolrex_token", data.token);
      onLogin(data.user);
      onClose();
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  async function handleLogin() {
    setError(""); setLoading(true);
    try {
      const data = await apiPost("/auth/login", { email, password });
      localStorage.setItem("toolrex_token", data.token);
      onLogin(data.user);
      onClose();
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  // ── Forgot / Reset ────────────────────────────────────────────────────────
  async function handleForgot() {
    setError(""); setLoading(true);
    try {
      await apiPost("/auth/forgot", { email });
      setStep(2);
      setSuccess(`A 6-digit reset code was sent to ${email}`);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleReset() {
    setError(""); setLoading(true);
    try {
      if (newPass.length < 8) throw new Error("Password must be at least 8 characters");
      await apiPost("/auth/reset", { email, code, new_password: newPass });
      setSuccess("Password updated! You can now log in.");
      setTimeout(() => switchView("login"), 1800);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  const inputStyle = {
    width: "100%", background: C.surface2, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: "12px 14px", color: C.text, fontSize: 14,
    outline: "none", boxSizing: "border-box",
  };

  const titles = {
    login: ["Sign In", "Welcome back to TOOLREX"],
    register: step === 1 ? ["Create Account", "Join TOOLREX.ONLINE"] : ["Verify Email", `Check ${email}`],
    forgot: step === 1 ? ["Forgot Password", "Reset your password"] : ["Enter Reset Code", `Code sent to ${email}`],
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal" style={{ maxWidth: 420 }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Orbitron,monospace", fontSize: 14, fontWeight: 800, color: C.accent }}>
              {titles[view][0]}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{titles[view][1]}</div>
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6,
            color: C.muted, cursor: "pointer", width: 30, height: 30,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
          }}>✕</button>
        </div>

        <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
          {error && (
            <div style={{ background: "#ef444411", border: "1px solid #ef444433", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: 13 }}>
              ⚠ {error}
            </div>
          )}
          {success && (
            <div style={{ background: `${C.green}11`, border: `1px solid ${C.green}33`, borderRadius: 8, padding: "10px 14px", color: C.green, fontSize: 13 }}>
              ✓ {success}
            </div>
          )}

          {/* ── LOGIN ── */}
          {view === "login" && (<>
            <input style={inputStyle} placeholder="Email address" type="email"
              value={email} onChange={e => setEmail(e.target.value)} />
            <input style={inputStyle} placeholder="Password" type="password"
              value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleLogin()} />
            <button className="btn-primary" style={{ width: "100%", padding: 14 }}
              onClick={handleLogin} disabled={loading}>
              {loading ? "Signing in..." : "Sign In →"}
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted }}>
              <span style={{ cursor: "pointer", color: C.accent }} onClick={() => switchView("register")}>
                Create account
              </span>
              <span style={{ cursor: "pointer" }} onClick={() => switchView("forgot")}>
                Forgot password?
              </span>
            </div>
          </>)}

          {/* ── REGISTER step 1 ── */}
          {view === "register" && step === 1 && (<>
            <input style={inputStyle} placeholder="Your name" type="text"
              value={name} onChange={e => setName(e.target.value)} />
            <input style={inputStyle} placeholder="Email address" type="email"
              value={email} onChange={e => setEmail(e.target.value)} />
            <input style={inputStyle} placeholder="Password (min 8 chars)" type="password"
              value={password} onChange={e => setPassword(e.target.value)} />
            <input style={inputStyle} placeholder="Confirm password" type="password"
              value={confirmPass} onChange={e => setConfirmPass(e.target.value)} />
            <button className="btn-primary" style={{ width: "100%", padding: 14 }}
              onClick={handleRegister} disabled={loading}>
              {loading ? "Sending code..." : "Send Verification Code →"}
            </button>
            <div style={{ textAlign: "center", fontSize: 12, color: C.muted }}>
              Already have an account?{" "}
              <span style={{ color: C.accent, cursor: "pointer" }} onClick={() => switchView("login")}>Sign in</span>
            </div>
          </>)}

          {/* ── REGISTER step 2 — verify 8-digit code ── */}
          {view === "register" && step === 2 && (<>
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📧</div>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.7 }}>
                Enter the <strong style={{ color: C.accent }}>8-digit code</strong> we sent to your email.
                It expires in 10 minutes.
              </p>
            </div>
            <input
              style={{ ...inputStyle, textAlign: "center", letterSpacing: 10, fontSize: 22, fontFamily: "JetBrains Mono,monospace" }}
              placeholder="— — — — — — — —"
              maxLength={8} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={e => e.key === "Enter" && handleVerify()}
            />
            <button className="btn-primary" style={{ width: "100%", padding: 14 }}
              onClick={handleVerify} disabled={loading || code.length !== 8}>
              {loading ? "Verifying..." : "Verify & Activate Account →"}
            </button>
            <div style={{ textAlign: "center", fontSize: 12, color: C.muted }}>
              Wrong email?{" "}
              <span style={{ color: C.accent, cursor: "pointer" }} onClick={() => setStep(1)}>Go back</span>
            </div>
          </>)}

          {/* ── FORGOT step 1 ── */}
          {view === "forgot" && step === 1 && (<>
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Enter your account email and we'll send a <strong style={{ color: C.orange }}>6-digit reset code</strong>.
            </p>
            <input style={inputStyle} placeholder="Email address" type="email"
              value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleForgot()} />
            <button className="btn-primary" style={{ width: "100%", padding: 14 }}
              onClick={handleForgot} disabled={loading}>
              {loading ? "Sending code..." : "Send Reset Code →"}
            </button>
            <div style={{ textAlign: "center", fontSize: 12, color: C.muted }}>
              <span style={{ color: C.accent, cursor: "pointer" }} onClick={() => switchView("login")}>← Back to login</span>
            </div>
          </>)}

          {/* ── FORGOT step 2 — reset with 6-digit code ── */}
          {view === "forgot" && step === 2 && (<>
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🔐</div>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.7 }}>
                Enter the <strong style={{ color: C.orange }}>6-digit code</strong> from your email,
                then set your new password.
              </p>
            </div>
            <input
              style={{ ...inputStyle, textAlign: "center", letterSpacing: 12, fontSize: 22, fontFamily: "JetBrains Mono,monospace" }}
              placeholder="— — — — — —"
              maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
            />
            <input style={inputStyle} placeholder="New password (min 8 chars)" type="password"
              value={newPass} onChange={e => setNewPass(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleReset()} />
            <button className="btn-primary" style={{ width: "100%", padding: 14 }}
              onClick={handleReset} disabled={loading || code.length !== 6}>
              {loading ? "Resetting..." : "Reset Password →"}
            </button>
            <div style={{ textAlign: "center", fontSize: 12, color: C.muted }}>
              <span style={{ color: C.accent, cursor: "pointer" }} onClick={() => setStep(1)}>← Try a different email</span>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─── Splash helpers (defined OUTSIDE SplashScreen to avoid remount on render) ──

const splashInp = {
  width:"100%", background:"rgba(255,255,255,0.03)",
  border:"1px solid rgba(255,255,255,0.08)", borderRadius:10,
  padding:"13px 16px", color:"#e2eaf6", fontSize:14,
  outline:"none", boxSizing:"border-box", fontFamily:"inherit",
};

function SplashField({ label, ...props }) {
  return (
    <div>
      <div style={{ fontSize:11,fontWeight:700,color:"#475569",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6 }}>{label}</div>
      <input className="splash-input" style={splashInp} {...props} />
    </div>
  );
}

function SplashPasswordField({ label, value, onChange, onKeyDown, show, onToggle }) {
  return (
    <div>
      <div style={{ fontSize:11,fontWeight:700,color:"#475569",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6 }}>{label}</div>
      <div style={{ position:"relative" }}>
        <input
          className="splash-input"
          style={{ ...splashInp, paddingRight:44 }}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{
            position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
            background:"none", border:"none", cursor:"pointer",
            color:"#475569", fontSize:16, padding:0, lineHeight:1,
            display:"flex", alignItems:"center",
          }}
          tabIndex={-1}
        >
          {show ? "🙈" : "👁"}
        </button>
      </div>
    </div>
  );
}

function SplashPasswordRules({ password }) {
  const rules = [
    { label:"At least 8 characters",     ok: password.length >= 8 },
    { label:"One uppercase letter (A-Z)", ok: /[A-Z]/.test(password) },
    { label:"One lowercase letter (a-z)", ok: /[a-z]/.test(password) },
    { label:"One number (0-9)",           ok: /[0-9]/.test(password) },
  ];
  if (!password) return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4, marginTop:4 }}>
      {rules.map(r => (
        <div key={r.label} style={{ display:"flex", alignItems:"center", gap:7, fontSize:11,
          color: r.ok ? "#34d399" : "#475569" }}>
          <span style={{ fontSize:10 }}>{r.ok ? "✓" : "○"}</span>
          {r.label}
        </div>
      ))}
    </div>
  );
}

function SplashMsg({ type, text }) {
  if (!text) return null;
  return (
    <div style={{ borderRadius:9,padding:"10px 14px",fontSize:13,marginBottom:2,
      background:type==="e"?"rgba(239,68,68,0.07)":"rgba(52,211,153,0.07)",
      border:`1px solid ${type==="e"?"rgba(239,68,68,0.2)":"rgba(52,211,153,0.2)"}`,
      color:type==="e"?"#f87171":"#34d399" }}>
      {type==="e"?"⚠ ":"✓ "}{text}
    </div>
  );
}

function SplashCta({ children, onClick, disabled, g }) {
  const bg = g || "linear-gradient(135deg,#38bdf8,#7c3aed)";
  return (
    <button className="splash-cta" onClick={onClick} disabled={disabled} style={{
      width:"100%",padding:"14px",border:"none",borderRadius:11,fontFamily:"inherit",
      background:disabled?"rgba(255,255,255,0.04)":bg,
      color:disabled?"#334155":"#fff",fontSize:14,fontWeight:800,
      cursor:disabled?"not-allowed":"pointer",letterSpacing:"0.03em",
      boxShadow:disabled?"none":"0 4px 24px rgba(124,58,237,0.28)",
      transition:"all 0.25s",
    }}>{children}</button>
  );
}

function SplashBack({ onClick, label = "← Back" }) {
  return (
    <button onClick={onClick} style={{ background:"none",border:"none",color:"#334155",cursor:"pointer",fontSize:12,padding:0,fontFamily:"inherit" }}>
      {label}
    </button>
  );
}

function SplashDots({ n, filled, c1, c2 }) {
  return (
    <div style={{ display:"flex",gap:7,justifyContent:"center" }}>
      {[...Array(n)].map((_,i) => (
        <div key={i} style={{ width:8,height:8,borderRadius:"50%",transition:"all 0.25s",
          background:i<filled?`linear-gradient(135deg,${c1},${c2})`:"rgba(255,255,255,0.08)",
          transform:i<filled?"scale(1.25)":"scale(1)" }} />
      ))}
    </div>
  );
}

// ─── Splash Screen ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function SplashScreen({ onLogin }) {
  // tab: "register" | "login"   subview: "form" | "verify" | "forgot" | "forgot2"
  const [tab, setTab] = useState("register");
  const [subview, setSubview] = useState("form");
  const [animKey, setAnimKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [visible, setVisible] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  function switchTab(t) {
    if (t === tab && subview === "form") return;
    setTab(t); setSubview("form");
    setError(""); setSuccess(""); setCode("");
    setAnimKey(k => k + 1);
  }

  function goSub(v) {
    setSubview(v); setError(""); setSuccess(""); setCode("");
    setAnimKey(k => k + 1);
  }

  async function handleRegister() {
    setError(""); setLoading(true);
    try {
      const pwErr = checkPassword(password);
      if (pwErr) throw new Error(pwErr);
      if (password !== confirm) throw new Error("Passwords do not match");
      const d = await apiPost("/auth/register", { email, name, password });
      if (d.auto_verified) {
        // SMTP not set up — account already activated, log in directly
        localStorage.setItem("toolrex_token", d.token);
        onLogin(d.user);
      } else {
        goSub("verify");
        setSuccess(`8-digit code sent to ${email}`);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleVerify() {
    setError(""); setLoading(true);
    try {
      const d = await apiPost("/auth/verify", { email, code });
      localStorage.setItem("toolrex_token", d.token);
      onLogin(d.user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleLogin() {
    setError(""); setLoading(true);
    try {
      const d = await apiPost("/auth/login", { email, password });
      localStorage.setItem("toolrex_token", d.token);
      onLogin(d.user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleForgot() {
    setError(""); setLoading(true);
    try {
      const d = await apiPost("/auth/forgot", { email });
      goSub("forgot2");
      if (d.fallback_code) {
        setSuccess(`Email failed. Your reset code is: ${d.fallback_code}`);
      } else {
        setSuccess(`6-digit reset code sent to ${email}`);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleReset() {
    setError(""); setLoading(true);
    try {
      if (newPass.length < 8) throw new Error("Password must be at least 8 characters");
      await apiPost("/auth/reset", { email, code, new_password: newPass });
      setSuccess("Password updated! Sign in now.");
      setTimeout(() => { switchTab("login"); }, 1600);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  // ── derived ─────────────────────────────────────────────────────────────────
  const isReg = tab === "register";

  // border/bg driven by active tab
  const cardBorder = isReg ? "rgba(124,58,237,0.22)" : "rgba(56,189,248,0.2)";
  const cardBg     = isReg ? "rgba(124,58,237,0.06)" : "rgba(56,189,248,0.05)";
  const topLine    = isReg
    ? "linear-gradient(90deg,transparent,#7c3aed,#a78bfa,transparent)"
    : "linear-gradient(90deg,transparent,#38bdf8,#0ea5e9,transparent)";

  return (
    <div style={{ position:"fixed",inset:0,zIndex:9000,
      display:"flex",alignItems:"flex-start",justifyContent:"center",
      overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"20px",paddingTop:"env(safe-area-inset-top,20px)",
      background: isReg
        ? "radial-gradient(ellipse 70% 70% at 30% 40%,rgba(124,58,237,0.08) 0%,#05080f 60%)"
        : "radial-gradient(ellipse 70% 70% at 70% 40%,rgba(56,189,248,0.07) 0%,#05080f 60%)",
      transition:"background 0.7s ease",
    }}>
      {/* grid */}
      <div style={{ position:"absolute",inset:0,pointerEvents:"none",
        backgroundImage:`linear-gradient(rgba(22,32,53,0.3) 1px,transparent 1px),linear-gradient(90deg,rgba(22,32,53,0.3) 1px,transparent 1px)`,
        backgroundSize:"60px 60px",
        maskImage:"radial-gradient(ellipse 85% 85% at 50% 50%,black 5%,transparent 100%)" }} />

      {/* orbs — shift position by tab */}
      <div style={{ position:"absolute",width:700,height:700,borderRadius:"50%",pointerEvents:"none",
        background:`radial-gradient(circle,${isReg?"rgba(124,58,237,0.11)":"rgba(56,189,248,0.08)"} 0%,transparent 65%)`,
        top:"-20%",left:isReg?"-15%":"auto",right:isReg?"auto":"-10%",
        animation:"orbDrift 14s ease-in-out infinite",transition:"all 0.8s ease" }} />
      <div style={{ position:"absolute",width:500,height:500,borderRadius:"50%",pointerEvents:"none",
        background:`radial-gradient(circle,${isReg?"rgba(56,189,248,0.05)":"rgba(124,58,237,0.08)"} 0%,transparent 65%)`,
        bottom:"-15%",right:isReg?"-8%":"auto",left:isReg?"auto":"-8%",
        animation:"orbDrift 18s ease-in-out infinite reverse",transition:"all 0.8s ease" }} />

      {/* particles */}
      {[...Array(14)].map((_,i) => (
        <div key={i} style={{ position:"absolute",borderRadius:"50%",pointerEvents:"none",
          width:i%4===0?4:i%4===2?3:2, height:i%4===0?4:i%4===2?3:2,
          background:["#38bdf8","#7c3aed","#a78bfa","rgba(255,255,255,0.6)"][i%4],
          left:`${5+i*7}%`, top:`${10+(i%5)*17}%`,
          opacity:0.12+(i%5)*0.06,
          animation:`float ${2.5+i*0.55}s ease-in-out infinite`,
          animationDelay:`${i*0.22}s` }} />
      ))}

      {/* ── main wrapper ── */}
      <div style={{ position:"relative",width:"100%",maxWidth:460,margin:"auto",
        paddingBottom:32,
        animation:visible?"splashIn 0.75s cubic-bezier(0.16,1,0.3,1) forwards":"none",
        opacity:visible?1:0 }}>

        {/* brand */}
        <div style={{ textAlign:"center",marginBottom:22 }}>
          <div style={{ display:"inline-flex",alignItems:"center",gap:12 }}>
            <div style={{ width:42,height:42,borderRadius:"50%",fontSize:19,
              background:`linear-gradient(135deg,rgba(56,189,248,0.14),rgba(124,58,237,0.14))`,
              border:`1.5px solid ${isReg?"rgba(124,58,237,0.35)":"rgba(56,189,248,0.35)"}`,
              display:"flex",alignItems:"center",justifyContent:"center",
              animation:"glow 4s ease-in-out infinite",transition:"border-color 0.5s" }}>⚡</div>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontFamily:"Orbitron,monospace",fontWeight:900,fontSize:16,
                background:"linear-gradient(135deg,#38bdf8,#a78bfa,#fff)",
                backgroundSize:"200% auto",WebkitBackgroundClip:"text",
                WebkitTextFillColor:"transparent",animation:"shimmer 5s linear infinite" }}>TOOLREX.ONLINE</div>
              <div style={{ fontSize:9,color:"#2d3f55",fontFamily:"JetBrains Mono,monospace",letterSpacing:"0.18em",marginTop:2 }}>
                DEV TOOLS MARKETPLACE · BY ZYREX
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD ── */}
        <div style={{ background:`linear-gradient(160deg,${cardBg},rgba(8,12,22,0.98))`,
          border:`1px solid ${cardBorder}`,borderRadius:22,overflow:"hidden",
          backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)",
          boxShadow:`0 40px 80px rgba(0,0,0,0.75),0 0 0 1px ${isReg?"rgba(124,58,237,0.06)":"rgba(56,189,248,0.06)"} inset`,
          transition:"border-color 0.5s,box-shadow 0.5s,background 0.5s" }}>

          {/* animated top line */}
          <div style={{ height:3,background:topLine,transition:"background 0.5s",
            animation:"borderPulse 3s ease-in-out infinite" }} />

          {/* ── TAB SWITCHER ── */}
          {subview === "form" && (
            <div style={{ display:"flex",padding:"6px 6px 0",gap:4,background:"rgba(0,0,0,0.2)" }}>
              {[
                { key:"register", icon:"📧", label:"Register & Connect Email" },
                { key:"login",    icon:"🔐", label:"Sign In" },
              ].map(t => {
                const active = tab === t.key;
                return (
                  <button key={t.key} className="splash-tab-btn"
                    onClick={() => switchTab(t.key)}
                    style={{ flex:1,padding:"11px 8px",border:"none",borderRadius:"10px 10px 0 0",
                      fontFamily:"inherit",cursor:"pointer",fontSize:12,fontWeight:active?800:500,
                      background:active?(t.key==="register"?"rgba(124,58,237,0.18)":"rgba(56,189,248,0.14)"):"transparent",
                      color:active?(t.key==="register"?"#a78bfa":"#38bdf8"):"#334155",
                      borderBottom:active?`2px solid ${t.key==="register"?"#7c3aed":"#38bdf8"}`:"2px solid transparent",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                      transition:"all 0.3s cubic-bezier(0.16,1,0.3,1)",
                    }}>
                    <span style={{ fontSize:14 }}>{t.icon}</span>
                    <span style={{ whiteSpace:"nowrap" }}>{t.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── FORM BODY ── */}
          <div key={animKey} style={{ padding:"28px 28px 32px",
            animation:"slideInUp 0.35s cubic-bezier(0.16,1,0.3,1)" }}>

            <SplashMsg type="e" text={error} />
            <SplashMsg type="s" text={success} />

            {/* REGISTER form */}
            {tab==="register" && subview==="form" && (
              <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                <div style={{ marginBottom:4 }}>
                  <div style={{ fontSize:20,fontWeight:900,color:"#fff",marginBottom:4 }}>Create Account</div>
                  <div style={{ fontSize:12,color:"#475569" }}>
                    An <span style={{ color:"#a78bfa",fontWeight:700 }}>8-digit code</span> will be sent to your email to verify
                  </div>
                </div>
                <SplashField label="Full Name" placeholder="Your name" value={name} onChange={e=>setName(e.target.value)} />
                <SplashField label="Email Address" type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} />
                <div>
                  <SplashPasswordField label="Password" value={password} onChange={e=>setPassword(e.target.value)} show={showPw} onToggle={()=>setShowPw(v=>!v)} />
                  <SplashPasswordRules password={password} />
                </div>
                <SplashPasswordField label="Confirm Password" value={confirm} onChange={e=>setConfirm(e.target.value)} show={showConfirm} onToggle={()=>setShowConfirm(v=>!v)} onKeyDown={e=>e.key==="Enter"&&handleRegister()} />
                <SplashCta onClick={handleRegister} disabled={loading} g="linear-gradient(135deg,#7c3aed,#a78bfa)">
                  {loading?"Sending Code...":"📧 Send 8-Digit Verification Code →"}
                </SplashCta>
              </div>
            )}

            {/* LOGIN form */}
            {tab==="login" && subview==="form" && (
              <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                <div style={{ marginBottom:4 }}>
                  <div style={{ fontSize:20,fontWeight:900,color:"#fff",marginBottom:4 }}>Welcome Back</div>
                  <div style={{ fontSize:12,color:"#475569" }}>Sign in to your TOOLREX account</div>
                </div>
                <SplashField label="Email Address" type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} />
                <SplashPasswordField label="Password" value={password} onChange={e=>setPassword(e.target.value)} show={showPw} onToggle={()=>setShowPw(v=>!v)} onKeyDown={e=>e.key==="Enter"&&handleLogin()} />
                <SplashCta onClick={handleLogin} disabled={loading}>
                  {loading?"Signing In...":"Sign In →"}
                </SplashCta>
                <div style={{ textAlign:"center",fontSize:12 }}>
                  <span style={{ color:"#334155",cursor:"pointer" }} onClick={()=>goSub("forgot")}>Forgot password?</span>
                </div>
              </div>
            )}

            {/* VERIFY — 8-digit */}
            {subview==="verify" && (
              <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:16 }}>
                <div style={{ width:66,height:66,borderRadius:"50%",fontSize:28,
                  background:"linear-gradient(135deg,rgba(124,58,237,0.18),rgba(56,189,248,0.1))",
                  border:"1px solid rgba(124,58,237,0.35)",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  animation:"glow 3s ease-in-out infinite" }}>📧</div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:20,fontWeight:900,color:"#fff",marginBottom:6 }}>Check Your Email</div>
                  <div style={{ fontSize:12,color:"#475569",lineHeight:1.8 }}>
                    Enter the <span style={{ color:"#a78bfa",fontWeight:700 }}>8-digit code</span> sent to<br/>
                    <span style={{ color:"#38bdf8",fontWeight:600 }}>{email}</span>
                  </div>
                </div>
                <input className="splash-input" style={{ ...splashInp,textAlign:"center",letterSpacing:12,
                  fontSize:28,fontFamily:"JetBrains Mono,monospace",padding:"20px",
                  border:`1px solid ${code.length>0?"rgba(124,58,237,0.55)":"rgba(255,255,255,0.08)"}`,
                  background:code.length>0?"rgba(124,58,237,0.07)":"rgba(255,255,255,0.03)",
                  boxShadow:code.length>0?"0 0 28px rgba(124,58,237,0.2)":"none",
                  transition:"all 0.3s",borderRadius:12,width:"100%" }}
                  placeholder="· · · · · · · ·" maxLength={8} value={code}
                  onChange={e=>setCode(e.target.value.replace(/\D/g,""))}
                  onKeyDown={e=>e.key==="Enter"&&code.length===8&&handleVerify()} />
                <SplashDots n={8} filled={code.length} c1="#a78bfa" c2="#38bdf8" />
                <SplashCta onClick={handleVerify} disabled={loading||code.length!==8} g="linear-gradient(135deg,#7c3aed,#38bdf8)">
                  {loading?"Verifying...":"Verify & Activate Account →"}
                </SplashCta>
                <SplashBack onClick={()=>switchTab("register")} label="← Wrong email? Go back" />
              </div>
            )}

            {/* FORGOT — email */}
            {subview==="forgot" && (
              <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                <div style={{ marginBottom:4 }}>
                  <div style={{ fontSize:20,fontWeight:900,color:"#fff",marginBottom:4 }}>Reset Password</div>
                  <div style={{ fontSize:12,color:"#475569",lineHeight:1.7 }}>
                    We'll send a <span style={{ color:"#fb923c",fontWeight:700 }}>6-digit reset code</span> to your email
                  </div>
                </div>
                <SplashField label="Email Address" type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleForgot()} />
                <SplashCta onClick={handleForgot} disabled={loading} g="linear-gradient(135deg,#fb923c,#7c3aed)">
                  {loading?"Sending Code...":"Send 6-Digit Code →"}
                </SplashCta>
                <SplashBack onClick={()=>switchTab("login")} label="← Back to Sign In" />
              </div>
            )}

            {/* RESET — 6-digit + new password */}
            {subview==="forgot2" && (
              <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                <div style={{ textAlign:"center",marginBottom:4 }}>
                  <div style={{ fontSize:34,marginBottom:10 }}>🔐</div>
                  <div style={{ fontSize:20,fontWeight:900,color:"#fff",marginBottom:6 }}>Enter Reset Code</div>
                  <div style={{ fontSize:12,color:"#475569",lineHeight:1.8 }}>
                    <span style={{ color:"#fb923c",fontWeight:700 }}>6-digit code</span> sent to <span style={{ color:"#a78bfa" }}>{email}</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:11,fontWeight:700,color:"#475569",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6 }}>Reset Code</div>
                  <input className="splash-input" style={{ ...splashInp,textAlign:"center",letterSpacing:14,
                    fontSize:26,fontFamily:"JetBrains Mono,monospace",padding:"16px",
                    border:`1px solid ${code.length>0?"rgba(251,146,60,0.5)":"rgba(255,255,255,0.08)"}`,
                    background:code.length>0?"rgba(251,146,60,0.07)":"rgba(255,255,255,0.03)",
                    boxShadow:code.length>0?"0 0 20px rgba(251,146,60,0.18)":"none",transition:"all 0.3s" }}
                    placeholder="· · · · · ·" maxLength={6} value={code}
                    onChange={e=>setCode(e.target.value.replace(/\D/g,""))} />
                  <div style={{ marginTop:10 }}>
                    <SplashDots n={6} filled={code.length} c1="#fb923c" c2="#7c3aed" />
                  </div>
                </div>
                <div>
                  <SplashPasswordField label="New Password" value={newPass} onChange={e=>setNewPass(e.target.value)} show={showNew} onToggle={()=>setShowNew(v=>!v)} onKeyDown={e=>e.key==="Enter"&&handleReset()} />
                  <SplashPasswordRules password={newPass} />
                </div>
                <SplashCta onClick={handleReset} disabled={loading||code.length!==6} g="linear-gradient(135deg,#fb923c,#7c3aed)">
                  {loading?"Resetting...":"Reset Password →"}
                </SplashCta>
                <SplashBack onClick={()=>goSub("forgot")} label="← Try different email" />
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign:"center",marginTop:14 }}>
          <span style={{ color:"#1a2535",fontSize:10,fontFamily:"JetBrains Mono,monospace",letterSpacing:"0.08em" }}>
            © 2026 TOOLREX.ONLINE · Secure · Encrypted · by ZYREX
          </span>
        </div>
      </div>
    </div>
  );
}


function Nav({ page, setPage, authUser, onLogout, onOpenAuth, lang, toggleLang, T }) {
  const [userMenu, setUserMenu] = useState(false);
  const bizActive = ["business","tools","buy","order"].includes(page);
  return (
    <nav className="nav">
      <div className="nav-inner">
        <div className="logo-wrap" onClick={() => setPage("home")}>
          <img src={LOGO_URL} alt="TOOLREX" className="logo-img"
            onError={e => e.target.style.display="none"} />
          <div>
            <span className="logo-text">TOOL<span>REX</span>.ONLINE</span>
            <span className="logo-sub">by ZYREX</span>
          </div>
        </div>
        <div className="nav-links">
          <button className={`nav-link${page==="home"?" active":""}`} onClick={() => setPage("home")}>{T?.home||"Home"}</button>
          <button className={`nav-link${bizActive?" active":""}`} onClick={() => setPage("business")}>{T?.business||"Business"}</button>
          <button
            className={`nav-link${page==="ai"?" active":""}`}
            onClick={() => setPage("ai")}
            style={{
              display:"flex", alignItems:"center", gap:6, position:"relative",
              padding:"7px 15px", fontSize:12, fontWeight:800,
              background: page==="ai" ? "rgba(99,102,241,0.2)" : "linear-gradient(135deg,#6366f1,#a855f7)",
              border: page==="ai" ? "1px solid rgba(99,102,241,0.5)" : "none",
              borderRadius:8, color:"#fff", cursor:"pointer",
              fontFamily:"Exo 2,sans-serif", letterSpacing:"0.06em",
              boxShadow: page==="ai" ? "none" : "0 0 14px rgba(99,102,241,0.45)",
              animation: page==="ai" ? "none" : "aiNavGlow 2.5s ease-in-out infinite",
              transition:"all 0.2s", overflow:"hidden",
            }}
            onMouseEnter={e => { if(page!=="ai"){ e.currentTarget.style.transform="translateY(-2px) scale(1.05)"; e.currentTarget.style.boxShadow="0 0 28px rgba(168,85,247,0.65)"; }}}
            onMouseLeave={e => { e.currentTarget.style.transform="translateY(0) scale(1)"; e.currentTarget.style.boxShadow= page==="ai"?"none":"0 0 14px rgba(99,102,241,0.45)"; }}
          >
            <span style={{ fontSize:14 }}>🤖</span>
            <span>{T?.ai||"AI"}</span>
            {page !== "ai" && <span style={{ position:"absolute", top:4, right:4, width:7, height:7, borderRadius:"50%", background:"#f87171", boxShadow:"0 0 6px #f87171", animation:"pulse 1.5s ease-in-out infinite" }}/>}
            <span style={{ position:"absolute", inset:0, background:"linear-gradient(135deg,rgba(255,255,255,0.1),transparent)", pointerEvents:"none" }}/>
          </button>
          <button className={`nav-link${page==="contact"?" active":""}`} onClick={() => setPage("contact")}>{T?.contact||"Contact"}</button>
          <button className={`nav-link${page==="faq"?" active":""}`} onClick={() => setPage("faq")}>FAQ</button>
        </div>
        <div className="nav-auth" style={{ display:"flex", alignItems:"center", gap:8 }}>
          {toggleLang && <button onClick={toggleLang} style={{
            padding:"6px 10px", fontSize:11, fontWeight:800, borderRadius:8, cursor:"pointer",
            background:"rgba(99,102,241,0.1)", border:"1px solid rgba(99,102,241,0.25)",
            color:"#a5b4fc", fontFamily:"JetBrains Mono,monospace", letterSpacing:"0.05em",
            whiteSpace:"nowrap",
          }}>{lang==="en" ? "🇰🇭 KH" : "🇺🇸 EN"}</button>}
          {authUser ? (
            <div style={{ position:"relative" }}>
              <button onClick={() => setUserMenu(m => !m)} style={{
                padding:"7px 14px", fontSize:12, fontWeight:700,
                border:`1px solid ${C.indigoGlow}`, borderRadius:8,
                background:C.indigoDim, color:"#a5b4fc", cursor:"pointer",
                display:"flex", alignItems:"center", gap:7,
              }}>
                <span style={{ fontSize:15 }}>👤</span>
                {authUser.name.split(" ")[0]}
                <span style={{ fontSize:9 }}>▾</span>
              </button>
              {userMenu && (
                <div style={{
                  position:"absolute", top:"calc(100% + 8px)", right:0,
                  background:"#0d0f1e", border:"1px solid rgba(99,102,241,0.2)",
                  borderRadius:12, padding:"6px", minWidth:190, zIndex:300,
                  boxShadow:"0 12px 40px rgba(0,0,0,0.7)",
                }}>
                  <div style={{ padding:"10px 14px 8px", borderBottom:"1px solid rgba(255,255,255,0.06)", marginBottom:6 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.textBright }}>{authUser.name}</div>
                    <div style={{ fontSize:11, color:C.mutedBright, marginTop:2 }}>{authUser.email}</div>
                  </div>
                  <button onClick={() => { setUserMenu(false); setPage("account"); }} style={{
                    width:"100%", textAlign:"left", padding:"9px 14px",
                    background:"transparent", border:"none", color:"#a5b4fc",
                    cursor:"pointer", borderRadius:6, fontSize:12,
                  }}>⚙️ My Account</button>
                  <button onClick={() => { setUserMenu(false); onLogout(); }} style={{
                    width:"100%", textAlign:"left", padding:"9px 14px",
                    background:"transparent", border:"none", color:"#f87171",
                    cursor:"pointer", borderRadius:6, fontSize:12,
                  }}>🚪 Sign Out</button>
                </div>
              )}
            </div>
          ) : bizActive ? (
            <>
              <button style={{
                padding:"8px 16px", fontSize:12, fontWeight:700,
                background:"transparent", border:"1px solid rgba(99,102,241,0.35)",
                borderRadius:8, color:"#a5b4fc", cursor:"pointer",
                fontFamily:"Exo 2,sans-serif", transition:"all 0.2s",
              }}
                onClick={() => onOpenAuth("login")}
                onMouseEnter={e => e.currentTarget.style.background="rgba(99,102,241,0.1)"}
                onMouseLeave={e => e.currentTarget.style.background="transparent"}
              >Sign In</button>
              <button className="neon-btn" style={{ padding:"8px 18px", fontSize:12 }}
                onClick={() => onOpenAuth("register")}>
                <span>Register</span>
              </button>
            </>
          ) : null}
        </div>
      </div>
    </nav>
  );
}

function CountdownTimer() {
  const [time, setTime] = useState({ h: 23, m: 47, s: 59 });
  useEffect(() => {
    const t = setInterval(() => {
      setTime((prev) => {
        let { h, m, s } = prev;
        s--;
        if (s < 0) {
          s = 59;
          m--;
        }
        if (m < 0) {
          m = 59;
          h--;
        }
        if (h < 0) {
          h = 23;
          m = 59;
          s = 59;
        }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const units = [pad(time.h), pad(time.m), pad(time.s)];
  const labels = ["HRS", "MIN", "SEC"];
  return (
    <div className="timer-bar" style={{ marginBottom: 24 }}>
      <span style={{ fontSize: 18 }}>🔥</span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            color: "#fb923c",
            fontWeight: 700,
            letterSpacing: "0.05em",
            marginBottom: 2,
          }}
        >
          🔥 SPECIAL LAUNCH PRICE — $1 PER YEAR!
        </div>
        <div style={{ fontSize: 11, color: C.muted }}>
          1 key · 1 device · 1 year · Free group support
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {units
          .map((v, i) => (
            <span
              key={i}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <span
                style={{
                  fontFamily: "Orbitron, monospace",
                  fontSize: 18,
                  fontWeight: 900,
                  color: "#fb923c",
                }}
              >
                {v}
              </span>
              <span
                style={{ fontSize: 8, color: C.muted, letterSpacing: "0.1em" }}
              >
                {labels[i]}
              </span>
            </span>
          ))
          .reduce(
            (acc, el, i) =>
              i === 0
                ? [el]
                : [
                    ...acc,
                    <span
                      key={"d" + i}
                      style={{
                        color: "#fb923c",
                        fontWeight: 900,
                        fontSize: 18,
                        paddingBottom: 12,
                      }}
                    >
                      :
                    </span>,
                    el,
                  ],
            [],
          )}
      </div>
    </div>
  );
}

function PaymentModal({ plan, tool, onClose }) {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("");
  if (!plan) return null;
  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal">
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: "Orbitron, monospace",
                fontSize: 14,
                fontWeight: 800,
                color: C.accent,
              }}
            >
              CHECKOUT
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {tool.name} · {plan.label} License
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.muted,
              cursor: "pointer",
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: 24 }}>
          {step === 1 && (
            <>
              <div
                style={{
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  padding: 20,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: C.accent,
                    letterSpacing: "0.15em",
                    fontFamily: "JetBrains Mono, monospace",
                    marginBottom: 14,
                  }}
                >
                  ORDER SUMMARY
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                    }}
                  >
                    {tool.icon}
                  </div>
                  <div>
                    <div
                      style={{ fontSize: 14, fontWeight: 700, color: C.text }}
                    >
                      {tool.name}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>
                      {plan.label} License
                    </div>
                  </div>
                  <div style={{ marginLeft: "auto", textAlign: "right" }}>
                    <div
                      style={{
                        fontFamily: "Orbitron, monospace",
                        fontSize: 22,
                        fontWeight: 900,
                        color: C.accent,
                      }}
                    >
                      ${plan.price}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        textDecoration: "line-through",
                      }}
                    >
                      ${plan.original}
                    </div>
                  </div>
                </div>
                <div
                  style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}
                >
                  {plan.perks.slice(0, 3).map((p, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 12,
                        color: C.muted,
                        display: "flex",
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ color: C.green }}>✓</span>
                      {p}
                    </div>
                  ))}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 20,
                }}
              >
                <span style={{ fontSize: 14, color: C.muted }}>Total</span>
                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      fontFamily: "Orbitron, monospace",
                      fontSize: 26,
                      fontWeight: 900,
                      color: C.accent,
                    }}
                  >
                    ${plan.price}
                  </span>
                  <div style={{ fontSize: 11, color: C.green }}>
                    You save ${plan.original - plan.price} ({plan.discount})
                  </div>
                </div>
              </div>
              <button
                className="btn-primary"
                style={{
                  width: "100%",
                  padding: 16,
                  fontSize: 14,
                  marginBottom: 12,
                }}
                onClick={() => setStep(2)}
              >
                Continue to Payment →
              </button>
              <div
                style={{
                  textAlign: "center",
                  fontSize: 11,
                  color: C.muted,
                  fontFamily: "JetBrains Mono",
                }}
              >
                🔒 Secure checkout · 30-day refund guarantee
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
                Choose how you want to pay:
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                {[
                  {
                    id: "telegram",
                    icon: "✈️",
                    label: "Pay via Telegram",
                    desc: "DM @zyrexdev_official — fastest and easiest",
                    color: "#229ED9",
                  },
                  {
                    id: "stripe",
                    icon: "💳",
                    label: "Pay with Card (Stripe)",
                    desc: "Credit or Debit card · Instant access",
                    color: C.accent,
                  },
                  {
                    id: "paypal",
                    icon: "🅿️",
                    label: "Pay with PayPal",
                    desc: "PayPal balance or card",
                    color: "#0070ba",
                  },
                ].map((m) => (
                  <div
                    key={m.id}
                    onClick={() => setMethod(m.id)}
                    style={{
                      padding: "16px 20px",
                      border: `2px solid ${method === m.id ? m.color : C.border}`,
                      borderRadius: 12,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      background:
                        method === m.id ? m.color + "11" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{m.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{ fontSize: 14, fontWeight: 700, color: C.text }}
                      >
                        {m.label}
                      </div>
                      <div style={{ fontSize: 12, color: C.muted }}>
                        {m.desc}
                      </div>
                    </div>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: `2px solid ${method === m.id ? m.color : C.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {method === m.id && (
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: m.color,
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {method === "telegram" && (
                <a
                  href="https://t.me/zyrexdev_official"
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <button
                    className="btn-tg"
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      padding: 16,
                      fontSize: 14,
                      marginBottom: 12,
                    }}
                  >
                    ✈ Open Telegram — @zyrexdev_official
                  </button>
                </a>
              )}
              {(method === "stripe" || method === "paypal") && (
                <a
                  href="https://t.me/zyrexdev_official"
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <button
                    className="btn-primary"
                    style={{
                      width: "100%",
                      padding: 16,
                      fontSize: 14,
                      marginBottom: 12,
                    }}
                  >
                    💬 Contact for Payment Link
                  </button>
                </a>
              )}
              {!method && (
                <button
                  className="btn-primary"
                  style={{
                    width: "100%",
                    padding: 16,
                    fontSize: 14,
                    marginBottom: 12,
                    opacity: 0.5,
                    cursor: "not-allowed",
                  }}
                  disabled
                >
                  Select a payment method
                </button>
              )}
              <button
                className="btn-ghost"
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
                onClick={() => setStep(1)}
              >
                ← Back to Order Summary
              </button>
              <div
                style={{
                  textAlign: "center",
                  marginTop: 16,
                  fontSize: 11,
                  color: C.muted,
                  fontFamily: "JetBrains Mono",
                }}
              >
                🔒 Safe and Secure · All plans include 30-day refund
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ToolCard({ tool, onDetail }) {
  const price = tool.pricing ? tool.pricing[0].price : tool.price;
  const original = tool.pricing ? tool.pricing[0].original : tool.originalPrice;
  const discount = Math.round((1 - price / original) * 100);
  return (
    <div
      className="card"
      onClick={() => onDetail(tool)}
      style={{ cursor: "pointer" }}
    >
      {tool.isFeatured && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: "linear-gradient(90deg, #fb923c, #38bdf8)",
          }}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 36 }}>{tool.icon}</span>
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {tool.isNew && <span className="new-badge">New</span>}
          {tool.isFeatured && <span className="hot-badge">🔥 Hot</span>}
          {discount >= 70 && (
            <span className="tag-orange">{discount}% OFF</span>
          )}
        </div>
      </div>
      <h3 style={{ fontSize: 16, marginBottom: 6, letterSpacing: "0.03em" }}>
        {tool.name}
      </h3>
      <p
        style={{
          color: C.muted,
          fontSize: 13,
          marginBottom: 16,
          lineHeight: 1.6,
        }}
      >
        {tool.tagline}
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <span
            style={{
              fontFamily: "Orbitron",
              fontSize: 20,
              fontWeight: 900,
              color: C.accent,
            }}
          >
            ${price}
          </span>
          <span
            style={{
              color: C.muted,
              fontSize: 12,
              textDecoration: "line-through",
              marginLeft: 8,
            }}
          >
            ${original}
          </span>
        </div>
        <span className="tag">{tool.category}</span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: C.muted,
            fontFamily: "JetBrains Mono",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span style={{ color: C.green }}>✓</span>
          {SOLD_COUNTS[tool.id] || Math.floor(Math.random() * 100 + 50)} sold
        </div>
        <div
          style={{
            fontSize: 10,
            color: C.muted,
            fontFamily: "JetBrains Mono",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          🛡️ 30-day refund
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  HOME PAGE — Developer Portfolio
// ═══════════════════════════════════════════════════════════════════════════════
function Hero3D() {
  const mountRef = useRef(null);
  useEffect(() => {
    let animId;
    const el = mountRef.current;
    if (!el) return;
    import("three").then(THREE => {
      const W = el.clientWidth, H = el.clientHeight;
      const renderer = new THREE.WebGLRenderer({ antialias:true, alpha:true });
      renderer.setSize(W, H);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      el.appendChild(renderer.domElement);

      const scene  = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(55, W/H, 0.1, 100);
      camera.position.z = 5;

      // ── AI NEURAL CORE ──────────────────────────────────────
      // 1. Central brain sphere — glowing iridescent core
      const coreGeo = new THREE.IcosahedronGeometry(0.9, 4);
      const coreMat = new THREE.MeshStandardMaterial({
        color:0x0d0f1e, metalness:0.9, roughness:0.1,
        emissive:0x6366f1, emissiveIntensity:0.4,
      });
      const core = new THREE.Mesh(coreGeo, coreMat);
      scene.add(core);

      // Core wireframe
      const coreWire = new THREE.Mesh(coreGeo,
        new THREE.MeshBasicMaterial({ color:0xa5b4fc, wireframe:true, opacity:0.15, transparent:true }));
      scene.add(coreWire);

      // 2. Three orbital rings at different angles
      const ringData = [
        { color:0x6366f1, rx:0,              ry:0,              speed:0.8,  r:1.7, tube:0.018 },
        { color:0xa855f7, rx:Math.PI/2,       ry:0,              speed:-0.6, r:1.9, tube:0.018 },
        { color:0x38bdf8, rx:Math.PI/4,       ry:Math.PI/3,      speed:0.5,  r:2.1, tube:0.016 },
      ];
      const rings = ringData.map(d => {
        const geo = new THREE.TorusGeometry(d.r, d.tube, 16, 120);
        const mat = new THREE.MeshBasicMaterial({ color:d.color, transparent:true, opacity:0.7 });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.rotation.x = d.rx; mesh.rotation.y = d.ry;
        scene.add(mesh);
        return { mesh, speed:d.speed };
      });

      // 3. Electron dots on rings (small spheres)
      const electronGeo = new THREE.SphereGeometry(0.06, 8, 8);
      const electrons = ringData.map((d, ri) => {
        const mat = new THREE.MeshBasicMaterial({ color:d.color });
        const dots = [];
        const count = 3 + ri;
        for (let i=0; i<count; i++) {
          const dot = new THREE.Mesh(electronGeo, mat);
          scene.add(dot);
          dots.push({ dot, offset: (i/count)*Math.PI*2, r:d.r, rx:d.rx, ry:d.ry });
        }
        return dots;
      }).flat();

      // 4. Neural network nodes — floating connected spheres
      const nodeGeo = new THREE.SphereGeometry(0.07, 8, 8);
      const nodePositions = Array.from({length:18}, () => ({
        x:(Math.random()-0.5)*6, y:(Math.random()-0.5)*5, z:(Math.random()-0.5)*3,
        vx:(Math.random()-0.5)*0.004, vy:(Math.random()-0.5)*0.004,
      }));
      const nodes = nodePositions.map(p => {
        const m = new THREE.Mesh(nodeGeo,
          new THREE.MeshBasicMaterial({ color: [0x6366f1,0xa855f7,0x38bdf8][Math.floor(Math.random()*3)] }));
        m.position.set(p.x, p.y, p.z);
        scene.add(m);
        return { m, p };
      });

      // Connection lines between nearby nodes
      const lineMat = new THREE.LineBasicMaterial({ color:0x6366f1, transparent:true, opacity:0.2 });
      const lineGroup = new THREE.Group();
      scene.add(lineGroup);

      function rebuildLines() {
        lineGroup.clear();
        for (let i=0; i<nodes.length; i++) {
          for (let j=i+1; j<nodes.length; j++) {
            const d = nodes[i].m.position.distanceTo(nodes[j].m.position);
            if (d < 2.5) {
              const pts = [nodes[i].m.position.clone(), nodes[j].m.position.clone()];
              lineGroup.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), lineMat));
            }
          }
        }
      }

      // 5. Particle dust
      const dustPos = new Float32Array(900);
      for (let i=0; i<dustPos.length; i++) dustPos[i] = (Math.random()-0.5)*12;
      const dustGeo = new THREE.BufferGeometry();
      dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
      scene.add(new THREE.Points(dustGeo, new THREE.PointsMaterial({ color:0x4f46e5, size:0.03, transparent:true, opacity:0.5 })));

      // Lights
      scene.add(new THREE.AmbientLight(0xffffff, 0.4));
      const pl1 = new THREE.PointLight(0x6366f1, 4, 12); pl1.position.set(3,3,3); scene.add(pl1);
      const pl2 = new THREE.PointLight(0xa855f7, 3, 10); pl2.position.set(-3,-2,2); scene.add(pl2);
      const pl3 = new THREE.PointLight(0x38bdf8, 2, 8);  pl3.position.set(0,3,-2); scene.add(pl3);

      let mx=0, my=0;
      const onMouse = e => { mx=(e.clientX/window.innerWidth-0.5)*1.5; my=-(e.clientY/window.innerHeight-0.5)*1.5; };
      window.addEventListener("mousemove", onMouse);

      let frame = 0;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        const t = Date.now()*0.001;
        frame++;

        // Core pulse
        const pulse = 1 + Math.sin(t*2)*0.04;
        core.scale.setScalar(pulse);
        coreWire.scale.setScalar(pulse*1.01);
        coreMat.emissiveIntensity = 0.3 + Math.sin(t*1.5)*0.15;

        // Core mouse tracking
        core.rotation.y = t*0.15 + mx*0.4;
        core.rotation.x = my*0.3;
        coreWire.rotation.y = core.rotation.y;
        coreWire.rotation.x = core.rotation.x;

        // Rings orbit
        rings.forEach((r, i) => { r.mesh.rotation.z += r.speed * 0.008; });

        // Electrons follow rings
        electrons.forEach(e => {
          const a = t * 1.2 + e.offset;
          const x = Math.cos(a)*e.r, y = Math.sin(a)*e.r;
          // apply ring rotation
          const cosX = Math.cos(e.rx), sinX = Math.sin(e.rx);
          const cosY = Math.cos(e.ry), sinY = Math.sin(e.ry);
          e.dot.position.x = x*cosY + 0;
          e.dot.position.y = x*sinX*sinY + y*cosX;
          e.dot.position.z = -x*cosX*sinY + y*sinX;
        });

        // Nodes drift
        nodes.forEach(({ m, p }) => {
          p.x += p.vx; p.y += p.vy;
          if (Math.abs(p.x) > 3.5) p.vx *= -1;
          if (Math.abs(p.y) > 2.8) p.vy *= -1;
          m.position.set(p.x, p.y, p.z);
        });
        if (frame % 8 === 0) rebuildLines();

        // Light orbit
        pl1.position.x = Math.sin(t*0.5)*4;
        pl1.position.y = Math.cos(t*0.4)*3;
        pl2.position.x = Math.cos(t*0.3)*4;
        pl2.position.z = Math.sin(t*0.6)*3;

        renderer.render(scene, camera);
      };
      animate();

      const onResize = () => {
        const W2=el.clientWidth, H2=el.clientHeight;
        camera.aspect=W2/H2; camera.updateProjectionMatrix();
        renderer.setSize(W2,H2);
      };
      window.addEventListener("resize", onResize);

      return () => {
        cancelAnimationFrame(animId);
        window.removeEventListener("mousemove", onMouse);
        window.removeEventListener("resize", onResize);
        renderer.dispose();
        if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
      };
    });
    return () => cancelAnimationFrame(animId);
  }, []);
  return <div ref={mountRef} style={{ position:"absolute", inset:0, zIndex:0 }} />;
}

function HomePage({ setPage, T }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), 80); }, []);

  const CORE_SKILLS = [
    { n:"Python",      c:"#3776ab" }, { n:"React.js",    c:"#38bdf8" },
    { n:"FastAPI",     c:"#009688" }, { n:"AI & ML",     c:"#a855f7" },
    { n:"Node.js",     c:"#339933" }, { n:"Docker",      c:"#2496ed" },
    { n:"PostgreSQL",  c:"#336791" }, { n:"TypeScript",  c:"#3178c6" },
  ];

  return (
    <div style={{ paddingTop:68 }}>

      {/* ── HERO ── */}
      <div style={{
        minHeight:"100vh", display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center", textAlign:"center",
        padding:"80px 24px 60px", position:"relative", overflow:"hidden",
        background:"linear-gradient(180deg,#050508 0%,#07091a 100%)",
      }}>
        {/* 3D background */}
        <Hero3D />
        {/* Subtle overlay so text stays readable */}
        <div style={{ position:"absolute", inset:0, zIndex:1, pointerEvents:"none",
          background:"radial-gradient(ellipse 60% 60% at 50% 50%,transparent 30%,#05080f 100%)" }}/>
        <div style={{ position:"absolute", width:600, height:600, borderRadius:"50%", top:"-10%", left:"-5%", pointerEvents:"none", zIndex:1,
          background:"radial-gradient(circle,rgba(99,102,241,0.08) 0%,transparent 65%)" }}/>
        <div style={{ position:"absolute", width:500, height:500, borderRadius:"50%", bottom:"-5%", right:"-5%", pointerEvents:"none", zIndex:1,
          background:"radial-gradient(circle,rgba(168,85,247,0.07) 0%,transparent 65%)" }}/>

        <div style={{ position:"relative", zIndex:2, opacity:vis?1:0, transform:vis?"translateY(0)":"translateY(24px)", transition:"all 0.9s cubic-bezier(0.16,1,0.3,1)", maxWidth:720, width:"100%" }}>

          {/* Animated Developer Avatar */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:28 }}>
            <div style={{ position:"relative", width:96, height:96 }}>
              {/* Spinning gradient ring */}
              <div style={{
                position:"absolute", inset:-4, borderRadius:"50%",
                background:"linear-gradient(135deg,#6366f1,#38bdf8,#a855f7,#6366f1)",
                backgroundSize:"300% 300%", animation:"gradShift 3s ease infinite, spin 6s linear infinite",
                padding:3,
              }}>
                <div style={{ width:"100%", height:"100%", borderRadius:"50%", background:"#05080f" }}/>
              </div>
              {/* Avatar circle */}
              <div style={{
                position:"absolute", inset:0, borderRadius:"50%",
                background:"linear-gradient(135deg,#0d0f1e,#07091a)",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:38, zIndex:1,
                boxShadow:"0 0 40px rgba(99,102,241,0.3)",
              }}>⚡</div>
              {/* Orbiting dot */}
              <div style={{
                position:"absolute", top:-2, left:"50%", marginLeft:-4,
                width:8, height:8, borderRadius:"50%", background:"#a5b4fc",
                boxShadow:"0 0 12px #6366f1",
                transformOrigin:"4px 50px",
                animation:"spin 3s linear infinite",
              }}/>
              {/* Status dot */}
              <div style={{
                position:"absolute", bottom:4, right:4, zIndex:2,
                width:16, height:16, borderRadius:"50%", background:"#4ade80",
                border:"2px solid #05080f", boxShadow:"0 0 10px #4ade80",
                animation:"pulse 2s ease-in-out infinite",
              }}/>
            </div>
          </div>

          <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"6px 16px", borderRadius:100,
            background:"rgba(99,102,241,0.1)", border:"1px solid rgba(99,102,241,0.25)", marginBottom:20 }}>
            <span style={{ fontSize:12, color:"#a5b4fc", fontFamily:"JetBrains Mono,monospace", letterSpacing:"0.06em" }}>ZYREX · Available for freelance</span>
          </div>

          <h1 style={{ fontSize:"clamp(32px,6vw,68px)", fontWeight:900, lineHeight:1.08, marginBottom:6, letterSpacing:"-0.02em",
            background:"linear-gradient(135deg,#f0f4ff 40%,#a5b4fc 100%)",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
            Full-Stack Developer
          </h1>
          <h1 style={{ fontSize:"clamp(32px,6vw,68px)", fontWeight:900, lineHeight:1.08, marginBottom:24, letterSpacing:"-0.02em",
            background:"linear-gradient(135deg,#6366f1,#38bdf8,#a855f7)",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
            backgroundSize:"200% auto", animation:"gradShift 4s ease infinite" }}>
            & AI Builder
          </h1>
          <p style={{ fontSize:"clamp(14px,2vw,17px)", color:"#8892c0", lineHeight:1.8, maxWidth:520, margin:"0 auto 36px" }}>
            Building AI systems, automation tools, and scalable apps.
            Creator of <strong style={{color:"#a5b4fc"}}>TOOLREX.ONLINE</strong>.
          </p>

          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap", marginBottom:56 }}>
            <button className="neon-btn" style={{ padding:"13px 32px", fontSize:14 }} onClick={() => setPage("business")}>
              <span>⚡ Explore Tools</span>
            </button>
            <button className="neon-btn-outline" style={{ padding:"13px 28px", fontSize:14 }} onClick={() => setPage("contact")}>
              📩 Contact
            </button>
          </div>

          {/* 3 stats only */}
          <div style={{ display:"flex", gap:32, justifyContent:"center", flexWrap:"wrap" }}>
            {[{num:"10+",label:"Tools Built"},{num:"50+",label:"Projects"},{num:"3+",label:"Years"}].map(s=>(
              <div key={s.label} style={{ textAlign:"center" }}>
                <div style={{ fontSize:"clamp(22px,4vw,30px)", fontWeight:900, fontFamily:"Orbitron,monospace",
                  background:"linear-gradient(135deg,#6366f1,#38bdf8)",
                  WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>{s.num}</div>
                <div style={{ fontSize:11, color:"#6b7b99", marginTop:4, fontFamily:"JetBrains Mono,monospace", letterSpacing:"0.1em" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ABOUT ── */}
      <div style={{ background:"#07091a", borderTop:"1px solid rgba(99,102,241,0.12)", padding:"72px 24px" }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div className="dev-hero-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, alignItems:"center" }}>
            <div>
              <div className="section-label" style={{ color:"#6366f1", marginBottom:12 }}>About</div>
              <h2 style={{ fontSize:"clamp(24px,3vw,36px)", fontWeight:900, lineHeight:1.15, marginBottom:16,
                background:"linear-gradient(135deg,#f0f4ff,#a5b4fc)",
                WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                AI Engineer &<br/>Software Architect
              </h2>
              <p style={{ color:"#8892c0", fontSize:14, lineHeight:1.9, marginBottom:24 }}>
                Focused on <strong style={{color:"#a5b4fc"}}>Full-Stack Engineering</strong>, AI Systems, and
                Business Automation. Built TOOLREX.ONLINE — a developer marketplace with AI-powered purchase flows.
              </p>
              {/* Skills pills */}
              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:24 }}>
                {CORE_SKILLS.map(s=>(
                  <span key={s.n} style={{ fontSize:12, fontWeight:700, padding:"5px 12px", borderRadius:8,
                    background:`${s.c}14`, border:`1px solid ${s.c}33`, color:s.c,
                    fontFamily:"JetBrains Mono,monospace" }}>{s.n}</span>
                ))}
              </div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {[["✈️","Telegram","https://t.me/zyrexdev_official"],["⌥","GitHub","https://github.com/zyrex-developer"]].map(([icon,label,href])=>(
                  <a key={label} href={href} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
                    <button className="neon-btn-outline" style={{ padding:"8px 16px", fontSize:12 }}>{icon} {label}</button>
                  </a>
                ))}
              </div>
            </div>
            <div className="terminal" style={{ borderColor:"rgba(99,102,241,0.2)" }}>
              <div className="terminal-bar" style={{ background:"#0c0e1e", borderColor:"rgba(99,102,241,0.15)" }}>
                <div className="t-dot" style={{background:"#f87171"}}/><div className="t-dot" style={{background:"#fbbf24"}}/><div className="t-dot" style={{background:"#34d399"}}/>
                <span style={{fontSize:11,color:"#6b7b99",marginLeft:8,fontFamily:"JetBrains Mono,monospace"}}>zyrex@toolrex</span>
              </div>
              <div className="terminal-body" style={{ background:"#0c0e1e", fontSize:13 }}>
                {[["$","whoami","#a5b4fc"],["→","zyrex-developer","#f0f4ff"],["$","cat stack.txt","#a5b4fc"],["→","Python · React · FastAPI · AI","#34d399"],["$","ls projects/","#a5b4fc"],["→","toolrex/ emrex/ dlrex/ airex/","#38bdf8"],["$","echo $STATUS","#a5b4fc"],["→","Open to work ✓","#fbbf24"]].map(([pre,txt,clr],i)=>(
                  <div key={i} style={{color:clr,marginBottom:4}}><span style={{color:"#6366f1",marginRight:8}}>{pre}</span>{txt}</div>
                ))}
                <span style={{color:"#6366f1",animation:"blink 1s step-end infinite",borderRight:"2px solid #6366f1"}}>&nbsp;</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{ padding:"72px 24px", textAlign:"center", background:"linear-gradient(180deg,#07091a,#050508)", borderTop:"1px solid rgba(99,102,241,0.1)" }}>
        <div style={{ maxWidth:560, margin:"0 auto" }}>
          <h2 style={{ fontSize:"clamp(24px,4vw,38px)", fontWeight:900, marginBottom:12,
            background:"linear-gradient(135deg,#6366f1,#38bdf8,#a855f7)",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
            backgroundSize:"200% auto", animation:"gradShift 4s ease infinite" }}>
            Ready to Build?
          </h2>
          <p style={{ color:"#8892c0", fontSize:14, lineHeight:1.8, marginBottom:28 }}>
            Browse my tools, request a custom build, or reach out on Telegram.
          </p>
          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <button className="neon-btn" style={{ padding:"13px 32px", fontSize:14 }} onClick={() => setPage("business")}>
              <span>⚡ Explore Business</span>
            </button>
            <a href="https://t.me/zyrexdev_official" target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
              <button className="btn-tg" style={{ borderRadius:10 }}>✈ Telegram</button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function ToolsPage({ onDetail }) {
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const filtered = ALL_TOOLS.filter(
    (t) =>
      (cat === "All" || t.category === cat) &&
      (t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.tagline.toLowerCase().includes(search.toLowerCase())),
  );
  return (
    <div style={{ paddingTop: 68 }}>
      <div className="section">
        <span className="label">Tool Catalog</span>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: 20,
            marginBottom: 40,
          }}
        >
          <div>
            <h1 style={{ fontSize: 44, letterSpacing: "0.02em" }}>All Tools</h1>
            <p style={{ color: C.muted, fontSize: 14, marginTop: 8 }}>
              Lifetime licenses · One-time payment · Instant access
            </p>
          </div>
          <input
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260, flexShrink: 0 }}
          />
        </div>
        <div
          style={{
            background: C.accentDim,
            border: `1px solid ${C.accent}22`,
            borderRadius: 10,
            padding: "12px 20px",
            marginBottom: 36,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 13,
          }}
        >
          <span>🔑</span>
          <span style={{ color: C.text }}>
            All tools come with a{" "}
            <strong style={{ color: C.accent }}>Lifetime License</strong> — pay
            once, use forever.
          </span>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 36,
            flexWrap: "wrap",
          }}
        >
          {CATEGORIES.map((c) => (
            <button
              key={c}
              className={`btn-ghost${cat === c ? " active-cat" : ""}`}
              onClick={() => setCat(c)}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid grid-3">
          {filtered.map((tool) => (
            <ToolCard key={tool.id} tool={tool} onDetail={onDetail} />
          ))}
        </div>
        {filtered.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: C.muted,
              padding: 80,
              fontSize: 14,
            }}
          >
            No tools found matching "{search}"
          </div>
        )}
        <div
          className="card"
          style={{
            marginTop: 60,
            textAlign: "center",
            padding: 48,
            borderStyle: "dashed",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 16 }}>🛠️</div>
          <h3
            style={{ fontSize: 22, marginBottom: 10, letterSpacing: "0.03em" }}
          >
            Want to Sell Your Tool Here?
          </h3>
          <p
            style={{
              color: C.muted,
              fontSize: 14,
              marginBottom: 24,
              maxWidth: 400,
              margin: "0 auto 24px",
            }}
          >
            Built something useful? Submit your tool for listing. We handle
            payments, licensing, and delivery.
          </p>
          <a
            href="https://t.me/zyrexdev_official"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none" }}
          >
            <button className="btn-tg">✈ DM @zyrexdev_official</button>
          </a>
        </div>
      </div>
    </div>
  );
}

function ToolDetailPage({ tool, setPage }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const isEMrex = tool.id === 7;
  // eslint-disable-next-line no-unused-vars
  const isDLRex = tool.id === 8;
  const plans = tool.pricing || [
    {
      id: "lifetime",
      label: "Lifetime",
      price: tool.price,
      original: tool.originalPrice,
      badge: "BEST DEAL",
      discount: `${Math.round((1 - tool.price / tool.originalPrice) * 100)}% OFF`,
      perks: tool.licensePerks || [
        "Lifetime access",
        "Free updates",
        "Full documentation",
        "Community support",
      ],
    },
  ];
  return (
    <div style={{ paddingTop: 68 }}>
      {showModal && (
        <PaymentModal
          plan={selectedPlan}
          tool={tool}
          onClose={() => setShowModal(false)}
        />
      )}
      <div className="section">
        <button
          className="btn-ghost"
          style={{ marginBottom: 36 }}
          onClick={() => setPage("tools")}
        >
          ← Back to Tools
        </button>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 400px",
            gap: 56,
            alignItems: "start",
          }}
          className="detail-grid"
        >
          <div className="fade-in">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 56 }}>{tool.icon}</span>
              {tool.isNew && <span className="new-badge">New</span>}
              {tool.isFeatured && <span className="hot-badge">🔥 Hot</span>}
              <span className="tag">{tool.category}</span>
              <span className="tag-green">🔑 Lifetime Available</span>
            </div>
            <h1
              style={{
                fontSize: 48,
                letterSpacing: "0.02em",
                marginBottom: 14,
              }}
            >
              {tool.name}
            </h1>
            <p
              style={{
                color: C.muted,
                fontSize: 16,
                lineHeight: 1.8,
                marginBottom: 40,
                maxWidth: 580,
              }}
            >
              {tool.description}
            </p>
            {isEMrex && <CountdownTimer />}
            {tool.features && (
              <>
                <h2
                  style={{
                    fontSize: 26,
                    marginBottom: 24,
                    letterSpacing: "0.03em",
                  }}
                >
                  Features
                </h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                    marginBottom: 48,
                  }}
                >
                  {tool.features.map((f) => (
                    <div
                      key={f.title}
                      style={{
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        padding: "16px 20px",
                        display: "flex",
                        gap: 14,
                        alignItems: "flex-start",
                      }}
                    >
                      <span style={{ fontSize: 22, flexShrink: 0 }}>
                        {f.icon}
                      </span>
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            marginBottom: 4,
                          }}
                        >
                          {f.title}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>
                          {f.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {tool.testimonials && (
              <>
                <h2
                  style={{
                    fontSize: 26,
                    marginBottom: 24,
                    letterSpacing: "0.03em",
                  }}
                >
                  Reviews
                </h2>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {tool.testimonials.map((t, i) => (
                    <div key={i} className="card">
                      <div
                        style={{
                          color: C.gold,
                          fontSize: 13,
                          marginBottom: 10,
                        }}
                      >
                        {"★".repeat(t.stars)}
                      </div>
                      <p
                        style={{
                          color: C.text,
                          fontSize: 15,
                          lineHeight: 1.7,
                          marginBottom: 16,
                          fontStyle: "italic",
                        }}
                      >
                        "{t.text}"
                      </p>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${C.accent}22, ${C.green}22)`,
                            border: `1px solid ${C.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            fontWeight: 700,
                            fontFamily: "Orbitron",
                          }}
                        >
                          {t.name[0]}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>
                            {t.name}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: C.muted,
                              fontFamily: "JetBrains Mono",
                            }}
                          >
                            {t.role}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── EMREX SCREENSHOTS ── */}
            {isEMrex && (
              <>
                <h2
                  style={{
                    fontSize: 26,
                    marginBottom: 20,
                    letterSpacing: "0.03em",
                  }}
                >
                  Screenshots
                </h2>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    marginBottom: 40,
                  }}
                >
                  {[
                    {
                      src: "/screenshots/capture1.png",
                      caption:
                        "Main Dashboard — Manage all email accounts in one place",
                    },
                    {
                      src: "/screenshots/capture2.png",
                      caption:
                        "Add Account — Connect TikTok, YouTube, Facebook, Instagram",
                    },
                    {
                      src: "/screenshots/capture3.png",
                      caption:
                        "License Activation — Simple key-based licensing system",
                    },
                    {
                      src: "/screenshots/capture4.png",
                      caption:
                        "Right-click Menu — Quick actions for every account",
                    },
                    {
                      src: "/screenshots/capture5.png",
                      caption:
                        "Account List — Full overview with all platform connections",
                    },
                  ].map((s, i) => (
                    <div
                      key={i}
                      style={{
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 12,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          background: C.surface2,
                          padding: "8px 14px",
                          borderBottom: `1px solid ${C.border}`,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {["#ff5f57", "#febc2e", "#28c840"].map((c, j) => (
                          <div
                            key={j}
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: c,
                            }}
                          />
                        ))}
                        <span
                          style={{
                            fontSize: 10,
                            color: C.muted,
                            fontFamily: "JetBrains Mono",
                            marginLeft: 6,
                          }}
                        >
                          Email Account Manager v1.3.6
                        </span>
                      </div>
                      <img
                        src={s.src}
                        alt={s.caption}
                        style={{ width: "100%", display: "block" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <div
                        style={{
                          padding: "8px 14px",
                          fontSize: 12,
                          color: C.muted,
                          fontStyle: "italic",
                        }}
                      >
                        {s.caption}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── DL REX SCREENSHOTS ── */}
            {tool.id === 8 && (
              <>
                <h2
                  style={{
                    fontSize: 26,
                    marginBottom: 20,
                    letterSpacing: "0.03em",
                  }}
                >
                  Screenshots
                </h2>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    marginBottom: 40,
                  }}
                >
                  {[
                    {
                      src: "/screenshots/dlrex1.png",
                      caption:
                        "Main Dashboard — Clean dark UI with live download queue",
                    },
                    {
                      src: "/screenshots/dlrex2.png",
                      caption:
                        "Bulk Download — Paste 100+ URLs and start all at once",
                    },
                    {
                      src: "/screenshots/dlrex3.png",
                      caption:
                        "Watermark Editor — Live 16:9 preview with drag positioning",
                    },
                    {
                      src: "/screenshots/dlrex4.png",
                      caption:
                        "Account Watcher — Monitor accounts and auto-download 24/7",
                    },
                    {
                      src: "/screenshots/dlrex5.png",
                      caption:
                        "Download Queue — Real-time speed, progress and ETA per file",
                    },
                    {
                      src: "/screenshots/dlrex6.png",
                      caption:
                        "Settings — Full control over proxy, quality and download options",
                    },
                  ].map((s, i) => (
                    <div
                      key={i}
                      style={{
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 12,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          background: C.surface2,
                          padding: "8px 14px",
                          borderBottom: `1px solid ${C.border}`,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {["#ff5f57", "#febc2e", "#28c840"].map((c, j) => (
                          <div
                            key={j}
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: c,
                            }}
                          />
                        ))}
                        <span
                          style={{
                            fontSize: 10,
                            color: C.muted,
                            fontFamily: "JetBrains Mono",
                            marginLeft: 6,
                          }}
                        >
                          DL REX v2.0.0
                        </span>
                      </div>
                      <img
                        src={s.src}
                        alt={s.caption}
                        style={{ width: "100%", display: "block" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <div
                        style={{
                          padding: "8px 14px",
                          fontSize: 12,
                          color: C.muted,
                          fontStyle: "italic",
                        }}
                      >
                        {s.caption}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 28 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.accent,
                      letterSpacing: "0.15em",
                      fontFamily: "JetBrains Mono",
                      marginBottom: 10,
                    }}
                  >
                    REQUIREMENTS
                  </div>
                  <div
                    style={{
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: "14px 18px",
                      fontSize: 13,
                      color: C.muted,
                      fontFamily: "JetBrains Mono",
                      lineHeight: 1.8,
                    }}
                  >
                    Python 3.8+ · Windows 10/11 (primary) · macOS · Linux ·
                    ffmpeg required for watermark/edit features
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ position: "sticky", top: 88 }}>
            <div style={{ marginBottom: 16 }}>
              <span className="label">Choose Your Plan</span>
              <h3
                style={{
                  fontSize: 20,
                  letterSpacing: "0.02em",
                  marginBottom: 4,
                }}
              >
                {tool.name}
              </h3>
              <p style={{ fontSize: 13, color: C.muted }}>
                All plans include full access to all features
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 20,
              }}
            >
              {plans.map((plan) => {
                const isBest = plan.id === "lifetime";
                return (
                  <div
                    key={plan.id}
                    className={`price-card${selectedPlan?.id === plan.id ? " selected" : ""}${isBest ? " best" : ""}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    {plan.badge && (
                      <div style={{ position: "absolute", top: -1, right: 16 }}>
                        <span
                          style={{
                            background: isBest ? C.orange : C.accent,
                            color: isBest ? "#fff" : C.bg,
                            fontSize: 9,
                            fontWeight: 800,
                            padding: "3px 8px",
                            borderRadius: "0 0 6px 6px",
                            fontFamily: "JetBrains Mono, monospace",
                            letterSpacing: "0.1em",
                          }}
                        >
                          {plan.badge}
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontFamily: "Orbitron, monospace",
                            fontSize: 13,
                            fontWeight: 700,
                            color: C.text,
                          }}
                        >
                          {plan.label}
                        </div>
                        <div
                          style={{ fontSize: 11, color: C.muted, marginTop: 2 }}
                        >
                          {plan.discount}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            fontFamily: "Orbitron, monospace",
                            fontSize: 26,
                            fontWeight: 900,
                            color: isBest ? C.orange : C.accent,
                          }}
                        >
                          ${plan.price}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.muted,
                            textDecoration: "line-through",
                          }}
                        >
                          ${plan.original}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      {plan.perks.slice(0, 3).map((p, i) => (
                        <div
                          key={i}
                          style={{
                            fontSize: 11,
                            color: C.muted,
                            display: "flex",
                            gap: 6,
                          }}
                        >
                          <span style={{ color: C.green, flexShrink: 0 }}>
                            ✓
                          </span>
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              className="btn-primary"
              style={{
                width: "100%",
                padding: 16,
                fontSize: 14,
                marginBottom: 12,
                background:
                  selectedPlan?.id === "lifetime"
                    ? `linear-gradient(135deg,${C.orange},#ea580c)`
                    : `linear-gradient(135deg,${C.accent},${C.accentB})`,
              }}
              onClick={() => selectedPlan && setPage("ai")}
              disabled={!selectedPlan}
            >
              {selectedPlan
                ? `🤖 Buy via AI Rex — $${selectedPlan.price}`
                : "Select a plan above"}
            </button>
            <a
              href="https://t.me/zyrexdev_official"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <button
                className="btn-tg"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginBottom: 16,
                }}
              >
                ✈ Questions? Ask on Telegram
              </button>
            </a>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {[
                "🔒 Secure Payment",
                "♾ Lifetime Available",
                "⚡ Instant Access",
                "🛡️ 30-Day Refund",
              ].map((b) => (
                <span
                  key={b}
                  style={{
                    fontSize: 11,
                    color: b.includes("Refund") ? C.green : C.muted,
                    background: b.includes("Refund")
                      ? `${C.green}11`
                      : C.surface,
                    border: `1px solid ${b.includes("Refund") ? C.green + "33" : C.border}`,
                    borderRadius: 20,
                    padding: "4px 10px",
                    fontFamily: "JetBrains Mono",
                  }}
                >
                  {b}
                </span>
              ))}

              {/* Sold counter */}
              <div
                style={{
                  width: "100%",
                  marginTop: 8,
                  padding: "10px 14px",
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: C.muted,
                    fontFamily: "JetBrains Mono",
                  }}
                >
                  <span style={{ color: C.green }}>✓</span>{" "}
                  {SOLD_COUNTS[tool.id] || 87} tools sold
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: C.muted,
                    fontFamily: "JetBrains Mono",
                  }}
                >
                  ⭐ 4.9/5 rating
                </div>
              </div>

              {/* Money-back guarantee card */}
              <div
                style={{
                  width: "100%",
                  marginTop: 4,
                  padding: "12px 14px",
                  background: `${C.green}08`,
                  border: `1px solid ${C.green}22`,
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 20 }}>🛡️</span>
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: C.green,
                      marginBottom: 2,
                    }}
                  >
                    30-Day Money-Back Guarantee
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    Not satisfied? Get a full refund — no questions asked.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function AboutPage() {
  return (
    <div style={{ paddingTop: 68 }}>
      <div className="section" style={{ maxWidth: 900 }}>
        <span className="label">About the Creator</span>
        <h1 style={{ fontSize: 52, letterSpacing: "0.02em", marginBottom: 48 }}>
          Meet <span style={{ color: C.accent }}>ZYREX</span>
        </h1>
        <div className="card" style={{ padding: 40, marginBottom: 48 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "200px 1fr",
              gap: 40,
              alignItems: "center",
            }}
            className="about-grid"
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  border: `3px solid ${C.accent}`,
                  overflow: "hidden",
                  margin: "0 auto 16px",
                  animation: "glow 4s ease-in-out infinite",
                }}
              >
                <img
                  src={LOGO_URL}
                  alt="ZYREX"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#0a0f1a;font-size:60px;">👨‍💻</div>`;
                  }}
                />
              </div>
              <div
                style={{
                  fontFamily: "Orbitron",
                  fontSize: 18,
                  fontWeight: 900,
                  color: C.accent,
                }}
              >
                ZYREX
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: C.muted,
                  fontFamily: "JetBrains Mono",
                  marginTop: 4,
                }}
              >
                @zyrexdev_official
              </div>
            </div>
            <div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 20,
                }}
              >
                <span className="tag">🎂 18 years old</span>
                <span className="tag">💻 Developer</span>
                <span className="tag">🧠 AI Builder</span>
                <span className="tag-green">🟢 Available</span>
              </div>
              <p
                style={{
                  color: C.muted,
                  fontSize: 15,
                  lineHeight: 1.9,
                  marginBottom: 20,
                }}
              >
                I'm <strong style={{ color: C.text }}>ZYREX</strong>, an
                18-year-old self-taught developer and AI builder. Everything on
                this store is something I built because I needed it myself
                first.
              </p>
              <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.9 }}>
                I started{" "}
                <strong style={{ color: C.accent }}>TOOLREX.ONLINE</strong> in
                2026 with one mission: make professional-grade developer tools
                accessible and affordable. No bloat, no subscriptions — just
                clean tools with lifetime licenses.
              </p>
            </div>
          </div>
        </div>
        <div
          style={{
            background: `linear-gradient(135deg, ${C.accentDim}, ${C.greenDim})`,
            border: `1px solid ${C.accent}22`,
            borderRadius: 16,
            padding: 40,
          }}
        >
          <span className="label">Mission</span>
          <h2 style={{ fontSize: 28, marginBottom: 16 }}>
            Build tools that save time and increase income.
          </h2>
          <p
            style={{
              color: C.muted,
              fontSize: 15,
              lineHeight: 1.9,
              maxWidth: 620,
            }}
          >
            The best software is built by one person who cares deeply about
            solving one specific problem. Every tool I ship has been
            battle-tested in my own workflows first.
          </p>
        </div>
      </div>
    </div>
  );
}

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  return (
    <div style={{ paddingTop: 68 }}>
      <div className="section" style={{ maxWidth: 900 }}>
        <span className="label">Get in Touch</span>
        <h1 style={{ fontSize: 52, letterSpacing: "0.02em", marginBottom: 16 }}>
          Contact <span style={{ color: C.accent }}>ZYREX</span>
        </h1>
        <p style={{ color: C.muted, fontSize: 16, marginBottom: 56 }}>
          Have a question? Want to buy a tool or sell yours? We are here to
          help.
        </p>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48 }}
          className="about-grid"
        >
          <div>
            {!sent ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <input
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  placeholder="Your email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <textarea
                  placeholder="Your message"
                  rows={5}
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                />
                <button
                  className="btn-primary"
                  onClick={() => {
                    setLoading(true);
                    setTimeout(() => {
                      setLoading(false);
                      setSent(true);
                    }, 1500);
                  }}
                  disabled={loading}
                  style={{ padding: "14px" }}
                >
                  {loading ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          border: `2px solid ${C.bg}`,
                          borderTopColor: "transparent",
                          borderRadius: "50%",
                          animation: "spin 0.7s linear infinite",
                          display: "inline-block",
                        }}
                      />
                      Sending...
                    </span>
                  ) : (
                    "Send Message ✉️"
                  )}
                </button>
              </div>
            ) : (
              <div
                style={{
                  background: `${C.green}0d`,
                  border: `1px solid ${C.green}33`,
                  borderRadius: 14,
                  padding: 48,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 48, marginBottom: 16 }}>✉️</div>
                <h3 style={{ fontSize: 22, color: C.green, marginBottom: 8 }}>
                  Message Sent!
                </h3>
                <p style={{ color: C.muted, fontSize: 14 }}>
                  ZYREX will respond within 24 hours.
                </p>
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <a
              href="https://t.me/zyrexdev_official"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <div
                className="card"
                style={{ borderColor: "#229ED944", cursor: "pointer" }}
              >
                <div style={{ fontSize: 28, marginBottom: 12 }}>✈️</div>
                <h3 style={{ fontSize: 16, marginBottom: 6, color: C.text }}>
                  Telegram — Fastest
                </h3>
                <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
                  DM me directly. I check Telegram constantly.
                </p>
                <button
                  className="btn-tg"
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  ✈ @zyrexdev_official
                </button>
              </div>
            </a>
            <a
              href="mailto:zyrexdev.supporter@gmail.com"
              style={{ textDecoration: "none" }}
            >
              <div
                className="card"
                style={{ borderColor: "#34d39944", cursor: "pointer" }}
              >
                <div style={{ fontSize: 28, marginBottom: 12 }}>📧</div>
                <h3 style={{ fontSize: 16, marginBottom: 6, color: C.text }}>
                  Email — Support
                </h3>
                <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
                  Send your question or payment proof via email. Replied within 24 hours.
                </p>
                <button
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    background: "linear-gradient(135deg,#34d399,#059669)",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 16px",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#05080f",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  📧 zyrexdev.supporter@gmail.com
                </button>
              </div>
            </a>
            <a
              href="https://github.com/zyrex-developer"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <div className="card" style={{ cursor: "pointer" }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>⌥</div>
                <h3 style={{ fontSize: 16, marginBottom: 6, color: C.text }}>
                  GitHub
                </h3>
                <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
                  Check out my open source work and other projects.
                </p>
                <button
                  className="btn-gh"
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  ⌥ github.com/zyrex-developer
                </button>
              </div>
            </a>
            <div className="card">
              <div style={{ fontSize: 28, marginBottom: 12 }}>⏰</div>
              <h3 style={{ fontSize: 16, marginBottom: 6 }}>Response Time</h3>
              <p style={{ color: C.muted, fontSize: 13 }}>
                Telegram: usually within 1 to 4 hours.
                <br />
                Email: within 24 hours on weekdays.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  BUY PAGE — How to Buy + Demo Buy
// ═══════════════════════════════════════════════════════════════════════════════

// eslint-disable-next-line no-unused-vars
const BUY_STEPS_PLACEHOLDER = [
  {
    num: 1, emoji: "🛒", title: "Choose Your Tool",
    desc: "Browse the Tools page and pick the tool that fits your needs.",
    mock: () => (
      <div style={{ background: "#0a0f1a", borderRadius: 8, padding: 10, fontSize: 10 }}>
        {[["📧 EMrex","$1"],["🎬 DL REX","$1"],["✅ TODO REX","$1"]].map(([t,p]) => (
          <div key={t} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
            background:"#0e1525", border:"1px solid #162035", borderRadius:6,
            padding:"7px 10px", marginBottom:5, color:"#e2eaf6", fontWeight:600 }}>
            <span>{t}</span>
            <span style={{ color:"#38bdf8", fontFamily:"Orbitron,monospace", fontWeight:900 }}>{p}</span>
          </div>
        ))}
        <div style={{ background:"linear-gradient(135deg,#38bdf8,#7c3aed)", borderRadius:6,
          padding:"6px 10px", textAlign:"center", color:"#fff", fontWeight:800, marginTop:4 }}>
          {"🛒 BUY in AI Rex →"}
        </div>
      </div>
    ),
  },
  {
    num: 2, emoji: "⏱️", title: "Select Duration",
    desc: "One simple plan — 1 year access for just $1. Includes group support and AI & IT community.",
    mock: () => (
      <div style={{ background: "#0a0f1a", borderRadius: 8, padding: 10, fontSize: 10 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          background:"#6366f111", border:"1px solid #6366f144",
          borderRadius:6, padding:"10px 12px", marginBottom:6 }}>
          <div>
            <div style={{ color:"#f0f4ff", fontWeight:700, marginBottom:3 }}>1 Year License</div>
            <div style={{ color:"#6b7ba8", fontSize:9 }}>1 key · 1 device · Group support</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <span style={{ fontSize:8, background:"#6366f133", color:"#a5b4fc", padding:"2px 6px", borderRadius:4, fontWeight:800, display:"block", marginBottom:3 }}>BEST DEAL</span>
            <span style={{ color:"#a5b4fc", fontFamily:"Orbitron,monospace", fontWeight:900, fontSize:16 }}>$1</span>
          </div>
        </div>
        <div style={{ color:"#6b7ba8", fontSize:8, textAlign:"center" }}>Free AI & IT discussion group included</div>
      </div>
    ),
  },
  {
    num: 3, emoji: "🏦", title: "Pay via ABA PayWay",
    desc: "Tap the ABA PayWay button, scan the QR code and pay the exact amount shown.",
    mock: () => (
      <div style={{ background:"#0a0f1a", borderRadius:8, padding:10, textAlign:"center", fontSize:10 }}>
        <div style={{ background:"rgba(56,189,248,0.06)", border:"1px solid rgba(56,189,248,0.25)",
          borderRadius:8, padding:"14px 10px", marginBottom:8 }}>
          <div style={{ fontSize:8, color:"#6b7f99", marginBottom:4, letterSpacing:"0.1em" }}>EXACT AMOUNT</div>
          <div style={{ fontSize:26, fontWeight:900, color:"#38bdf8", fontFamily:"Orbitron,monospace" }}>$1.00</div>
          <div style={{ fontSize:8, color:"#6b7f99" }}>USD</div>
        </div>
        <div style={{ background:"linear-gradient(135deg,#003087,#009fda)", borderRadius:6,
          padding:"8px", color:"#fff", fontWeight:800 }}>{"🏦 Pay with ABA PayWay"}</div>
      </div>
    ),
  },
  {
    num: 4, emoji: "🧾", title: "Enter Transaction ID",
    desc: "After paying, find your Transaction ID on the receipt and paste it into AI Rex.",
    mock: () => (
      <div style={{ background:"#0a0f1a", borderRadius:8, padding:10, fontSize:10 }}>
        <div style={{ fontSize:9, color:"#6b7f99", marginBottom:4, letterSpacing:"0.08em" }}>TRANSACTION / BILL ID</div>
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid #34d39966",
          borderRadius:6, padding:"10px 12px", color:"#34d399", fontFamily:"JetBrains Mono,monospace",
          fontSize:13, letterSpacing:2, marginBottom:8 }}>177769514234376</div>
        <div style={{ background:"linear-gradient(135deg,#38bdf8,#7c3aed)", borderRadius:6,
          padding:"7px", textAlign:"center", color:"#fff", fontWeight:800 }}>{"🔍 Verify Bill"}</div>
      </div>
    ),
  },
  {
    num: 5, emoji: "📜", title: "Agree to Rules",
    desc: "Read and agree to all 10 TOOLREX rules before receiving your download.",
    mock: () => (
      <div style={{ background:"#0a0f1a", borderRadius:8, padding:10, fontSize:9 }}>
        {["DON'T RESELL YOUR KEY","ONE KEY = ONE DEVICE","KEEP YOUR KEY SAFE"].map((r,i) => (
          <div key={r} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:5,
            background:"rgba(56,189,248,0.04)", border:"1px solid #38bdf822", borderRadius:6, padding:"6px 8px" }}>
            <div style={{ width:16, height:16, borderRadius:"50%", background:"#38bdf8",
              color:"#05080f", display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:8, fontWeight:900, flexShrink:0 }}>{i+1}</div>
            <span style={{ color:"#e2eaf6", fontWeight:600, fontSize:9 }}>{r}</span>
          </div>
        ))}
        <div style={{ background:"linear-gradient(135deg,#34d399,#38bdf8)", borderRadius:6,
          padding:"6px", textAlign:"center", color:"#05080f", fontWeight:800, marginTop:4 }}>
          {"✅ I Agree → Get Download"}
        </div>
      </div>
    ),
  },
  {
    num: 6, emoji: "📦", title: "Download Your Tool",
    desc: "Download the tool ZIP file. Open it and run the exe to see your Device ID.",
    mock: () => (
      <div style={{ background:"#0a0f1a", borderRadius:8, padding:10, fontSize:10 }}>
        <div style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center" }}>
          <span style={{ fontSize:20 }}>📧</span>
          <div>
            <div style={{ color:"#e2eaf6", fontWeight:700 }}>EMrex</div>
            <div style={{ fontSize:9, color:"#6b7f99" }}>Email Account Manager</div>
          </div>
        </div>
        <div style={{ background:"linear-gradient(135deg,#38bdf8,#34d399)", borderRadius:6,
          padding:"8px", textAlign:"center", color:"#05080f", fontWeight:800, marginBottom:6 }}>
          {"⬇ Download EMrex.zip"}
        </div>
        <div style={{ fontSize:9, color:"#6b7f99", textAlign:"center" }}>{"Open tool → find your Device ID"}</div>
      </div>
    ),
  },
  {
    num: 7, emoji: "🖥️", title: "Copy Your Device ID",
    desc: "Open the tool, copy your unique Device ID (format: DEV-XXXX-XXXX-XXXX) and paste it into AI Rex.",
    mock: () => (
      <div style={{ background:"#0a0f1a", borderRadius:8, padding:10, fontSize:10 }}>
        <div style={{ fontSize:9, color:"#6b7f99", marginBottom:4 }}>DEVICE ID</div>
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid #38bdf844",
          borderRadius:6, padding:"10px 12px", color:"#38bdf8", fontFamily:"JetBrains Mono,monospace",
          fontSize:12, letterSpacing:2, marginBottom:8, textAlign:"center" }}>DEV-2701-A19D-26F2</div>
        <div style={{ background:"linear-gradient(135deg,#38bdf8,#7c3aed)", borderRadius:6,
          padding:"7px", textAlign:"center", color:"#fff", fontWeight:800 }}>{"🔑 Generate My Key"}</div>
      </div>
    ),
  },
  {
    num: 8, emoji: "🎉", title: "Get Your License Key",
    desc: "AI Rex generates your unique REX key. Copy it, save it safely, and activate your tool!",
    mock: () => (
      <div style={{ background:"#0a0f1a", borderRadius:8, padding:10, fontSize:10 }}>
        <div style={{ background:"rgba(52,211,153,0.06)", border:"1px solid rgba(52,211,153,0.3)",
          borderRadius:8, padding:"12px", textAlign:"center", marginBottom:8 }}>
          <div style={{ fontSize:8, color:"#6b7f99", marginBottom:6, letterSpacing:"0.1em" }}>YOUR LICENSE KEY</div>
          <div style={{ fontFamily:"JetBrains Mono,monospace", fontSize:11, fontWeight:900,
            color:"#34d399", letterSpacing:2 }}>REX-EM-XXXX-XXXX-XXXX</div>
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <div style={{ flex:1, background:"linear-gradient(135deg,#38bdf8,#34d399)", borderRadius:6,
            padding:"7px", textAlign:"center", color:"#05080f", fontWeight:800, fontSize:9 }}>{"📋 Copy Key"}</div>
          <div style={{ flex:1, background:"rgba(56,189,248,0.08)", border:"1px solid #38bdf844",
            borderRadius:6, padding:"7px", textAlign:"center", color:"#38bdf8", fontWeight:800, fontSize:9 }}>{"💾 toolkey.zip"}</div>
        </div>
      </div>
    ),
  },
];

function BuyPage({ setPage }) {
  const [active, setActive] = useState(0);

  const STEPS = [
    { num:1, icon:"🛒", color:"#6366f1", title:"Pick a Tool",     desc:"Go to Business Hub → AI Tools tab. Browse EMrex, DL REX, YT REX, or TODO REX." },
    { num:2, icon:"🤖", color:"#a855f7", title:"Open AI Rex",     desc:"Click the AI button in the nav. AI Rex is your guide and purchase assistant." },
    { num:3, icon:"💬", color:"#38bdf8", title:"Tell Rex to Buy", desc:'Type "I want to buy EMrex" — Rex will walk you through everything step by step.' },
    { num:4, icon:"🏦", color:"#34d399", title:"Pay via ABA",     desc:"Scan the QR code in the ABA PayWay app. Pay exactly $1 for your chosen tool." },
    { num:5, icon:"🧾", color:"#fbbf24", title:"Send Bill ID",    desc:"After paying, copy your Transaction ID from the ABA receipt and paste it to Rex." },
    { num:6, icon:"📜", color:"#fb923c", title:"Agree to Rules",  desc:"Read and accept the 10 TOOLREX usage rules. 1 key = 1 device, no sharing." },
    { num:7, icon:"📥", color:"#f87171", title:"Download Tool",   desc:"Rex sends you a download link. Extract the ZIP and run the installer." },
    { num:8, icon:"🔑", color:"#4ade80", title:"Get Your Key",    desc:"Enter your Device ID into Rex → receive your unique REX license key instantly." },
  ];

  return (
    <div style={{ paddingTop:68, minHeight:"100vh", background:"#05080f" }}>
      <div style={{ maxWidth:960, margin:"0 auto", padding:"56px 24px" }}>

        {/* Header */}
        <div style={{ textAlign:"center", marginBottom:56 }}>
          <div className="section-label" style={{ color:"#6366f1", marginBottom:12 }}>Purchase Guide</div>
          <h1 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, marginBottom:12,
            background:"linear-gradient(135deg,#6366f1,#38bdf8,#a855f7)",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
            backgroundSize:"200% auto", animation:"gradShift 4s ease infinite" }}>
            How to Buy in 8 Steps
          </h1>
          <p style={{ color:"#8892c0", fontSize:14, marginBottom:28, maxWidth:480, margin:"0 auto 28px" }}>
            Everything goes through AI Rex — your personal guide from payment to key delivery.
          </p>
          <button onClick={() => setPage?.("ai")} style={{
            background:"linear-gradient(135deg,#6366f1,#a855f7)", border:"none",
            borderRadius:12, padding:"13px 32px", color:"#fff", fontSize:14,
            fontWeight:800, cursor:"pointer", boxShadow:"0 0 32px rgba(99,102,241,0.4)",
          }}>🤖 Open AI Rex Now</button>
        </div>

        {/* Animated steps — vertical timeline on mobile, 2-col on desktop */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16, marginBottom:56 }}>
          {STEPS.map((s, i) => (
            <div key={s.num}
              onClick={() => setActive(i)}
              style={{
                background: active===i ? `${s.color}12` : "#0d0f1e",
                border:`2px solid ${active===i ? s.color : "rgba(255,255,255,0.06)"}`,
                borderRadius:16, padding:"20px 22px", cursor:"pointer",
                transition:"all 0.25s cubic-bezier(0.16,1,0.3,1)",
                transform: active===i ? "translateY(-4px)" : "translateY(0)",
                boxShadow: active===i ? `0 16px 48px ${s.color}22` : "none",
                animation:`slideInUp 0.5s ease ${i*0.06}s both`,
              }}>
              <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:10 }}>
                <div style={{
                  width:40, height:40, borderRadius:12, flexShrink:0,
                  background: active===i ? s.color : `${s.color}22`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:20, transition:"all 0.25s",
                  boxShadow: active===i ? `0 0 20px ${s.color}66` : "none",
                }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize:10, fontWeight:800, color:s.color, letterSpacing:"0.1em",
                    fontFamily:"JetBrains Mono,monospace", marginBottom:2 }}>STEP {s.num}</div>
                  <div style={{ fontSize:14, fontWeight:800, color:"#f0f4ff" }}>{s.title}</div>
                </div>
              </div>
              <p style={{ fontSize:13, color: active===i ? "#c4cfe8" : "#6b7ba8", lineHeight:1.7, margin:0 }}>
                {s.desc}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ textAlign:"center", padding:"40px 24px",
          background:"linear-gradient(135deg,rgba(99,102,241,0.08),rgba(168,85,247,0.06))",
          borderRadius:20, border:"1px solid rgba(99,102,241,0.2)" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🤖</div>
          <h2 style={{ fontSize:"clamp(20px,3vw,28px)", fontWeight:900, color:"#f0f4ff", marginBottom:8 }}>Ready? Open AI Rex</h2>
          <p style={{ color:"#8892c0", fontSize:13, marginBottom:24 }}>
            Just say "I want to buy [tool name]" — Rex handles the rest.
          </p>
          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <button onClick={() => setPage?.("ai")} style={{
              background:"linear-gradient(135deg,#6366f1,#a855f7)", border:"none",
              borderRadius:12, padding:"13px 32px", color:"#fff", fontSize:14,
              fontWeight:800, cursor:"pointer",
            }}>🤖 Open AI Rex</button>
            <button onClick={() => setPage?.("business")} style={{
              background:"transparent", border:"1px solid rgba(99,102,241,0.35)",
              borderRadius:12, padding:"13px 28px", color:"#a5b4fc", fontSize:14,
              fontWeight:700, cursor:"pointer",
            }}>Browse Tools</button>
          </div>
          <div style={{ display:"flex", gap:32, justifyContent:"center", flexWrap:"wrap", marginTop:24 }}>
            {[["⚡","Instant key"],["🔑","1 device"],["💵","$1/year"],["📞","Free support"]].map(([ic,lb]) => (
              <div key={lb} style={{ fontSize:12, color:"#6b7ba8", display:"flex", alignItems:"center", gap:6 }}>
                <span>{ic}</span>{lb}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
//  ORDER PAGE — Custom Tool Order
// ═══════════════════════════════════════════════════════════════════════════════
const ORDER_TOOLS = [
  { name: "EMrex",          icon: "📧", prices: { yearly: 1 } },
  { name: "DL REX",         icon: "🎬", prices: { yearly: 1 } },
  { name: "YT REX",         icon: "▶️", prices: { yearly: 1 } },
  { name: "TODO REX",       icon: "✅", prices: { yearly: 1 } },
];

function OrderPage() {
  const [selections, setSelections] = useState({});   // { toolName: duration | null }
  const [contact, setContact]       = useState({ name: "", email: "", telegram: "", note: "" });
  const [submitted, setSubmitted]   = useState(false);
  const [submitErr, setSubmitErr]   = useState("");

  function toggleTool(name) {
    setSelections(prev => {
      const next = { ...prev };
      if (next[name]) { delete next[name]; } else { next[name] = "yearly"; }
      return next;
    });
  }


  const selectedTools = ORDER_TOOLS.filter(t => selections[t.name]);
  const total = selectedTools.reduce((sum, t) => sum + (t.prices[selections[t.name]] || 0), 0);

  async function handleSubmit() {
    if (!selectedTools.length) { setSubmitErr("Please select at least one tool."); return; }
    if (!contact.name.trim())  { setSubmitErr("Please enter your name.");          return; }
    if (!contact.email.trim()) { setSubmitErr("Please enter your email.");          return; }
    setSubmitErr("");
    // Build order summary text
    // Try to send via contact API, fall through if not available
    try {
      await fetch(`${API}/contact/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...contact, order: selectedTools.map(t => ({ tool: t.name, duration: selections[t.name], price: t.prices[selections[t.name]] })), total }),
      });
    } catch (_) {}
    setSubmitted(true);
    window.open("https://t.me/zyrexdev_official", "_blank");
  }

  if (submitted) {
    return (
      <div style={{ paddingTop: 68 }}>
        <div className="section" style={{ maxWidth: 600, textAlign: "center" }}>
          <div style={{ fontSize: 60, marginBottom: 20 }}>✅</div>
          <h2 style={{ fontSize: 28, fontWeight: 900, color: C.green, fontFamily: "Orbitron,monospace", marginBottom: 12 }}>
            Order Received!
          </h2>
          <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.8, marginBottom: 24 }}>
            Thank you <strong style={{ color: C.text }}>{contact.name}</strong>!<br/>
            We received your order for <strong style={{ color: C.accent }}>{selectedTools.length} tool{selectedTools.length > 1 ? "s" : ""}</strong>.
            <br/><br/>
            A Telegram window just opened — please send us a quick message so we can confirm your order and send payment details.
          </p>
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: "20px 24px", marginBottom: 24, textAlign: "left",
          }}>
            <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.1em", marginBottom: 12 }}>ORDER SUMMARY</div>
            {selectedTools.map(t => (
              <div key={t.name} style={{ display:"flex", justifyContent:"space-between", marginBottom:8, fontSize:13 }}>
                <span style={{ color: C.text }}>{t.icon} {t.name} <span style={{ color: C.muted, fontSize:11 }}>({selections[t.name]})</span></span>
                <span style={{ color: C.accent, fontWeight:700, fontFamily:"Orbitron,monospace" }}>${t.prices[selections[t.name]].toFixed(2)}</span>
              </div>
            ))}
            <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12, paddingTop: 12,
              display:"flex", justifyContent:"space-between", fontWeight:900, fontSize:15 }}>
              <span style={{ color: C.text }}>Total</span>
              <span style={{ color: C.accent, fontFamily:"Orbitron,monospace" }}>${total.toFixed(2)}</span>
            </div>
          </div>
          <a href="https://t.me/zyrexdev_official" target="_blank" rel="noreferrer">
            <button style={{
              background: "#229ED9", border:"none", borderRadius:10,
              padding:"13px 32px", color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer",
            }}>✈ Open Telegram to Confirm</button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 68 }}>
      <div className="section" style={{ maxWidth: 1100 }}>

        {/* Hero */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span className="label">Custom Order</span>
          <h1 style={{ fontSize: 40, fontWeight: 900, fontFamily: "Orbitron,monospace",
            background: "linear-gradient(135deg,#fb923c,#f43f5e)", WebkitBackgroundClip:"text",
            WebkitTextFillColor:"transparent", marginBottom: 12 }}>
            Build Your Order
          </h1>
          <p style={{ color: C.muted, fontSize: 14, maxWidth: 520, margin: "0 auto" }}>
            Pick the tools you want, choose your plan, and we'll confirm everything on Telegram.
          </p>
        </div>

        <div className="order-grid" style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 28, alignItems: "start" }}>

          {/* LEFT — Tool selector */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, letterSpacing: "0.1em",
              marginBottom: 16, fontFamily: "JetBrains Mono" }}>STEP 1 — SELECT TOOLS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ORDER_TOOLS.map(tool => {
                const selected = !!selections[tool.name];
                const dur = selections[tool.name] || "yearly";
                return (
                  <div key={tool.name} style={{
                    background: selected ? "rgba(56,189,248,0.06)" : C.surface,
                    border: `1px solid ${selected ? C.accent + "55" : C.border}`,
                    borderRadius: 12, padding: "14px 16px",
                    transition: "all 0.2s", cursor: "pointer",
                  }}
                    onClick={() => toggleTool(tool.name)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      {/* Checkbox */}
                      <div style={{
                        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                        border: `2px solid ${selected ? C.accent : C.border}`,
                        background: selected ? C.accent : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s",
                      }}>
                        {selected && <span style={{ color: "#05080f", fontSize: 12, fontWeight: 900 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 20 }}>{tool.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{tool.name}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>
                          1 Year License · $1 · Free group support
                        </div>
                      </div>
                      {selected && (
                        <div style={{ fontSize: 16, fontWeight: 900, color: C.accent, fontFamily: "Orbitron,monospace" }}>
                          ${tool.prices[dur].toFixed(2)}
                        </div>
                      )}
                    </div>

                    {/* Plan info — shown when selected */}
                    {selected && (
                      <div style={{ marginTop: 8, paddingLeft: 44 }}>
                        <span style={{ fontSize: 10, color: C.accent, fontWeight: 700 }}>
                          1 Year · $1 · 1 key · 1 device · Free group support · Free AI & IT community
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — Summary + Contact */}
          <div style={{ position: "sticky", top: 88 }}>

            {/* Order summary */}
            <div style={{
              background: C.surface2, border: `1px solid ${C.border}`,
              borderRadius: 14, padding: "20px", marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: "0.1em",
                marginBottom: 14, fontFamily: "JetBrains Mono" }}>YOUR ORDER</div>
              {selectedTools.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: "16px 0" }}>
                  No tools selected yet
                </div>
              ) : (
                <>
                  {selectedTools.map(t => (
                    <div key={t.name} style={{ display:"flex", justifyContent:"space-between",
                      marginBottom:10, fontSize:12 }}>
                      <span style={{ color: C.text }}>{t.icon} {t.name}
                        <span style={{ color: C.muted, fontSize:10 }}> ({selections[t.name]})</span>
                      </span>
                      <span style={{ color: C.accent, fontWeight:700 }}>${t.prices[selections[t.name]].toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, marginTop:4,
                    display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontSize:13, color:C.muted }}>Total</span>
                    <span style={{ fontSize:20, fontWeight:900, color:C.accent,
                      fontFamily:"Orbitron,monospace" }}>${total.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Contact form */}
            <div style={{
              background: C.surface2, border: `1px solid ${C.border}`,
              borderRadius: 14, padding: "20px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: "0.1em",
                marginBottom: 14, fontFamily: "JetBrains Mono" }}>STEP 2 — YOUR INFO</div>
              {[
                ["name",     "Your Name *",     "text",  "John Doe"],
                ["email",    "Email Address *",  "email", "you@email.com"],
                ["telegram", "Telegram (optional)","text","@username"],
              ].map(([field, label, type, ph]) => (
                <div key={field} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{label}</div>
                  <input
                    type={type}
                    value={contact[field]}
                    onChange={e => setContact(p => ({ ...p, [field]: e.target.value }))}
                    placeholder={ph}
                    style={{
                      width: "100%", background: C.bg, border: `1px solid ${C.border}`,
                      borderRadius: 7, padding: "9px 12px", color: C.text,
                      fontFamily: "Exo 2,sans-serif", fontSize: 12, outline: "none",
                      boxSizing: "border-box", transition: "border-color 0.2s",
                    }}
                    onFocus={e => e.target.style.borderColor = C.accent}
                    onBlur={e => e.target.style.borderColor = C.border}
                  />
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Note / Special Request</div>
                <textarea
                  value={contact.note}
                  onChange={e => setContact(p => ({ ...p, note: e.target.value }))}
                  placeholder="Anything we should know about your order..."
                  rows={3}
                  style={{
                    width: "100%", background: C.bg, border: `1px solid ${C.border}`,
                    borderRadius: 7, padding: "9px 12px", color: C.text,
                    fontFamily: "Exo 2,sans-serif", fontSize: 12, resize: "vertical",
                    outline: "none", boxSizing: "border-box",
                  }}
                  onFocus={e => e.target.style.borderColor = C.accent}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
              </div>
              {submitErr && (
                <div style={{ color: "#f87171", fontSize: 11, marginBottom: 10 }}>⚠ {submitErr}</div>
              )}
              <button onClick={handleSubmit} style={{
                width: "100%", padding: "13px",
                background: selectedTools.length ? "linear-gradient(135deg,#fb923c,#f43f5e)" : "rgba(255,255,255,0.04)",
                border: "none", borderRadius: 10,
                color: selectedTools.length ? "#fff" : C.muted,
                fontSize: 14, fontWeight: 800, cursor: selectedTools.length ? "pointer" : "not-allowed",
                transition: "all 0.2s",
              }}>
                🚀 Place Order — ${total.toFixed(2)}
              </button>
              <div style={{ fontSize: 10, color: C.muted, textAlign: "center", marginTop: 8 }}>
                We'll confirm via Telegram or Email within 1 hour
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  BUSINESS PAGE
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
//  AI PAGE — Full-page AI Rex chat
// ═══════════════════════════════════════════════════════════════════════════════
const AI_QUICK = [
  { label:"🛠️ Show all tools",    text:"show all tools" },
  { label:"💰 Compare prices",    text:"compare all tools" },
  { label:"📧 Tell me about EMrex", text:"Tell me about EMrex" },
  { label:"🎬 Tell me about DL REX", text:"Tell me about DL REX" },
  { label:"🛒 How to buy",        text:"how to buy" },
  { label:"🤔 Recommend a tool",  text:"recommend a tool" },
  { label:"📞 Contact ZYREX",     text:"contact" },
];

function aiReply(msg) {
  const m = msg.toLowerCase().trim();
  const toolMatch = ALL_TOOLS.find(t =>
    t.name.toLowerCase().includes(m) ||
    m.includes(t.name.toLowerCase())
  );
  if (toolMatch) return {
    text: `**${toolMatch.icon} ${toolMatch.name}** — ${toolMatch.tagline}\n\n${toolMatch.description || ""}\n\n💰 From **$${toolMatch.pricing?.[0]?.price ?? "?"}** · ${toolMatch.license || "Lifetime available"}`,
  };
  if (m.match(/show all|all tool|list|catalog/)) return {
    text: `Here are all **${ALL_TOOLS.length} tools** on TOOLREX:\n\n` +
      ALL_TOOLS.map(t => `${t.icon} **${t.name}** — $${t.pricing?.[0]?.price ?? "?"} — ${t.tagline}`).join("\n"),
  };
  if (m.includes("compare") || m.includes("price") || m.includes("how much")) return {
    text: ALL_TOOLS.map(t =>
      `${t.icon} **${t.name}** — $${t.pricing?.[0]?.price ?? "?"} lifetime — ${t.category}`
    ).join("\n"),
  };
  if (m.includes("buy") || m.includes("purchase") || m.includes("order")) return {
    text: `🛒 **How to buy on TOOLREX.ONLINE:**\n\n1. Go to **Business** page\n2. Pick your tool\n3. Click **How to Buy** tab\n4. Pay via **ABA PayWay**\n5. Enter your Transaction ID\n6. Agree to rules → download → enter Device ID → get your key!\n\n💬 Questions? Telegram: **@zyrexdev_official**`,
  };
  if (m.includes("contact") || m.includes("support") || m.includes("telegram")) return {
    text: `📞 **Contact ZYREX:**\n\n✈️ Telegram — @zyrexdev_official ⚡ fastest\n📧 Email — zyrexdev.supporter@gmail.com\n⌥ GitHub — github.com/zyrex-developer`,
  };
  if (m.includes("recommend") || m.includes("best") || m.includes("popular")) return {
    text: `🏆 **Top picks:**\n\n📧 **EMrex** — Email manager, 80% OFF → $10\n🎬 **DL REX** — Video downloader, 1000+ sites\n⚡ **AutoFlow AI** — No-code automation\n\nAll have **lifetime licenses** — pay once, use forever!`,
  };
  if (m.match(/^(hi|hello|hey|sup|yo)/)) return {
    text: `👋 Hey! I'm **AI Rex** — your TOOLREX assistant.\n\nI can help you:\n🔍 Find the right tool\n💰 Compare prices\n🛒 Guide you through buying\n📞 Connect you with ZYREX\n\nWhat would you like to know?`,
  };
  if (m.includes("about") || m.includes("zyrex") || m.includes("toolrex") || m.includes("who")) return {
    text: `🏪 **TOOLREX.ONLINE** — built by **ZYREX**, an 18-year-old indie developer.\n\n🛠️ ${ALL_TOOLS.length} developer tools available\n🔑 All have Lifetime licenses\n📅 Founded 2026\n💼 Custom dev services available\n\nWant to sell your own tool? Keep **80%** of every sale!`,
  };
  const found = ALL_TOOLS.filter(t =>
    t.name.toLowerCase().includes(m) || t.category.toLowerCase().includes(m) ||
    (t.tagline || "").toLowerCase().includes(m)
  );
  if (found.length) return {
    text: `🔍 Found **${found.length}** tool${found.length>1?"s":""} matching "${msg}":\n\n` +
      found.map(t => `${t.icon} **${t.name}** — ${t.tagline}`).join("\n"),
  };
  return {
    text: `Thanks for your message! 😊\n\nTry asking:\n🔍 **"show all tools"** — browse everything\n💰 **"compare prices"** — see all prices\n🛒 **"how to buy"** — purchase guide\n🤔 **"recommend a tool"** — get a pick\n📞 **"contact"** — reach ZYREX`,
  };
}

function AIPage({ setPage }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [busy, setBusy]         = useState(false);
  const chatRef  = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (chatRef.current)
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  function send(txt) {
    const msg = (txt || input).trim();
    if (!msg || busy) return;
    setInput("");
    const userMsg = { role:"user", text:msg, ts:new Date() };
    setMessages(prev => [...prev, userMsg]);
    setBusy(true);
    setTimeout(() => {
      const { text } = aiReply(msg);
      setMessages(prev => [...prev, { role:"ai", text, ts:new Date() }]);
      setBusy(false);
    }, 350 + Math.random() * 250);
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  }

  function fmt(text) {
    return text.split("\n").map((line, i) => {
      const parts = line.split(/(\*\*.*?\*\*)/g).map((p, j) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={j} style={{ color:"#a5b4fc" }}>{p.slice(2,-2)}</strong>
          : p
      );
      return <div key={i} style={{ marginBottom: line === "" ? 6 : 2 }}>{parts}</div>;
    });
  }

  return (
    <div style={{ paddingTop:68, height:"100vh", display:"flex", flexDirection:"column",
      background:"linear-gradient(160deg,#050508,#07091a)", overflow:"hidden" }}>

      <div style={{ flex:1, display:"flex", overflow:"hidden", maxWidth:1200, width:"100%", margin:"0 auto", padding:"0 0" }}>

        {/* ── SIDEBAR ── */}
        <div style={{ width:280, flexShrink:0, display:"flex", flexDirection:"column",
          borderRight:"1px solid rgba(99,102,241,0.15)",
          background:"rgba(99,102,241,0.03)", padding:"28px 20px", overflowY:"auto" }}>

          {/* Avatar */}
          <div style={{ textAlign:"center", marginBottom:24 }}>
            <div style={{
              width:72, height:72, borderRadius:"50%", margin:"0 auto 14px",
              background:"linear-gradient(135deg,#6366f1,#a855f7)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:32, boxShadow:"0 0 32px rgba(99,102,241,0.5)",
              border:"2px solid rgba(165,180,252,0.3)",
            }}>🤖</div>
            <div style={{ fontFamily:"Orbitron,monospace", fontSize:16, fontWeight:900,
              background:"linear-gradient(135deg,#a5b4fc,#e0e7ff)",
              WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>AI REX</div>
            <div style={{ fontSize:11, color:"rgba(165,180,252,0.6)", marginTop:4,
              fontFamily:"JetBrains Mono,monospace", letterSpacing:"0.1em" }}>
              TOOLREX ASSISTANT
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
              gap:6, marginTop:8 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"#34d399",
                boxShadow:"0 0 8px #34d399" }}/>
              <span style={{ fontSize:10, color:"#34d399", fontFamily:"JetBrains Mono,monospace" }}>ONLINE</span>
            </div>
          </div>

          <div style={{ fontSize:12, color:"#8892c0", lineHeight:1.8, marginBottom:24, textAlign:"center" }}>
            Ask me anything about TOOLREX — tools, prices, how to buy, or just chat!
          </div>

          <div style={{ height:1, background:"rgba(99,102,241,0.15)", marginBottom:20 }}/>

          {/* Quick questions */}
          <div style={{ fontSize:10, color:"rgba(165,180,252,0.5)", letterSpacing:"0.2em",
            fontFamily:"JetBrains Mono,monospace", marginBottom:12 }}>QUICK QUESTIONS</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:24 }}>
            {AI_QUICK.map(q => (
              <button key={q.label} onClick={() => send(q.text)} style={{
                textAlign:"left", padding:"9px 12px", borderRadius:8,
                background:"rgba(255,255,255,0.03)", border:"1px solid rgba(99,102,241,0.12)",
                color:"#c4caeb", fontSize:11, cursor:"pointer", fontFamily:"Exo 2,sans-serif",
                transition:"all 0.2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background="rgba(99,102,241,0.1)"; e.currentTarget.style.borderColor="rgba(99,102,241,0.4)"; e.currentTarget.style.color="#a5b4fc"; }}
                onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor="rgba(99,102,241,0.12)"; e.currentTarget.style.color="#c4caeb"; }}
              >{q.label}</button>
            ))}
          </div>

          <div style={{ height:1, background:"rgba(99,102,241,0.15)", marginBottom:20 }}/>

          {/* Action buttons */}
          <button onClick={() => setPage("business")} style={{
            width:"100%", padding:"11px", marginBottom:8, borderRadius:8,
            background:"linear-gradient(135deg,#6366f1,#a855f7)",
            border:"none", color:"#fff", fontSize:12, fontWeight:700,
            cursor:"pointer", fontFamily:"Exo 2,sans-serif",
            boxShadow:"0 0 16px rgba(99,102,241,0.3)",
          }}>🛒 Buy a Tool</button>
          <button onClick={() => setPage("contact")} style={{
            width:"100%", padding:"11px", borderRadius:8,
            background:"transparent", border:"1px solid rgba(99,102,241,0.3)",
            color:"#a5b4fc", fontSize:12, fontWeight:700,
            cursor:"pointer", fontFamily:"Exo 2,sans-serif",
          }}>📩 Contact ZYREX</button>
        </div>

        {/* ── CHAT AREA ── */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

          {/* Messages */}
          <div ref={chatRef} style={{ flex:1, overflowY:"auto", padding:"24px 28px",
            display:"flex", flexDirection:"column", gap:16,
            scrollbarWidth:"thin", scrollbarColor:"rgba(99,102,241,0.3) transparent" }}>

            {messages.length === 0 && (
              <div style={{ margin:"auto", textAlign:"center", maxWidth:480 }}>
                <div style={{ fontSize:56, marginBottom:16 }}>🤖</div>
                <h2 style={{ fontSize:28, fontWeight:900, marginBottom:12,
                  background:"linear-gradient(135deg,#a5b4fc,#e0e7ff)",
                  WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
                  fontFamily:"Orbitron,monospace" }}>Hey! I'm AI Rex</h2>
                <p style={{ color:"#8892c0", fontSize:14, lineHeight:1.8, marginBottom:28 }}>
                  I know everything about <strong style={{color:"#a5b4fc"}}>TOOLREX.ONLINE</strong>.<br/>
                  Ask me to find tools, compare prices, or guide you through buying.
                </p>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center" }}>
                  {AI_QUICK.slice(0,4).map(q => (
                    <button key={q.label} onClick={() => send(q.text)} style={{
                      padding:"8px 14px", borderRadius:20,
                      background:"rgba(99,102,241,0.1)", border:"1px solid rgba(99,102,241,0.25)",
                      color:"#a5b4fc", fontSize:11, cursor:"pointer",
                      fontFamily:"Exo 2,sans-serif", transition:"all 0.2s",
                    }}
                      onMouseEnter={e => e.currentTarget.style.background="rgba(99,102,241,0.2)"}
                      onMouseLeave={e => e.currentTarget.style.background="rgba(99,102,241,0.1)"}
                    >{q.label}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} style={{
                display:"flex", gap:12,
                flexDirection: msg.role==="user" ? "row-reverse" : "row",
                alignItems:"flex-end",
              }}>
                {/* Avatar */}
                <div style={{
                  width:34, height:34, borderRadius:10, flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, fontWeight:900, fontFamily:"Orbitron,monospace",
                  background: msg.role==="user"
                    ? "linear-gradient(135deg,rgba(251,146,60,0.2),rgba(251,146,60,0.1))"
                    : "linear-gradient(135deg,#6366f1,#a855f7)",
                  border: msg.role==="user" ? "1px solid rgba(251,146,60,0.3)" : "none",
                  color: msg.role==="user" ? "#fb923c" : "#fff",
                  boxShadow: msg.role==="ai" ? "0 0 12px rgba(99,102,241,0.4)" : "none",
                }}>
                  {msg.role==="user" ? "U" : "🤖"}
                </div>
                {/* Bubble */}
                <div style={{
                  maxWidth:"72%",
                  background: msg.role==="user"
                    ? "rgba(251,146,60,0.06)"
                    : "rgba(99,102,241,0.08)",
                  border: msg.role==="user"
                    ? "1px solid rgba(251,146,60,0.15)"
                    : "1px solid rgba(99,102,241,0.2)",
                  borderRadius: msg.role==="user"
                    ? "14px 4px 14px 14px"
                    : "4px 14px 14px 14px",
                  padding:"12px 16px",
                  fontSize:13, lineHeight:1.7,
                  color:"#e8eaff",
                  backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)",
                }}>
                  {fmt(msg.text)}
                  <div style={{ fontSize:9, color:"rgba(165,180,252,0.35)",
                    marginTop:6, textAlign: msg.role==="user" ? "right" : "left",
                    fontFamily:"JetBrains Mono,monospace" }}>
                    {msg.ts?.toLocaleTimeString("en-US",{hour12:false,hour:"2-digit",minute:"2-digit"})}
                  </div>
                </div>
              </div>
            ))}

            {busy && (
              <div style={{ display:"flex", gap:12, alignItems:"flex-end" }}>
                <div style={{ width:34, height:34, borderRadius:10, flexShrink:0,
                  background:"linear-gradient(135deg,#6366f1,#a855f7)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, boxShadow:"0 0 12px rgba(99,102,241,0.4)" }}>🤖</div>
                <div style={{ background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.2)",
                  borderRadius:"4px 14px 14px 14px", padding:"14px 18px",
                  display:"flex", gap:5, alignItems:"center" }}>
                  {[0,0.2,0.4].map((d,i) => (
                    <div key={i} style={{ width:6, height:6, borderRadius:"50%",
                      background:"#a5b4fc", opacity:0.5,
                      animation:`rexDot 1.2s ease-in-out ${d}s infinite` }}/>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{ padding:"16px 28px",
            borderTop:"1px solid rgba(99,102,241,0.12)",
            background:"rgba(99,102,241,0.03)" }}>
            <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                onInput={e => { e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"; }}
                placeholder="Ask anything about TOOLREX, tools, prices…"
                rows={1}
                style={{
                  flex:1, background:"rgba(255,255,255,0.04)",
                  border:"1px solid rgba(99,102,241,0.2)", borderRadius:12,
                  padding:"12px 16px", color:"#e8eaff",
                  fontFamily:"Exo 2,sans-serif", fontSize:13,
                  resize:"none", minHeight:44, maxHeight:120, lineHeight:1.5,
                  outline:"none", transition:"border 0.2s",
                }}
                onFocus={e => e.target.style.borderColor="rgba(99,102,241,0.5)"}
                onBlur={e => e.target.style.borderColor="rgba(99,102,241,0.2)"}
              />
              <button onClick={() => send()} disabled={busy || !input.trim()} style={{
                width:44, height:44, borderRadius:12, flexShrink:0,
                background: input.trim() ? "linear-gradient(135deg,#6366f1,#a855f7)" : "rgba(99,102,241,0.08)",
                border:"none", color:"#fff", cursor: input.trim() ? "pointer" : "not-allowed",
                fontSize:16, display:"flex", alignItems:"center", justifyContent:"center",
                boxShadow: input.trim() ? "0 0 16px rgba(99,102,241,0.4)" : "none",
                transition:"all 0.2s", opacity: busy ? 0.5 : 1,
              }}>▶</button>
            </div>
            <div style={{ fontSize:9, color:"rgba(165,180,252,0.3)", marginTop:8,
              fontFamily:"JetBrains Mono,monospace", display:"flex", justifyContent:"space-between" }}>
              <span>Enter to send · Shift+Enter new line</span>
              <span>AI REX · TOOLREX.ONLINE</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes rexDot{0%,80%,100%{transform:scale(0.7);opacity:0.3}40%{transform:scale(1.2);opacity:1}}`}</style>
    </div>
  );
}


function BusinessPage({ onDetail, T }) {
  const [tab, setTab] = useState("tools");
  const [search, setSearch] = useState("");
  const TABS = [
    { id:"tools",    label:"🛠️ AI Tools"    },
    { id:"services", label:"💼 Services"     },
    { id:"buy",      label:"🛒 How to Buy"   },
    { id:"order",    label:"📋 Custom Order" },
  ];
  return (
    <div style={{ paddingTop:68, minHeight:"100vh", background:"#050508" }}>
      <div style={{
        background:"linear-gradient(180deg,#07091a 0%,#050508 100%)",
        borderBottom:"1px solid rgba(99,102,241,0.15)", padding:"56px 24px 0",
      }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="section-label" style={{ color:"#6366f1", marginBottom:12 }}>Platform</div>
          <h1 style={{ fontSize:"clamp(28px,4vw,52px)", fontWeight:900, marginBottom:12,
            background:"linear-gradient(135deg,#f0f4ff,#a5b4fc)",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
            Business Hub
          </h1>
          <p style={{ color:"#8892c0", fontSize:14, marginBottom:32, maxWidth:520 }}>
            AI tools, automation services, custom software development, and more — all from one platform.
          </p>
          <div className="biz-tabs" style={{ display:"flex", gap:8 }}>
            {TABS.map(t=>(
              <button key={t.id} className={`biz-tab${tab===t.id?" active":""}`} onClick={()=>setTab(t.id)}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"48px 24px" }}>
        {tab === "tools" && (
          <div>
            {/* Header + Search */}
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:16, marginBottom:28 }}>
              <div>
                <h2 style={{ fontSize:28, fontWeight:900, color:"#f0f4ff", marginBottom:6 }}>Developer Tools</h2>
                <p style={{ color:"#8892c0", fontSize:14 }}>Production-ready · 1 year · $1 each · Built by ZYREX</p>
              </div>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={T?.searchPlaceholder || "Search tools..."}
                style={{ padding:"10px 16px", fontSize:14, background:"#0d0f1e",
                  border:"1px solid rgba(99,102,241,0.25)", borderRadius:10, color:"#f0f4ff",
                  outline:"none", width:220, fontFamily:"JetBrains Mono,monospace" }}
              />
            </div>

            {/* Bundle Deal Banner */}
            <div style={{ marginBottom:28, padding:"18px 24px", borderRadius:14,
              background:"linear-gradient(135deg,rgba(99,102,241,0.12),rgba(168,85,247,0.08))",
              border:"1px solid rgba(99,102,241,0.25)", display:"flex", alignItems:"center",
              justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#a5b4fc", marginBottom:4 }}>
                  🎁 Bundle Deal — Get All 4 Tools
                </div>
                <div style={{ fontSize:12, color:"#6b7ba8" }}>
                  EMrex + DL REX + YT REX + TODO REX ·{" "}
                  <span style={{ color:"#f87171", textDecoration:"line-through" }}>$4</span>
                  {" "}→{" "}
                  <strong style={{ color:"#4ade80" }}>$3 only</strong>
                </div>
              </div>
              <button onClick={() => window.__openRexChat?.()} style={{
                padding:"9px 20px", fontSize:12, fontWeight:800,
                background:"linear-gradient(135deg,#6366f1,#a855f7)",
                border:"none", borderRadius:10, color:"#fff", cursor:"pointer", whiteSpace:"nowrap",
              }}>🤖 Get Bundle via AI Rex</button>
            </div>

            {/* Tool grid — filtered */}
            {(() => {
              const filtered = ALL_TOOLS.filter(t =>
                t.name.toLowerCase().includes(search.toLowerCase()) ||
                t.tagline.toLowerCase().includes(search.toLowerCase()) ||
                t.category.toLowerCase().includes(search.toLowerCase())
              );
              return filtered.length ? (
                <div className="grid grid-3">
                  {filtered.map(tool => <ToolCard key={tool.id} tool={tool} onDetail={onDetail} />)}
                </div>
              ) : (
                <div style={{ textAlign:"center", padding:"48px 24px", color:"#6b7ba8" }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
                  <p style={{ fontSize:14 }}>No tools found for "<strong style={{color:"#a5b4fc"}}>{search}</strong>"</p>
                </div>
              );
            })()}
          </div>
        )}

        {tab === "services" && (
          <div>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <h2 style={{ fontSize:36, fontWeight:900, marginBottom:12,
                background:"linear-gradient(135deg,#6366f1,#38bdf8)",
                WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                Service Packages
              </h2>
              <p style={{ color:"#8892c0", fontSize:14, maxWidth:500, margin:"0 auto" }}>
                Custom development services — from small apps to large-scale AI platforms.
              </p>
            </div>
            <div className="pkg-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20, marginBottom:56 }}>
              {SERVICE_PACKAGES.map((pkg,i)=>(
                <div key={pkg.name} className="pkg-card glass-card" style={{
                  borderColor:`${pkg.color}22`,
                  boxShadow: i===1 ? `0 0 40px ${pkg.color}22` : "none",
                  transform: i===1 ? "scale(1.03)" : "none",
                }}>
                  {pkg.badge && (
                    <div style={{ position:"absolute", top:-12, left:"50%", transform:"translateX(-50%)",
                      background:`linear-gradient(135deg,${pkg.color},#6366f1)`,
                      color:"#fff", fontSize:9, fontWeight:800, padding:"4px 14px",
                      borderRadius:100, letterSpacing:"0.12em", fontFamily:"JetBrains Mono,monospace", whiteSpace:"nowrap" }}>
                      {pkg.badge}
                    </div>
                  )}
                  <div style={{ fontSize:32, marginBottom:12 }}>{pkg.icon}</div>
                  <div style={{ fontSize:18, fontWeight:800, color:"#f0f4ff", marginBottom:6 }}>{pkg.name}</div>
                  <div style={{ fontSize:12, color:"#8892c0", marginBottom:20, lineHeight:1.6 }}>{pkg.desc}</div>
                  <div style={{ fontSize:40, fontWeight:900, fontFamily:"Orbitron,monospace", color:pkg.color, marginBottom:4 }}>${pkg.price}</div>
                  <div style={{ fontSize:11, color:"#6b7b99", marginBottom:24 }}>per project</div>
                  <div style={{ marginBottom:24 }}>
                    {pkg.features.map(f=>(
                      <div key={f} style={{ display:"flex", gap:10, alignItems:"center", marginBottom:10 }}>
                        <span style={{ color:pkg.color, fontSize:12, flexShrink:0 }}>✓</span>
                        <span style={{ color:"#c4c9e0", fontSize:13 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <a href="https://t.me/zyrexdev_official" target="_blank" rel="noreferrer" style={{textDecoration:"none",display:"block"}}>
                    <button className="neon-btn" style={{ width:"100%", padding:"12px", fontSize:13, borderRadius:10 }}>
                      <span>✈ Get Started</span>
                    </button>
                  </a>
                </div>
              ))}
            </div>
            <h3 style={{ fontSize:22, fontWeight:800, color:"#f0f4ff", marginBottom:24, textAlign:"center" }}>What We Build</h3>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12 }}>
              {[
                { icon:"🤖", title:"AI Agents & Bots",   desc:"Custom AI chatbots, Telegram bots, automation agents" },
                { icon:"🌐", title:"Web Applications",   desc:"Full-stack React + FastAPI/Node platforms" },
                { icon:"⚡", title:"SaaS Platforms",     desc:"Scalable subscription products with auth & billing" },
                { icon:"🔌", title:"API Services",       desc:"REST APIs, GraphQL, webhook integrations" },
                { icon:"📊", title:"Dashboards",         desc:"Admin panels, analytics dashboards, real-time data" },
                { icon:"🛡️", title:"Auth & Security",   desc:"JWT auth, 2FA, role management, secure systems" },
                { icon:"🚀", title:"Deployment",         desc:"Docker, cloud deploy, CI/CD pipelines" },
                { icon:"📱", title:"Mobile-First UI",    desc:"Responsive design, PWAs, mobile-optimised apps" },
                { icon:"🧠", title:"AI Integration",     desc:"OpenAI, Claude, LangChain, custom ML pipelines" },
              ].map(s=>(
                <div key={s.title} className="glass-card" style={{ padding:"16px 18px" }}>
                  <div style={{ fontSize:22, marginBottom:8 }}>{s.icon}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:"#d0d8f0", marginBottom:4 }}>{s.title}</div>
                  <div style={{ fontSize:11, color:"#6b7b99", lineHeight:1.6 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "buy"   && <BuyPage />}
        {tab === "order" && <OrderPage />}
      </div>
    </div>
  );
}

// ─── Account Page ─────────────────────────────────────────────────────────────
function AccountPage({ authUser, onLogout, setPage, onNameChange }) {
  const [tab, setTab]         = useState("profile");
  const [name, setName]       = useState(authUser?.name || "");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg] = useState("");

  const [pwStep, setPwStep]   = useState("idle"); // idle | sent | done
  const [pwCode, setPwCode]   = useState("");
  const [newPw, setNewPw]     = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg]     = useState({ type:"", text:"" });

  const token = localStorage.getItem("toolrex_token");

  async function handleSaveName() {
    if (!name.trim() || name.trim() === authUser?.name) return;
    setNameLoading(true); setNameMsg("");
    try {
      const d = await apiPatch("/auth/update-name", { name }, token);
      setNameMsg("✓ Name updated!");
      onNameChange && onNameChange(d.name);
    } catch (e) { setNameMsg("✗ " + e.message); }
    setNameLoading(false);
  }

  async function handleSendCode() {
    setPwLoading(true); setPwMsg({ type:"", text:"" });
    try {
      const d = await apiPost("/auth/change-password-request", {}, token);
      setPwStep("sent");
      if (d.fallback_code) {
        setPwMsg({ type:"warn", text:`Email failed. Your code: ${d.fallback_code}` });
      } else {
        setPwMsg({ type:"ok", text:`6-digit code sent to ${authUser?.email}` });
      }
    } catch (e) { setPwMsg({ type:"err", text: e.message }); }
    setPwLoading(false);
  }

  async function handleChangePw() {
    if (newPw.length < 8) { setPwMsg({ type:"err", text:"Min 8 characters, 1 uppercase" }); return; }
    setPwLoading(true); setPwMsg({ type:"", text:"" });
    try {
      await apiPost("/auth/change-password-confirm", { email: authUser?.email, code: pwCode, new_password: newPw }, token);
      setPwMsg({ type:"ok", text:"Password changed! Please log in again." });
      setPwStep("done");
      setTimeout(() => { onLogout(); setPage("home"); }, 2000);
    } catch (e) { setPwMsg({ type:"err", text: e.message }); }
    setPwLoading(false);
  }

  const card = { background:"#0d0f1e", border:"1px solid rgba(99,102,241,0.18)", borderRadius:16, padding:28, marginBottom:20 };
  const inp  = { width:"100%", padding:"12px 14px", fontSize:16, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(99,102,241,0.2)", borderRadius:10, color:"#e8eaff", outline:"none", boxSizing:"border-box", fontFamily:"JetBrains Mono,monospace", WebkitAppearance:"none", appearance:"none" };
  const lbl  = { fontSize:11, color:"#6b7ba8", fontWeight:700, letterSpacing:"0.08em", marginBottom:6, display:"block" };
  const btn  = (g) => ({ padding:"11px 22px", fontSize:13, fontWeight:800, background:g, border:"none", borderRadius:10, color:"#fff", cursor:"pointer" });

  return (
    <div style={{ paddingTop:80, minHeight:"100vh", background:"#05080f" }}>
      <div style={{ maxWidth:640, margin:"0 auto", padding:"40px 24px" }}>
        <div className="section-label" style={{ color:"#6366f1", marginBottom:10 }}>My Account</div>
        <h1 style={{ fontSize:32, fontWeight:900, color:"#f0f4ff", marginBottom:6 }}>Account Settings</h1>
        <p style={{ color:"#6b7ba8", fontSize:13, marginBottom:32 }}>{authUser?.email}</p>

        {/* Tabs */}
        <div style={{ display:"flex", gap:8, marginBottom:28, borderBottom:"1px solid rgba(255,255,255,0.07)", paddingBottom:0 }}>
          {[["profile","👤 Profile"],["security","🔒 Security"]].map(([k,l]) => (
            <button key={k} onClick={()=>setTab(k)} style={{
              padding:"10px 18px", fontSize:13, fontWeight:700, background:"transparent",
              border:"none", cursor:"pointer", fontFamily:"Exo 2,sans-serif",
              color: tab===k ? "#a5b4fc" : "#4a5568",
              borderBottom: tab===k ? "2px solid #6366f1" : "2px solid transparent",
              transition:"all 0.2s",
            }}>{l}</button>
          ))}
        </div>

        {/* Profile Tab */}
        {tab === "profile" && (
          <div style={card}>
            <h3 style={{ fontSize:16, fontWeight:800, color:"#f0f4ff", marginBottom:20 }}>Display Name</h3>
            <label style={lbl}>Name</label>
            <input style={{ ...inp, marginBottom:14 }} value={name} onChange={e=>setName(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleSaveName()} />
            <label style={lbl}>Email <span style={{ color:"#334155" }}>(cannot change)</span></label>
            <input style={{ ...inp, marginBottom:20, color:"#4a5568", cursor:"not-allowed" }} value={authUser?.email} readOnly />
            <div style={{ display:"flex", alignItems:"center", gap:14 }}>
              <button onClick={handleSaveName} disabled={nameLoading} style={btn("linear-gradient(135deg,#6366f1,#a855f7)")}>
                {nameLoading ? "Saving…" : "Save Name"}
              </button>
              {nameMsg && <span style={{ fontSize:12, color: nameMsg.startsWith("✓") ? "#4ade80" : "#f87171" }}>{nameMsg}</span>}
            </div>
          </div>
        )}

        {/* Security Tab */}
        {tab === "security" && (
          <div style={card}>
            <h3 style={{ fontSize:16, fontWeight:800, color:"#f0f4ff", marginBottom:6 }}>Change Password</h3>
            <p style={{ color:"#6b7ba8", fontSize:13, marginBottom:20 }}>
              We'll send a 6-digit code to <strong style={{ color:"#f0f4ff" }}>{authUser?.email}</strong> to confirm.
            </p>

            {pwMsg.text && (
              <div style={{ background: pwMsg.type==="ok"?"rgba(34,197,94,0.1)":pwMsg.type==="warn"?"rgba(251,146,60,0.1)":"rgba(239,68,68,0.1)",
                border:`1px solid ${pwMsg.type==="ok"?"rgba(34,197,94,0.3)":pwMsg.type==="warn"?"rgba(251,146,60,0.3)":"rgba(239,68,68,0.3)"}`,
                borderRadius:10, padding:"10px 14px", marginBottom:16,
                color: pwMsg.type==="ok"?"#4ade80":pwMsg.type==="warn"?"#fb923c":"#f87171", fontSize:12 }}>
                {pwMsg.text}
              </div>
            )}

            {pwStep === "idle" && (
              <button onClick={handleSendCode} disabled={pwLoading} style={btn("linear-gradient(135deg,#38bdf8,#6366f1)")}>
                {pwLoading ? "Sending…" : "📧 Send Verification Code"}
              </button>
            )}

            {pwStep === "sent" && (
              <>
                <label style={lbl}>6-Digit Code</label>
                <input style={{ ...inp, marginBottom:14 }} placeholder="Enter code from email" value={pwCode} onChange={e=>setPwCode(e.target.value)} />
                <label style={lbl}>New Password</label>
                <div style={{ position:"relative", marginBottom:20 }}>
                  <input style={inp} placeholder="Min 8 chars, 1 uppercase" type={showPw?"text":"password"} value={newPw} onChange={e=>setNewPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleChangePw()} />
                  <button onClick={()=>setShowPw(v=>!v)} style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#6b7ba8",cursor:"pointer" }}>{showPw?"🙈":"👁"}</button>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={handleChangePw} disabled={pwLoading} style={btn("linear-gradient(135deg,#6366f1,#a855f7)")}>
                    {pwLoading ? "Changing…" : "Change Password →"}
                  </button>
                  <button onClick={handleSendCode} disabled={pwLoading} style={{ ...btn("transparent"), border:"1px solid rgba(99,102,241,0.3)", color:"#a5b4fc" }}>
                    Send Again
                  </button>
                </div>
              </>
            )}

            {pwStep === "done" && (
              <div style={{ color:"#4ade80", fontSize:14, fontWeight:700 }}>✓ Password changed! Logging out…</div>
            )}
          </div>
        )}

        <button onClick={()=>{ onLogout(); setPage("home"); }} style={{ ...btn("rgba(239,68,68,0.12)"), border:"1px solid rgba(239,68,68,0.3)", color:"#f87171", marginTop:8 }}>
          🚪 Sign Out
        </button>
      </div>
    </div>
  );
}

// ─── FAQ Page ─────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  { q:"Is the license key permanent?", a:"The license is valid for 1 year from purchase date. You will receive a new key when you renew." },
  { q:"Can I use the key on multiple devices?", a:"Each key is locked to 1 device only. If you need multiple devices, purchase additional licenses." },
  { q:"How do I get my license key after payment?", a:"Open AI Rex on the website, complete the purchase flow, then enter your Device ID to receive your REX key instantly." },
  { q:"What payment methods are accepted?", a:"We accept ABA PayWay (QR code payment). More methods coming soon." },
  { q:"How do I contact support?", a:"Join our free Telegram group or message @zyrexdev_official directly on Telegram." },
  { q:"Can I get a refund?", a:"Yes, within 30 days of purchase if the tool does not work on your device. Contact us on Telegram." },
  { q:"What is included with the license?", a:"1 license key, 1 device activation, 1 year access, free group support, and access to the AI & IT discussion community." },
  { q:"Do tools work on Mac/Linux?", a:"Currently all tools are built for Windows. Mac/Linux versions are planned for future releases." },
];

function FAQPage({ T }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ paddingTop:80, minHeight:"100vh", background:"#05080f" }}>
      <div style={{ maxWidth:780, margin:"0 auto", padding:"56px 24px" }}>
        <div className="section-label" style={{ color:"#6366f1", marginBottom:12 }}>Support</div>
        <h1 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, marginBottom:12,
          background:"linear-gradient(135deg,#f0f4ff,#a5b4fc)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
          {T?.faqTitle || "FAQ"}
        </h1>
        <p style={{ color:"#8892c0", fontSize:14, marginBottom:48 }}>
          {T?.faqDesc || "Common questions answered."}
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} style={{
              background:"#0d0f1e", border:`1px solid ${open===i?"rgba(99,102,241,0.4)":"rgba(99,102,241,0.12)"}`,
              borderRadius:14, overflow:"hidden", transition:"border-color 0.2s",
            }}>
              <button onClick={() => setOpen(open===i ? null : i)} style={{
                width:"100%", textAlign:"left", padding:"18px 22px",
                background:"transparent", border:"none", cursor:"pointer",
                display:"flex", justifyContent:"space-between", alignItems:"center", gap:16,
              }}>
                <span style={{ fontSize:15, fontWeight:700, color:"#f0f4ff", lineHeight:1.4 }}>{item.q}</span>
                <span style={{ fontSize:18, color:"#6366f1", flexShrink:0, transition:"transform 0.2s",
                  transform: open===i ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
              </button>
              {open === i && (
                <div style={{ padding:"0 22px 18px", color:"#8892c0", fontSize:14, lineHeight:1.8 }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ marginTop:48, textAlign:"center", padding:"32px 24px",
          background:"rgba(99,102,241,0.06)", borderRadius:16, border:"1px solid rgba(99,102,241,0.15)" }}>
          <div style={{ fontSize:24, marginBottom:12 }}>💬</div>
          <p style={{ color:"#8892c0", fontSize:14, marginBottom:16 }}>Still have questions? Reach us on Telegram.</p>
          <a href="https://t.me/zyrexdev_official" target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
            <button style={{ background:"linear-gradient(135deg,#229ED9,#1a7db5)", border:"none", borderRadius:10,
              padding:"11px 28px", color:"#fff", fontWeight:800, fontSize:14, cursor:"pointer" }}>
              ✈ Open Telegram
            </button>
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── License Checker Page ──────────────────────────────────────────────────────
function LicenseCheckerPage({ T }) {
  const [key, setKey]       = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  async function checkLicense() {
    if (!key.trim()) { setError("Please enter a license key"); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const d = await apiGet(`/purchase/toolkey-info/${encodeURIComponent(key.trim())}`);
      setResult(d);
    } catch (e) {
      setError(e.message || "Key not found");
    }
    setLoading(false);
  }

  const statusColor = result?.status === "active" ? "#4ade80" : result?.status === "expired" ? "#f87171" : "#fbbf24";

  return (
    <div style={{ paddingTop:80, minHeight:"100vh", background:"#05080f" }}>
      <div style={{ maxWidth:600, margin:"0 auto", padding:"56px 24px" }}>
        <div className="section-label" style={{ color:"#6366f1", marginBottom:12 }}>License</div>
        <h1 style={{ fontSize:"clamp(26px,4vw,44px)", fontWeight:900, marginBottom:12,
          background:"linear-gradient(135deg,#f0f4ff,#a5b4fc)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
          {T?.licenseCheck || "Check License"}
        </h1>
        <p style={{ color:"#8892c0", fontSize:14, marginBottom:40 }}>
          Enter your REX key to view status, device, and expiry date.
        </p>
        <div style={{ display:"flex", gap:10, marginBottom:20 }}>
          <input
            value={key} onChange={e => setKey(e.target.value)}
            onKeyDown={e => e.key === "Enter" && checkLicense()}
            placeholder="REX-XX-XXXX-XXXX-XXXX"
            style={{ flex:1, padding:"13px 16px", fontSize:15, background:"#0d0f1e",
              border:"1px solid rgba(99,102,241,0.25)", borderRadius:12, color:"#f0f4ff",
              outline:"none", fontFamily:"JetBrains Mono,monospace" }}
          />
          <button onClick={checkLicense} disabled={loading} style={{
            padding:"13px 24px", fontWeight:800, fontSize:14,
            background:"linear-gradient(135deg,#6366f1,#a855f7)",
            border:"none", borderRadius:12, color:"#fff", cursor:"pointer", whiteSpace:"nowrap",
          }}>
            {loading ? "..." : "🔍 Check"}
          </button>
        </div>
        {error && <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)",
          borderRadius:10, padding:"12px 16px", color:"#f87171", fontSize:13, marginBottom:16 }}>{error}</div>}
        {result && (
          <div style={{ background:"#0d0f1e", border:"1px solid rgba(99,102,241,0.2)", borderRadius:16, padding:24 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:statusColor, boxShadow:`0 0 10px ${statusColor}` }}/>
              <span style={{ fontWeight:800, color:statusColor, fontSize:15, textTransform:"uppercase", letterSpacing:"0.1em" }}>
                {result.status}
              </span>
            </div>
            {[
              ["🔑 Key",      result.key],
              ["🛠️ Tool",     result.tool],
              ["📅 Created",  result.created],
              ["⏳ Expires",  result.expires || "—"],
              ["💻 Devices",  result.devs?.length ? result.devs.join(", ") : "None registered"],
              ["👤 Customer", result.customer],
            ].map(([label, val]) => val && (
              <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0",
                borderBottom:"1px solid rgba(255,255,255,0.05)", fontSize:13 }}>
                <span style={{ color:"#6b7ba8" }}>{label}</span>
                <span style={{ color:"#f0f4ff", fontFamily:"JetBrains Mono,monospace", fontSize:12, textAlign:"right", maxWidth:"60%" }}>{val}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Footer({ setPage, T }) {
  return (
    <footer>
      <div
        style={{ maxWidth: 1200, margin: "0 auto", padding: "56px 24px 32px" }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr",
            gap: 40,
            marginBottom: 48,
          }}
        >
          <div>
            <div
              className="logo-wrap"
              style={{ marginBottom: 16 }}
              onClick={() => setPage("home")}
            >
              <img
                src={LOGO_URL}
                alt="TOOLREX"
                className="logo-img"
                style={{ width: 36, height: 36 }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <div>
                <span className="logo-text" style={{ fontSize: 15 }}>
                  TOOL<span>REX</span>.ONLINE
                </span>
                <span className="logo-sub">by ZYREX</span>
              </div>
            </div>
            <p
              style={{
                color: C.muted,
                fontSize: 13,
                lineHeight: 1.8,
                maxWidth: 240,
                marginBottom: 20,
              }}
            >
              Buy developer tools with lifetime licenses. Built by ZYREX, for
              developers who move fast.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a
                href="https://t.me/zyrexdev_official"
                target="_blank"
                rel="noreferrer"
              >
                <button
                  className="btn-tg"
                  style={{ padding: "8px 14px", fontSize: 11 }}
                >
                  ✈ Telegram
                </button>
              </a>
              <a
                href="mailto:zyrexdev.supporter@gmail.com"
              >
                <button
                  style={{
                    padding: "8px 14px",
                    fontSize: 11,
                    background: "linear-gradient(135deg,#34d399,#059669)",
                    border: "none",
                    borderRadius: 6,
                    fontWeight: 700,
                    color: "#05080f",
                    cursor: "pointer",
                  }}
                >
                  📧 Email
                </button>
              </a>
              <a
                href="https://github.com/zyrex-developer"
                target="_blank"
                rel="noreferrer"
              >
                <button
                  className="btn-gh"
                  style={{ padding: "8px 14px", fontSize: 11 }}
                >
                  ⌥ GitHub
                </button>
              </a>
            </div>
          </div>
          {[
            {
              title: "Platform",
              links: [
                ["Home", "home"],
                ["Business Hub", "business"],
                ["AI Rex", "ai"],
              ],
            },
            {
              title: "Support",
              links: [
                ["FAQ", "faq"],
                ["Check License", "license"],
                ["Contact", "contact"],
              ],
            },
            {
              title: "Legal",
              links: [
                ["Privacy Policy", "home"],
                ["License Terms", "faq"],
                ["Refund Policy", "faq"],
              ],
            },
          ].map(({ title, links }) => (
            <div key={title}>
              <div
                style={{
                  fontFamily: "Orbitron",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.08em",
                  marginBottom: 16,
                  color: C.text,
                }}
              >
                {title}
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {links.map(([label, pg]) => (
                  <span
                    key={label}
                    style={{
                      color: C.muted,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "color 0.2s",
                    }}
                    onClick={() => setPage(pg)}
                    onMouseEnter={(e) => (e.target.style.color = C.accent)}
                    onMouseLeave={(e) => (e.target.style.color = C.muted)}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            borderTop: `1px solid ${C.border}`,
            paddingTop: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span
            style={{
              color: C.muted,
              fontSize: 12,
              fontFamily: "JetBrains Mono",
            }}
          >
            © 2026 TOOLREX.ONLINE — Created by ZYREX
          </span>
          <span
            style={{
              color: C.muted,
              fontSize: 12,
              fontFamily: "JetBrains Mono",
            }}
          >
            <span style={{ color: C.accent }}>⚡</span> Built with precision ·
            All licenses lifetime
          </span>
        </div>
      </div>
    </footer>
  );
}

// ─── Business Auth Gate — Clean login like Claude ────────────────────────────
function BusinessAuthGate({ onLogin }) {
  const [view, setView]       = useState("main"); // main | email | code
  const [email, setEmail]     = useState("");
  const [code, setCode]       = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const inp = { width:"100%", padding:"13px 16px", fontSize:16, background:"rgba(255,255,255,0.05)",
    border:"1px solid rgba(99,102,241,0.22)", borderRadius:12, color:"#f0f4ff", outline:"none",
    boxSizing:"border-box", fontFamily:"JetBrains Mono,monospace", WebkitAppearance:"none" };

  async function sendCode() {
    if (!email.trim()) { setError("Enter your email"); return; }
    setError(""); setLoading(true);
    try {
      const d = await apiPost("/auth/magic", { email });
      setView("code");
      setSuccess(d.fallback_code ? `Email failed. Code: ${d.fallback_code}` : `6-digit code sent to ${email}`);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function verifyCode() {
    if (!code.trim()) { setError("Enter the code"); return; }
    setError(""); setLoading(true);
    try {
      const d = await apiPost("/auth/magic-verify", { email, code });
      localStorage.setItem("toolrex_token", d.token);
      onLogin(d.user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function googleLogin(credential) {
    setError(""); setLoading(true);
    try {
      const d = await apiPost("/auth/google", { credential });
      localStorage.setItem("toolrex_token", d.token);
      onLogin(d.user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  return (
    <div style={{ minHeight:"100vh", paddingTop:68, display:"flex", alignItems:"center",
      justifyContent:"center", flexDirection:"column", padding:"68px 20px 60px",
      background:"radial-gradient(ellipse 80% 60% at 50% 30%,rgba(99,102,241,0.08) 0%,#05080f 70%)" }}>

      {/* Logo + title */}
      <div style={{ textAlign:"center", marginBottom:32 }}>
        <div style={{ width:56, height:56, borderRadius:14, margin:"0 auto 16px",
          background:"linear-gradient(135deg,#6366f1,#a855f7)",
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>⚡</div>
        <h2 style={{ fontSize:"clamp(20px,3vw,28px)", fontWeight:900, color:"#f0f4ff", marginBottom:6 }}>
          Access Business Hub
        </h2>
        <p style={{ color:"#6b7ba8", fontSize:13 }}>
          Sign in to browse and purchase tools
        </p>
      </div>

      {/* Card */}
      <div style={{ width:"100%", maxWidth:400 }}>
        {error && <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)",
          borderRadius:10, padding:"10px 14px", marginBottom:14, color:"#f87171", fontSize:13 }}>{error}</div>}
        {success && <div style={{ background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)",
          borderRadius:10, padding:"10px 14px", marginBottom:14, color:"#4ade80", fontSize:13 }}>{success}</div>}

        {view === "main" && (<>
          {/* Google button */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}>
            <GoogleLogin
              onSuccess={r => googleLogin(r.credential)}
              onError={() => setError("Google sign-in cancelled.")}
              theme="filled_black" shape="rectangular" size="large"
              text="continue_with" width="400"
            />
          </div>

          {/* OR divider */}
          <div style={{ display:"flex", alignItems:"center", gap:12, margin:"16px 0" }}>
            <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.08)" }}/>
            <span style={{ fontSize:12, color:"#334155", fontWeight:600 }}>OR</span>
            <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.08)" }}/>
          </div>

          {/* Continue with Email button */}
          <button onClick={() => setView("email")} style={{ width:"100%", padding:"13px",
            fontSize:14, fontWeight:700, background:"transparent",
            border:"1px solid rgba(99,102,241,0.35)", borderRadius:12, color:"#a5b4fc",
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10,
            transition:"all 0.2s", fontFamily:"Exo 2,sans-serif" }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(99,102,241,0.08)"}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}>
            📧 Continue with Email
          </button>

          <p style={{ textAlign:"center", color:"#2d3748", fontSize:11, marginTop:14, lineHeight:1.6 }}>
            By continuing you agree to our{" "}
            <span style={{ color:"#6366f1", cursor:"pointer" }}>Terms of Service</span>
          </p>
        </>)}

        {view === "email" && (<>
          <div style={{ marginBottom:12 }}>
            <input value={email} onChange={e => setEmail(e.target.value)} style={inp}
              placeholder="your@email.com" type="email"
              onKeyDown={e => e.key === "Enter" && sendCode()} autoFocus />
          </div>
          <button onClick={sendCode} disabled={loading} style={{ width:"100%", padding:"13px",
            fontSize:14, fontWeight:800, background:"linear-gradient(135deg,#6366f1,#a855f7)",
            border:"none", borderRadius:12, color:"#fff", cursor:"pointer", marginBottom:12 }}>
            {loading ? "Sending…" : "Continue →"}
          </button>
          <button onClick={() => { setView("main"); setError(""); setEmail(""); }}
            style={{ width:"100%", padding:"10px", fontSize:13, fontWeight:600, background:"transparent",
              border:"none", color:"#6b7ba8", cursor:"pointer" }}>← Back</button>
        </>)}

        {view === "code" && (<>
          <p style={{ color:"#8892c0", fontSize:13, marginBottom:14 }}>
            Enter the 6-digit code sent to <strong style={{color:"#f0f4ff"}}>{email}</strong>
          </p>
          <div style={{ marginBottom:12 }}>
            <input value={code} onChange={e => setCode(e.target.value)} style={inp}
              placeholder="1 2 3 4 5 6" onKeyDown={e => e.key === "Enter" && verifyCode()} autoFocus />
          </div>
          <button onClick={verifyCode} disabled={loading} style={{ width:"100%", padding:"13px",
            fontSize:14, fontWeight:800, background:"linear-gradient(135deg,#6366f1,#a855f7)",
            border:"none", borderRadius:12, color:"#fff", cursor:"pointer", marginBottom:10 }}>
            {loading ? "Verifying…" : "Verify & Enter →"}
          </button>
          <div style={{ display:"flex", justifyContent:"center", gap:20, fontSize:12 }}>
            <span style={{ color:"#6366f1", cursor:"pointer" }} onClick={sendCode}>Resend code</span>
            <span style={{ color:"#6b7ba8", cursor:"pointer" }} onClick={() => { setView("email"); setCode(""); setSuccess(""); }}>← Change email</span>
          </div>
        </>)}
      </div>
    </div>
  );
}

function App() {
  const [page, setPage] = useState("home");
  const [selectedTool, setSelectedTool] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [authModal, setAuthModal] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("toolrex_lang") || "en");
  const topRef = useRef(null);
  const T = LANG[lang];

  function toggleLang() {
    const next = lang === "en" ? "kh" : "en";
    setLang(next);
    localStorage.setItem("toolrex_lang", next);
  }

  // Silently restore session on mount — no forced splash
  useEffect(() => {
    const token = localStorage.getItem("toolrex_token");
    if (!token) return;
    apiGet("/auth/me", token)
      .then(user => setAuthUser(user))
      .catch(() => localStorage.removeItem("toolrex_token"));
  }, []);

  function navigate(p) {
    setPage(p);
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth" }), 10);
  }
  function onDetail(tool) { setSelectedTool(tool); navigate("detail"); }
  function handleLogin(user) { setAuthUser(user); setAuthModal(null); }
  function handleLogout() { localStorage.removeItem("toolrex_token"); setAuthUser(null); }

  return (
    <>
      <style>{CSS}</style>
      <Cursor />
      <div ref={topRef} />
      <Nav
        page={page} setPage={navigate}
        authUser={authUser} onLogout={handleLogout}
        onOpenAuth={(v) => setAuthModal(v)}
        lang={lang} toggleLang={toggleLang} T={T}
      />
      {authModal && (
        <AuthModal view={authModal} onClose={() => setAuthModal(null)} onLogin={handleLogin} />
      )}
      <div className="fade-in" key={page}>
        {page === "home"     && <HomePage setPage={navigate} T={T} />}
        {page === "business" && (authUser
          ? <BusinessPage onDetail={onDetail} T={T} />
          : <BusinessAuthGate onLogin={handleLogin} />
        )}
        {page === "detail"   && selectedTool && (
          <ToolDetailPage tool={selectedTool} setPage={navigate} />
        )}
        {page === "contact"  && <ContactPage T={T} />}
        {page === "tools"    && (authUser
          ? <BusinessPage onDetail={onDetail} T={T} />
          : <BusinessAuthGate onLogin={handleLogin} />
        )}
        {page === "buy"      && <BuyPage setPage={navigate} />}
        {page === "order"    && <OrderPage />}
        {page === "about"    && <HomePage setPage={navigate} T={T} />}
        {page === "ai"       && <AIPage setPage={navigate} />}
        {page === "account"  && authUser && (
          <AccountPage authUser={authUser} onLogout={handleLogout} setPage={navigate}
            onNameChange={(n) => setAuthUser(u => ({ ...u, name: n }))} />
        )}
        {page === "faq"      && <FAQPage T={T} />}
        {page === "license"  && <LicenseCheckerPage T={T} />}
      </div>
      <Footer setPage={navigate} T={T} />
    </>
  );
}

// Wrap with Google OAuth provider
const AppWithGoogle = () => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <App />
  </GoogleOAuthProvider>
);

export { AppWithGoogle as default };
